import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { AlertModal, ConfirmModal } from './AlertModal';
import { useAuth } from '../context/AuthContext';
import MatchTimerPill from './MatchTimerPill';
import { useTranslation } from 'react-i18next';
import { VersionContext } from './VersionManager';
import PublicHeader from './PublicHeader';
import Footer from './Footer';
import MatchAccessPanel from './match/MatchAccessPanel';
import McIcon from './match/mcIcons';
import ReplayMapModal from './match/ReplayMapModal';
import ActionModal from './match/ActionModal';
import TeamChatPanel from './match/TeamChatPanel';
import MatchDetailSkeleton from './match/MatchDetailSkeleton';
import MapDuration, { useMapTimes } from './match/MapDuration';
import './match/matchControls.css';
import { formatLocalTime, formatShortDateTime, formatLocalDateTime, parseServerDate } from '../utils/timeUtils';
import usePageTitle from '../utils/usePageTitle';
import { matchTabTitle } from '../utils/matchTitle';
import './PublicMatches.css';
import MatchFeed from './feed/MatchFeed';

// Connection dot pulse animation (injected once into document head)
const dotStyleId = 'match-detail-dot-style';
if (!document.getElementById(dotStyleId)) {
  const style = document.createElement('style');
  style.id = dotStyleId;
  style.textContent = `
    @keyframes dotPulse {
      0%, 100% { opacity: 0.85; transform: scale(1); }
      50% { opacity: 1; transform: scale(1.15); }
    }
    .conn-dot {
      animation: dotPulse 2s ease-in-out infinite;
    }
    .conn-dot.disconnected {
      animation: none;
      opacity: 0.5 !important;
    }
  `;
  document.head.appendChild(style);
}

/* ── Connected Players: HLTV-style live HP bar ──
   v = { hp, armor, helmet, alive } from player-status `vitals` (built from log lines
   server-side: `attacked … (health) (armor)`, `left buyzone with [kevlar(N) helmet]`,
   purchases, kills; back to 100 HP at each respawn = "Starting Freeze period"). Only HP is shown for now —
   armor/helmet are still tracked server-side if an armor icon is wanted later. */
function VitalsBadge({ v }) {
  const { t } = useTranslation();
  if (!v) return null;
  if (!v.alive || v.hp <= 0) {
    return (
      <span style={{ width: 64, textAlign: 'center', fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.08em', color: 'var(--text-muted, #4a4a5a)' }}>
        {t('matchDetail.vitalsDead', 'DEAD')}
      </span>
    );
  }
  const hp = Math.max(0, Math.min(100, v.hp));
  // 70–100 green · 40–69 blue · 20–39 amber · below 20 red
  const fill = hp >= 70 ? '#16a34a' : hp >= 40 ? '#2563eb' : hp >= 20 ? '#d97706' : '#dc2626';
  return (
    // HP bar: number inside, fill width = HP%
    <span
      title={t('matchDetail.vitalsHp', '{{hp}} HP', { hp })}
      style={{ position: 'relative', width: 64, height: 14, borderRadius: 3, background: 'rgba(255,255,255,0.08)', overflow: 'hidden', flexShrink: 0 }}
    >
      <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${hp}%`, background: fill, transition: 'width 0.25s ease, background 0.25s ease' }} />
      <span style={{ position: 'relative', paddingLeft: 4, fontSize: '0.62rem', fontWeight: 700, lineHeight: '14px', color: '#fff', fontFamily: 'var(--font-mono)', textShadow: '0 1px 1px rgba(0,0,0,0.6)' }}>{hp}</span>
    </span>
  );
}

/* ── Restore Checker: collapsible round row ── */
function RestoreRoundRow({ roundNum, hasDamage, sortedDamage, hasFlags, safeToRestore, statusColor, flags, requestAt, damageBeforeRequest, isLive }) {
  const { t } = useTranslation();
  // The in-progress round opens by itself and closes by itself when it ends (the
  // same row stays mounted — key is map_round — and flips is_live → false). Manual
  // toggling in between is respected; only the live/ended transition overrides it.
  const [open, setOpen] = React.useState(!!isLive);
  React.useEffect(() => { setOpen(!!isLive); }, [isLive]);
  const detailCount = sortedDamage.length + flags.length;
  const canToggle = detailCount > 0 || isLive;

  // One time-ordered list of requests + hits so "before or after the damage" is
  // visible at a glance. Timestamps are whole seconds: on a tie the hit is listed
  // first, matching the server verdict (same-second counts as damage-before).
  const timeline = [
    ...sortedDamage.map((ev, i) => ({ kind: 'hit', ts: ev.created_at || '', i, ev })),
    ...flags.map((flag, i) => ({ kind: 'req', ts: flag.ts || '', i, flag })),
  ].sort((a, b) => (a.ts < b.ts ? -1 : a.ts > b.ts ? 1 : (a.kind === b.kind ? a.i - b.i : (a.kind === 'hit' ? -1 : 1))));
  // Show the real wall-clock time the damage was recorded (easier to cross-reference
  // than in-game round seconds). created_at is a SQLite UTC datetime without a 'Z',
  // so append it before parsing so the browser converts to local time correctly.
  const fmtClock = (createdAt) => formatLocalTime(createdAt);

  return (
    <div style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
      {/* Summary row - always visible */}
      <div
        onClick={() => canToggle && setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.6rem',
          padding: '0.5rem 1rem',
          cursor: canToggle ? 'pointer' : 'default',
          transition: 'background 0.12s',
          background: open ? 'rgba(255,255,255,0.02)' : 'transparent',
        }}
        onMouseEnter={e => { if (canToggle) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = open ? 'rgba(255,255,255,0.02)' : 'transparent'; }}
      >
        {/* Status dot */}
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor, flexShrink: 0 }} />

        {/* Round number */}
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-primary, #e8e8ed)', minWidth: 52 }}>R{roundNum}</span>

        {/* Live badge for the in-progress round */}
        {isLive && (
          <span style={{ fontSize: '0.55rem', fontWeight: 700, padding: '1px 6px', borderRadius: 3, background: 'rgba(34,211,238,0.12)', color: '#22d3ee', border: '1px solid rgba(34,211,238,0.25)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            LIVE
          </span>
        )}

        {/* Verdict */}
        {hasFlags && (
          <span style={{
            fontSize: '0.68rem', fontWeight: 600, color: statusColor,
          }}>
            {safeToRestore
              ? t('restoreChecker.safeVerdict', 'Requested BEFORE any damage ({{time}}) - safe to restore', { time: fmtClock(requestAt) })
              : t('restoreChecker.damageVerdict', 'Requested AFTER damage ({{time}}) - hits before request: {{count}}', { count: damageBeforeRequest, time: fmtClock(requestAt) })}
          </span>
        )}
        {!hasFlags && (
          <span style={{ fontSize: '0.68rem', fontWeight: 500, color: 'var(--text-muted, #4a4a5a)' }}>
            {hasDamage
              ? t('restoreChecker.noRequestDamage', 'Damage dealt · no restore request')
              : t('restoreChecker.noRequest', 'No damage · no restore request')}
          </span>
        )}

        {/* Quick info badges */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          {flags.length > 0 && (
            <span style={{ fontSize: '0.58rem', fontWeight: 600, padding: '1px 6px', borderRadius: 3, background: 'rgba(245,158,11,0.1)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.15)' }}>
              {t('restoreChecker.requestBadge', 'Requests: {{count}}', { count: flags.length })}
            </span>
          )}
          {sortedDamage.length > 0 && (
            <span style={{ fontSize: '0.58rem', fontWeight: 600, padding: '1px 6px', borderRadius: 3, background: hasFlags ? 'rgba(239,68,68,0.1)' : 'rgba(255,255,255,0.04)', color: hasFlags ? '#ef4444' : 'var(--text-muted, #4a4a5a)', border: `1px solid ${hasFlags ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.06)'}` }}>
              {t('restoreChecker.hitBadge', 'Hits: {{count}}', { count: sortedDamage.length })}
            </span>
          )}
          {canToggle && (
            <span style={{ fontSize: '0.65rem', color: 'var(--text-muted, #4a4a5a)', transition: 'transform 0.15s', transform: open ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block' }}>›</span>
          )}
        </div>
      </div>

      {/* Expanded details */}
      {open && (
        <div style={{ padding: '0 1rem 0.6rem', display: 'flex', flexDirection: 'column', gap: '3px', animation: 'fadeSlideIn 0.15s ease' }}>
          {timeline.length === 0 && (
            <div style={{ padding: '4px 8px', fontSize: '0.66rem', color: 'var(--text-muted, #4a4a5a)' }}>
              {t('restoreChecker.watching', 'Watching this round - no damage or restore request yet')}
            </div>
          )}
          {/* Requests and hits in time order. Hits after the first request are dimmed. */}
          {timeline.map((item) => item.kind === 'req' ? (
            <div key={`f${item.i}`} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '3px 8px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.18)', borderRadius: '4px', fontSize: '0.66rem', minWidth: 0 }}>
              <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: '0.62rem', fontWeight: 600, minWidth: 56 }}>{item.flag.ts ? fmtClock(item.flag.ts) : ''}</span>
              <span style={{ fontSize: '0.55rem', fontWeight: 700, color: '#f59e0b', letterSpacing: '0.04em', flexShrink: 0 }}>{t('restoreChecker.requestTag', 'REQUEST')}</span>
              <span style={{ color: '#fb923c', fontWeight: 600, flexShrink: 0 }}>{item.flag.name}</span>
              {item.flag.message
                ? <span style={{ color: 'var(--text-secondary, #8b8b9e)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.flag.message}</span>
                : null}
              <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontWeight: 600, marginLeft: 'auto', flexShrink: 0 }}>{item.flag.keyword}</span>
            </div>
          ) : (
            <div key={item.i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '3px 8px', background: 'rgba(239,68,68,0.04)', borderRadius: '4px', fontSize: '0.68rem', opacity: (requestAt && item.ev.created_at && item.ev.created_at > requestAt) ? 0.45 : 1 }}>
              <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: '0.62rem', fontWeight: 600, minWidth: 56 }}>{fmtClock(item.ev.created_at)}</span>
              <span style={{ color: '#f87171', fontWeight: 600 }}>{item.ev.player_name || item.ev.steam_id}</span>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.55rem' }}>→</span>
              <span style={{ color: '#93c5fd', fontWeight: 500 }}>{item.ev.victim_name || item.ev.victim_steam_id || '?'}</span>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.6rem', fontFamily: 'var(--font-mono)', background: 'rgba(255,255,255,0.04)', padding: '0 4px', borderRadius: 3 }}>{item.ev.weapon}</span>
              {item.ev.damage && <span style={{ color: '#f59e0b', fontSize: '0.6rem', fontWeight: 600, marginLeft: 'auto' }}>-{item.ev.damage}hp</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Player-management live step tracker ────────────────────────────────────
// Renders the real-time progress of an add / remove / spec action. Steps flip
// from pending → running → done/skipped/error as WS `roster_progress` messages
// arrive. Purely presentational; driven entirely by the `progress` prop.
function PmStepTracker({ progress }) {
  const { t } = useTranslation();
  if (!progress) return null;

  const LABELS = {
    // add
    name:      t('matchDetail.pmStepName', 'Set player name'),
    avatar:    t('matchDetail.pmStepAvatar', 'Upload avatar'),
    backup:    t('matchDetail.pmStepBackup', 'Write side backup files'),
    admit:     t('matchDetail.pmStepAdmit', 'Admit to team'),
    // remove
    kick:      t('matchDetail.pmStepKick', 'Kick from server'),
    remove:    t('matchDetail.pmStepRemove', 'Remove from match'),
    resync:    t('matchDetail.pmStepResync', 'Re-sync names & backups'),
    // spec
    spec:      t('matchDetail.pmStepSpec', 'Add to spectators'),
    tempadmin: t('matchDetail.pmStepTempAdmin', 'Grant temp admin powers'),
  };

  const TITLES = {
    add:    t('matchDetail.pmTitleAdd', 'Adding player'),
    remove: t('matchDetail.pmTitleRemove', 'Removing player'),
    spec:   t('matchDetail.pmTitleSpec', 'Adding spectator'),
  };

  const anyRunning = progress.order.some(k => progress.steps[k] === 'running');
  const headerColor = progress.error ? '#ef4444' : progress.finished ? '#22c55e' : '#3b82f6';

  const dot = (status) => {
    const base = { width: 16, height: 16, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 700 };
    switch (status) {
      case 'running':
        return <span style={{ ...base, border: '2px solid rgba(59,130,246,0.3)', borderTopColor: '#3b82f6', animation: 'pmSpin 0.7s linear infinite' }} />;
      case 'done':
        return <span style={{ ...base, background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.4)', color: '#22c55e' }}>✓</span>;
      case 'skipped':
        return <span style={{ ...base, background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-subtle, rgba(255,255,255,0.1))', color: 'var(--text-muted, #6b6b7b)' }}>–</span>;
      case 'error':
        return <span style={{ ...base, background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)', color: '#ef4444' }}>✕</span>;
      default: // pending
        return <span style={{ ...base, border: '1px solid var(--border-subtle, rgba(255,255,255,0.1))', background: 'transparent' }} />;
    }
  };

  const rowColor = (status) => {
    if (status === 'done') return 'var(--text-primary, #e8e8ed)';
    if (status === 'running') return '#93c5fd';
    if (status === 'error') return '#fca5a5';
    if (status === 'skipped') return 'var(--text-muted, #6b6b7b)';
    return 'var(--text-muted, #6b6b7b)';
  };

  return (
    <div style={{
      marginTop: '0.5rem', borderRadius: 'var(--radius-md, 8px)',
      border: `1px solid ${progress.error ? 'rgba(239,68,68,0.35)' : progress.finished ? 'rgba(34,197,94,0.35)' : 'rgba(59,130,246,0.35)'}`,
      background: 'var(--bg-elevated, #1a1a24)', padding: '0.7rem 0.85rem',
    }}>
      <style>{`@keyframes pmSpin { to { transform: rotate(360deg); } }`}</style>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem' }}>
        {anyRunning && !progress.finished && (
          <span style={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid rgba(59,130,246,0.3)', borderTopColor: '#3b82f6', animation: 'pmSpin 0.7s linear infinite', flexShrink: 0 }} />
        )}
        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: headerColor, fontFamily: 'var(--font-sans)' }}>
          {TITLES[progress.action] || ''}
        </span>
        {progress.name && (
          <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary, #8b8b9e)', fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {progress.name}
          </span>
        )}
      </div>
      {/* Steps */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
        {progress.order.map((key, i) => {
          const status = progress.steps[key];
          return (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
              {dot(status)}
              <span style={{ fontSize: '0.76rem', color: rowColor(status), fontFamily: 'var(--font-sans)', transition: 'color 0.2s' }}>
                {LABELS[key] || key}
              </span>
              {status === 'skipped' && (
                <span style={{ fontSize: '0.62rem', color: 'var(--text-muted, #6b6b7b)', fontStyle: 'italic' }}>
                  {t('matchDetail.pmSkipped', 'skipped')}
                </span>
              )}
            </div>
          );
        })}
      </div>
      {progress.error && (
        <div style={{ marginTop: '0.55rem', fontSize: '0.72rem', color: '#fca5a5', lineHeight: 1.4 }}>
          {progress.error}
        </div>
      )}
    </div>
  );
}

// Player-management feature card: colored header (icon + title + description) + body.
// Defined at module scope (NOT inside MatchDetail's render) so its component identity
// is stable across renders. When it lived inside the render body, every poll
// (fetchPlayerStatus every 2s, fetchMatch every 3s) created a new function reference,
// forcing React to unmount/remount the whole card — which destroyed the <input> children
// and made typing impossible in the Add/Remove/Temp-Admin fields.
function PmFeatureCard({ accent, icon, title, desc, children }) {
  return (
    <div style={{
      border: `1px solid ${accent}44`, borderRadius: 'var(--radius-md, 8px)',
      background: 'var(--bg-card, #111118)', overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.55rem',
        padding: '0.6rem 0.8rem', background: `${accent}14`,
        borderBottom: `1px solid ${accent}33`,
      }}>
        <span style={{
          width: 26, height: 26, borderRadius: 'var(--radius-sm, 6px)', flexShrink: 0,
          background: `${accent}22`, color: accent,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>{icon}</span>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-primary, #e8e8ed)', fontFamily: 'var(--font-sans)' }}>{title}</span>
          {desc && <span style={{ fontSize: '0.68rem', color: 'var(--text-muted, #6b6b7b)', fontFamily: 'var(--font-sans)' }}>{desc}</span>}
        </div>
      </div>
      {/* Body */}
      <div style={{ padding: '0.8rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {children}
      </div>
    </div>
  );
}

/* ── Admin Logs Tab Component ── */
function AdminLogsTab({ matchId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!matchId) return;
    setLoading(true);
    axios.get(`/api/matches/${matchId}/admin-logs`)
      .then(res => {
        setLogs(res.data.logs || []);
      })
      .catch(err => {
        console.error('Failed to fetch admin logs:', err);
        setLogs([]);
      })
      .finally(() => setLoading(false));
  }, [matchId]);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted, #9ca3af)' }}>
        <div style={{ fontSize: '0.9rem' }}>Loading admin logs...</div>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted, #9ca3af)' }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📋</div>
        <div style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.25rem' }}>No Admin Logs</div>
        <div style={{ fontSize: '0.75rem' }}>No administrative events have been logged for this match yet.</div>
      </div>
    );
  }

  const getEventBadge = (eventType, data) => {
    const badges = {
      veto_action: (() => {
        const msg = (data.message || '').toLowerCase();
        if (msg.includes('removed')) return { label: 'VETO BAN', color: 'var(--accent-red, #ef4444)' };
        if (msg.includes('picked')) return { label: 'VETO PICK', color: 'var(--accent-green, #22c55e)' };
        if (msg.includes('left over')) return { label: 'DECIDER', color: 'var(--accent-amber, #f59e0b)' };
        return { label: 'VETO', color: 'var(--accent-purple, #a855f7)' };
      })(),
      match_created: { label: 'CREATED', color: 'var(--accent-blue, #3b82f6)' },
      match_started: { label: 'STARTED', color: 'var(--accent-green, #22c55e)' },
      knife_started: { label: 'KNIFE', color: 'var(--accent-cyan, #06b6d4)' },
      knife_winner: { label: 'KNIFE WIN', color: 'var(--accent-cyan, #06b6d4)' },
      side_chosen: { label: 'SIDE', color: 'var(--accent-cyan, #06b6d4)' },
      tech_pause: { label: 'TECH PAUSE', color: 'var(--accent-amber, #f59e0b)' },
      tech_unpause: { label: 'TECH UNPAUSE', color: 'var(--accent-green, #22c55e)' },
      admin_pause: { label: 'ADMIN PAUSE', color: 'var(--accent-red, #ef4444)' },
      admin_unpause: { label: 'ADMIN UNPAUSE', color: 'var(--accent-green, #22c55e)' },
      round_restore: { label: 'RESTORE', color: 'var(--accent-purple, #a855f7)' },
      match_cancelled: { label: 'CANCELLED', color: 'var(--accent-red, #ef4444)' },
      match_forfeit: { label: 'FORFEIT', color: 'var(--accent-red, #ef4444)' },
    };
    return badges[eventType] || { label: eventType.toUpperCase().replace(/_/g, ' '), color: 'var(--text-muted, #9ca3af)' };
  };

  const formatLogMessage = (eventType, data) => {
    if (eventType === 'veto_action') {
      return data.message || 'Veto action';
    }
    return data.message || `${eventType.replace(/_/g, ' ')}`;
  };

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary, #fff)', margin: 0 }}>
          Admin Logs
        </h3>
        <span style={{ fontSize: '0.6rem', color: 'var(--text-muted, #9ca3af)', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)', padding: '0.15rem 0.4rem', borderRadius: 'var(--radius-sm, 6px)', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          ADMIN ONLY
        </span>
        <div style={{ marginLeft: 'auto', fontSize: '0.7rem', color: 'var(--text-muted, #9ca3af)' }}>
          {logs.length} {logs.length === 1 ? 'event' : 'events'}
        </div>
      </div>
      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted, #9ca3af)', marginBottom: '1rem' }}>
        Complete audit trail of match events, veto actions, and administrative operations
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {logs.map((log, i) => {
          const { eventType, data, timestamp } = log;
          const badge = getEventBadge(eventType, data);
          const message = formatLogMessage(eventType, data);

          return (
            <div key={log.id || i} style={{ background: 'var(--bg-elevated, #1a1a23)', border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))', borderRadius: 'var(--radius-md, 8px)', padding: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.7rem', color: 'var(--text-muted, #9ca3af)', fontFamily: 'var(--font-mono, monospace)', minWidth: 24 }}>{i + 1}</span>
                <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: 'var(--radius-sm, 6px)', background: `${badge.color}10`, border: `1px solid ${badge.color}18`, color: badge.color }}>
                  {badge.label}
                </span>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary, #d1d5db)', flex: 1 }}>{message}</span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.7rem', paddingLeft: '2rem', flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--text-muted, #9ca3af)' }}>{new Date(timestamp).toLocaleString()}</span>

                {/* Show player name and Steam ID for veto actions */}
                {eventType === 'veto_action' && data.playerName && (
                  <>
                    <span style={{ color: 'var(--text-muted, #9ca3af)' }}>•</span>
                    <span style={{ color: 'var(--text-secondary, #d1d5db)', fontWeight: 600 }}>{data.playerName}</span>
                    <span style={{ color: 'var(--text-muted, #9ca3af)' }}>•</span>
                    <span style={{ color: 'var(--text-muted, #9ca3af)' }}>Steam ID:</span>
                    <span style={{ fontFamily: 'var(--font-mono, monospace)', color: 'var(--accent-green, #22c55e)', background: 'rgba(34,197,94,0.08)', padding: '0.2rem 0.5rem', borderRadius: 'var(--radius-sm, 6px)', border: '1px solid rgba(34,197,94,0.15)' }}>
                      {data.steamId}
                    </span>
                    {data.team && (
                      <>
                        <span style={{ color: 'var(--text-muted, #9ca3af)' }}>•</span>
                        <span style={{ color: 'var(--text-muted, #9ca3af)' }}>Team {data.team}</span>
                      </>
                    )}
                  </>
                )}

                {/* Show Steam ID for other events if available */}
                {eventType !== 'veto_action' && data.steamId && (
                  <>
                    <span style={{ color: 'var(--text-muted, #9ca3af)' }}>•</span>
                    <span style={{ color: 'var(--text-muted, #9ca3af)' }}>Steam ID:</span>
                    <span style={{ fontFamily: 'var(--font-mono, monospace)', color: 'var(--accent-green, #22c55e)', background: 'rgba(34,197,94,0.08)', padding: '0.2rem 0.5rem', borderRadius: 'var(--radius-sm, 6px)', border: '1px solid rgba(34,197,94,0.15)' }}>
                      {data.steamId}
                    </span>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MatchDetail() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const versionManager = React.useContext(VersionContext);
  const isAdminView = window.location.pathname.startsWith('/admin/');
  const isSuperAdmin = isAdminView && (user?.role === 'super_admin' || user?.role === 'admin');
  const userId = user?.id;

  const { id } = useParams();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backupFiles, setBackupFiles] = useState([]);
  const [showBackupModal, setShowBackupModal] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState('');
  const [playerStatus, setPlayerStatus] = useState(null);
  // CSTV/GOTV viewers watching the broadcast. Fetched via RCON `status` — these
  // clients connect to the relay port, not the game server, so they never show up
  // in playerStatus. { viewers: [{ userid, name, connected, ping }], spectators, available }
  const [cstvStatus, setCstvStatus] = useState(null);
  // Per-relay "copied" feedback for the CSTV connect strings, keyed by relay number (1|2).
  const [cstvCopyFeedback, setCstvCopyFeedback] = useState(null);
  // CSTV viewer history section: null = auto (open once the match has ended).
  const [showCstvHistory, setShowCstvHistory] = useState(null);
  // Relay number currently being toggled public/private (disables its button).
  const [cstvToggling, setCstvToggling] = useState(null);
  const [rconCustom, setRconCustom] = useState('');
  const [lastAction, setLastAction] = useState(null);
  const [alertModal, setAlertModal] = useState({ show: false, message: '', type: 'info' });
  const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
  const [rosterSteamId, setRosterSteamId] = useState('');
  const [rosterName, setRosterName] = useState('');
  const [rosterTeamSide, setRosterTeamSide] = useState('team1');
  const [addingToRoster, setAddingToRoster] = useState(false);
  const [removeSteamId, setRemoveSteamId] = useState('');
  const [removingPlayer, setRemovingPlayer] = useState(false);
  const [specSteamId, setSpecSteamId] = useState('');
  const [addingSpec, setAddingSpec] = useState(false);
  // Live step tracker for player-management actions (add / remove / spec).
  // Shape: { action, steps: { [key]: 'pending'|'running'|'done'|'skipped'|'error' }, order: [key], name, error }
  const [pmProgress, setPmProgress] = useState(null);
  const [tempAdmins, setTempAdmins] = useState([]);
  const [tempAdminsLoading, setTempAdminsLoading] = useState(false);
  const [roundHistory, setRoundHistory] = useState([]);
  const [adminChatMessage, setAdminChatMessage] = useState('');
  const [chatPrefix, setChatPrefix] = useState('');
  const [availableMaps, setAvailableMaps] = useState([]);
  const [bracketMaps, setBracketMaps] = useState([]);
  const [bracketSides, setBracketSides] = useState([]);
  const [bracketStarting, setBracketStarting] = useState(false);
  const [setupStep, setSetupStep] = useState(0);
  const [setupStepLabel, setSetupStepLabel] = useState('');
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const mountedRef = useRef(true);
  const logPanelRef = useRef(null);
  const matchRef = useRef(null); // always-current match - safe to read from WS closures
  // Restore Checker live-refresh throttle (damage_update fires on every hit)
  const restoreFetchAtRef = useRef(0);
  const restoreFetchTimerRef = useRef(null);
  const vitalsFetchAtRef = useRef(0); // live HP bars refresh throttle (damage_update)
  useEffect(() => () => { if (restoreFetchTimerRef.current) clearTimeout(restoreFetchTimerRef.current); }, []);

  const [selectedMapIndex, setSelectedMapIndex] = useState(null);
  const [selectedOtPeriod, setSelectedOtPeriod] = useState(null); // null = regulation, 1 = OT1, 2 = OT2, etc.
  // Steam IDs the admin has explicitly "Restore"d — overrides the auto ghost-player
  // filter (0/0/0/0 on a finished map) so a manually-restored player stays visible.
  const [forceShownSteamIds, setForceShownSteamIds] = useState(() => new Set());
  const [copyFeedback, setCopyFeedback] = useState(null);
  const [serverLogs, setServerLogs] = useState([]);
  const [chatLogs, setChatLogs] = useState([]);
  const [logTab, setLogTab] = useState('chat'); // 'server' | 'chat' | 'killfeed' | 'backup' | 'restore'
  // Admin match tab is synced to the URL (?tab=operations|scoreboard|players) so a
  // refresh or shared link stays on the same tab instead of resetting to Operations.
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_ADMIN_TABS = ['operations', 'scoreboard', 'players', 'admin-logs'];
  const urlTab = searchParams.get('tab');
  const adminMatchTab = VALID_ADMIN_TABS.includes(urlTab) ? urlTab : 'operations';
  const setAdminMatchTab = (tab) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    }, { replace: true });
  };

  // Collapsed state of the Match Controls sections (map controls, flow, danger zone, veto, …),
  // remembered in this browser for EVERY match page (live, upcoming, finished, cancelled): hiding
  // a section once hides it everywhere until it is shown again. Value = collapsed section keys,
  // e.g. { maps: true, danger: true }. It used to be stored per match (fluxbot_mc_collapsed_<id>),
  // so every other match showed it again; with no global value yet, the old one of this match is
  // the starting point.
  const COLLAPSE_KEY = 'fluxbot_mc_collapsed';
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try {
      const saved = localStorage.getItem(COLLAPSE_KEY) ?? (id ? localStorage.getItem(`fluxbot_mc_collapsed_${id}`) : null);
      return JSON.parse(saved || '{}') || {};
    } catch (_) { return {}; }
  });
  const toggleSection = (key) => {
    setCollapsedSections(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(next)); } catch (_) { }
      return next;
    });
  };
  // another tab hid / showed a section: follow it
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== COLLAPSE_KEY) return;
      try { setCollapsedSections(JSON.parse(e.newValue || '{}') || {}); } catch (_) { }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  // Collapsible section header: label + a Hide/Show toggle. Clicking anywhere on the
  // row toggles the section. `color` themes the label (e.g. red for the danger zone).
  const renderSectionHeader = (key, label, color) => {
    const collapsed = !!collapsedSections[key];
    return (
      <div
        onClick={() => toggleSection(key)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', marginBottom: collapsed ? 0 : '6px', cursor: 'pointer', userSelect: 'none' }}
        title={collapsed ? 'Show section' : 'Hide section'}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.84rem', fontWeight: 700, color: color || 'var(--text-secondary, #8b8b9e)', fontFamily: 'var(--font-sans)' }}>
          <span style={{ fontSize: '0.7rem', transition: 'transform 0.15s', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)', display: 'inline-block' }}>▾</span>
          {label}
        </span>
        <span style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--text-muted, #4a4a5a)' }}>
          {collapsed ? t('matchDetail.show', 'Show') : t('matchDetail.hide', 'Hide')}
        </span>
      </div>
    );
  };
  const [autoScroll, setAutoScroll] = useState(true);
  const autoScrollInitRef = useRef(false);
  const [logCount, setLogCount] = useState(0);
  const [serverLogMeta, setServerLogMeta] = useState({}); // mapIndex -> { line_count }
  const [demoStatus, setDemoStatus] = useState({}); // mapIndex -> { status, expiresAt, ... }
  const demoPollingRef = useRef({}); // timers for polling per mapIndex
  const [backupRounds, setBackupRounds] = useState([]); // round restore points
  const [backupRestoreBlock, setBackupRestoreBlock] = useState({ blocked: false });
  const [backupLoading, setBackupLoading] = useState(false);
  const [, setDemoCountdown] = useState(0); // tick to force re-render for live countdown
  const [restoringRound, setRestoringRound] = useState(null); // filename being restored
  const [killFeed, setKillFeed] = useState([]); // historical and live kills (joins / leaves in the Chat tab)
  const [feedTick, setFeedTick] = useState(0); // bumped on kill_event → the Kill Feed tab fetches new events
  const [streamDelay, setStreamDelay] = useState(0);
  const streamDelayEditingRef = useRef(false); // true while admin is focused on the delay input
  const chatPrefixEditingRef = useRef(false); // true while admin is focused on the prefix input
  const [showForfeitModal, setShowForfeitModal] = useState(false);
  const [forfeitWinner, setForfeitWinner] = useState('team1');
  const [forfeitMapScores, setForfeitMapScores] = useState([]);
  const [forfeitMatchScore, setForfeitMatchScore] = useState({ team1: 0, team2: 0 });
  const [forfeitSubmitting, setForfeitSubmitting] = useState(false);

  const [showMapForfeitModal, setShowMapForfeitModal] = useState(false);
  const [mapForfeitIndex, setMapForfeitIndex] = useState(0);
  const [mapForfeitScore1, setMapForfeitScore1] = useState(0);
  const [mapForfeitScore2, setMapForfeitScore2] = useState(0);
  const [mapForfeitSubmitting, setMapForfeitSubmitting] = useState(false);

  const [showRemoveForfeitModal, setShowRemoveForfeitModal] = useState(false);
  const [removeForfeitIndex, setRemoveForfeitIndex] = useState(0);
  const [removeForfeitSubmitting, setRemoveForfeitSubmitting] = useState(false);

  // ── Server Migration state ──
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrationProgress, setMigrationProgress] = useState(null); // { step, total_steps, message }
  const [migrationTargetServer, setMigrationTargetServer] = useState(null);
  const [availableServers, setAvailableServers] = useState([]);
  const [isManualMigration, setIsManualMigration] = useState(false);
  const [manualFilesConfirmed, setManualFilesConfirmed] = useState(false);
  const [migrationFiles, setMigrationFiles] = useState(null); // from /api/matches/:id/migration-files
  const [sourceHasBackup, setSourceHasBackup] = useState(true);

  // ── Rehost state ──
  const [showRehostModal, setShowRehostModal] = useState(false);
  const [rehosting, setRehosting] = useState(false);
  const [replayMapIdx, setReplayMapIdx] = useState(null); // map index whose Replay modal is open
  const [rehostProgress, setRehostProgress] = useState(null); // { step, total_steps, message }
  const [rehostScores, setRehostScores] = useState([]); // available scores from DB
  const [rehostSelectedRound, setRehostSelectedRound] = useState(null);
  const [rehostMatchInfo, setRehostMatchInfo] = useState(null); // { map, team1_name, team2_name }
  const [damageEvents, setDamageEvents] = useState([]);

  // ── Restore Checker: per-map collapse state ──
  // Initialized lazily from match.current_map_index so the live map starts open.
  const [expandedRestoreMaps, setExpandedRestoreMaps] = useState(() => {
    const live = (typeof match?.current_map_index === 'number')
      ? match.current_map_index
      : parseInt(match?.current_map_index, 10) || 0;
    return new Set([live]);
  });
  // Previous maps (BO3/BO5) are hidden behind a toggle; only the current map shows.
  const [showPrevRestoreMaps, setShowPrevRestoreMaps] = useState(false);
  // When the live map changes (or the match first loads — the lazy init above runs
  // before `match` exists and defaults to map 0), show ONLY the current map expanded
  // and fold the previous ones away again.
  useEffect(() => {
    const live = (typeof match?.current_map_index === 'number')
      ? match.current_map_index
      : parseInt(match?.current_map_index, 10) || 0;
    setExpandedRestoreMaps(new Set([live]));
    setShowPrevRestoreMaps(false);
  }, [match?.current_map_index]);

  // ── Match Timer State ──
  const [matchTimer, setMatchTimer] = useState(null); // { type, remaining, total, stage }
  usePageTitle(matchTabTitle(match, matchTimer)); // "[11-1] A vs B" / "Tech - A | [11-1] A vs B", follows the live score
  // per-map live start / end: the duration under the status pill + the date line of the map shown
  const mapTimes = useMapTimes(match?.id, match?.status, !isAdminView);

  // ── Server-authoritative ownership flag ──
  const isOwner = match?.is_owner === true;

  useEffect(() => {
    if (!backupRestoreBlock?.blocked || !backupRestoreBlock.remainingMs) return undefined;
    const timer = setTimeout(() => {
      setBackupRestoreBlock(prev => prev?.blocked ? { ...prev, blocked: false, remainingMs: 0, remainingSeconds: 0 } : prev);
    }, Math.max(500, backupRestoreBlock.remainingMs));
    return () => clearTimeout(timer);
  }, [backupRestoreBlock?.blocked, backupRestoreBlock?.remainingMs]);

  const restoreBlockNotice = backupRestoreBlock?.blocked
    ? `Round restore is disabled during round restart delay. Available in ${backupRestoreBlock.remainingSeconds || 1}s.`
    : '';

  const fetchMatch = React.useCallback(async () => {
    if (!id || id === 'undefined') return;
    try {
      const response = await axios.get(`/api/matches/${id}`);
      setMatch(response.data);
      // Only sync prefix from server if user is NOT actively typing in the prefix field
      if (!chatPrefixEditingRef.current) {
        setChatPrefix(response.data.chat_prefix || '[Flux]');
      }
      matchRef.current = response.data;
      // Only sync delay from server if user is NOT actively typing in the delay field
      if (!streamDelayEditingRef.current) {
        setStreamDelay(response.data.stream_delay || 0);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching match:', error);
      setMatch(null);
      setLoading(false);
    }
  }, [id]);

  const fetchPlayerStatus = React.useCallback(async () => {
    if (!id || id === 'undefined') return;
    try {
      const response = await axios.get(`/api/matches/${id}/player-status`);
      setPlayerStatus(response.data);
    } catch (error) {
      console.error('Error fetching player status:', error);
    }
  }, [id]);

  const fetchRoundHistory = React.useCallback(async () => {
    if (!id || id === 'undefined') return;
    try {
      const response = await axios.get(`/api/matches/${id}/rounds`);
      setRoundHistory(response.data.rounds || []);
    } catch (error) {
      console.error('Error fetching round history:', error);
    }
  }, [id]);

  const fetchCstvStatus = React.useCallback(async () => {
    if (!id || id === 'undefined') return;
    try {
      const res = await axios.get(`/api/matches/${id}/cstv-status`);
      setCstvStatus(res.data);
    } catch (error) {
      // Request failed (network / 4xx / 5xx). Store an explicit "unavailable" state —
      // resetting to null rendered "Checking CSTV…" forever.
      setCstvStatus({ viewers: [], relays: [], spectators: 0, available: false, reason: 'fetch_failed' });
    }
  }, [id]);

  // Toggle a CSTV relay's public visibility (relay = 1 | 2). On success, patch the
  // match locally so the button + public-badge flip immediately without a refetch.
  const toggleCstvPublic = React.useCallback(async (relay, makePublic) => {
    if (!id || (relay !== 1 && relay !== 2)) return;
    setCstvToggling(relay);
    try {
      const res = await axios.put(`/api/matches/${id}/cstv-public`, { relay, public: makePublic });
      const col = relay === 1 ? 'cstv1_public' : 'cstv2_public';
      setMatch(prev => prev ? { ...prev, [col]: res.data.public ? 1 : 0 } : prev);
    } catch (error) {
      setAlertModal({ show: true, type: 'error', message: error?.response?.data?.error || t('matchDetail.cstvToggleFailed', 'Failed to update CSTV visibility') });
    } finally {
      setCstvToggling(null);
    }
  }, [id, t]);

  const fetchDamageEvents = React.useCallback(async () => {
    if (!id || id === 'undefined') return;
    try {
      const res = await axios.get(`/api/matches/${id}/restore-checks`);
      setDamageEvents(res.data || []);
    } catch (error) {
      console.error('Error fetching restore checks:', error);
    }
  }, [id]);

  const fetchAvailableMaps = React.useCallback(async (pool) => {
    try {
      // Scope to the match's pool so the manual map picker shows only that pool's
      // maps (not every pool's maps merged, which caused duplicate entries).
      const url = pool ? `/api/maps/public?pool=${encodeURIComponent(pool)}` : '/api/maps/public';
      const res = await axios.get(url);
      // De-duplicate by name — the same map can exist under multiple owners; the
      // picker only needs one option per unique map name.
      const seen = new Set();
      const deduped = (res.data || []).filter(m => {
        const key = (m.name || '').toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setAvailableMaps(deduped);
    } catch (error) {
      console.error('Error fetching map dictionary', error);
    }
  }, []);

  const fetchChatHistory = React.useCallback(async () => {
    if (!id || id === 'undefined') return;
    try {
      const response = await axios.get(`/api/matches/${id}/chats`);
      if (response.data && response.data.length > 0) {
        setChatLogs(response.data.map(chat => ({
          ts: (parseServerDate(chat.created_at) || new Date()).getTime(),
          is_chat: true,
          player_name: chat.player_name,
          chat_message: chat.message,
          team: chat.team,
          is_team: !!chat.is_team,
          team_no: chat.team_no ?? null,
          steam_id: chat.steam_id || null,
          map_index: chat.map_index ?? 0
        })));
      }
    } catch (error) {
      console.error('Error fetching chat history:', error);
    }
  }, [id]);

  const fetchServerLogMeta = React.useCallback(async () => {
    if (!id || id === 'undefined') return;
    try {
      const res = await axios.get(`/api/matches/${id}/server-logs`);
      const meta = {};
      (res.data.maps || []).forEach(m => { meta[m.map_index] = m; });
      setServerLogMeta(meta);
    } catch (_) { /* non-admin or no logs yet */ }
  }, [id]);

  const fetchBackupRounds = React.useCallback(async () => {
    if (!id || id === 'undefined') return;
    setBackupLoading(true);
    try {
      const res = await axios.get(`/api/matches/${id}/backups`);
      setBackupRounds(res.data.rounds || []);
      setBackupRestoreBlock(res.data.restoreBlock || { blocked: false });
    } catch (_) {
      setBackupRounds([]);
      setBackupRestoreBlock({ blocked: false });
    } finally {
      setBackupLoading(false);
    }
  }, [id]);

  const fetchKillFeed = React.useCallback(async () => {
    if (!id || id === 'undefined') return;
    try {
      const res = await axios.get(`/api/matches/${id}/killfeed`);
      setKillFeed(res.data || []);
    } catch (_) {
      console.error('Failed to fetch kill feed');
    }
  }, [id]);

  const fetchHistoricalLogs = React.useCallback(async (mapIdx) => {
    if (!id || id === 'undefined' || mapIdx == null) return;
    try {
      const res = await axios.get(`/api/matches/${id}/maps/${mapIdx}/logs?limit=500`);
      if (res.data && res.data.lines && res.data.lines.length > 0) {
        const lines = res.data.lines.map(r => ({ line: r.line, ts: (parseServerDate(r.created_at) || new Date()).getTime(), is_chat: false }));
        setServerLogs(lines);
        setLogCount(lines.length);
      }
    } catch (_) { /* not admin or no logs yet */ }
  }, [id]);

  // Download logs for a specific map using auth-aware axios fetch (fixes download.json issue)
  const handleDownloadLogs = React.useCallback((mapIdx) => async (e) => {
    e && e.preventDefault();
    try {
      const res = await axios.get(`/api/matches/${id}/maps/${mapIdx}/logs/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/plain' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `match_${id}_map${parseInt(mapIdx) + 1}.log`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download logs:', err);
    }
  }, [id]);

  // Fetch demo status for a specific map
  const fetchDemoStatus = React.useCallback(async (mapIdx) => {
    if (!id || id === 'undefined' || mapIdx == null) return;
    try {
      const res = await axios.get(`/api/matches/${id}/maps/${mapIdx}/demo/status`);
      const s = res.data;
      setDemoStatus(prev => ({ ...prev, [mapIdx]: s }));
      // Keep polling while downloading or uploading
      if (s.status === 'downloading' || s.status === 'uploading') {
        if (demoPollingRef.current[mapIdx]) clearTimeout(demoPollingRef.current[mapIdx]);
        demoPollingRef.current[mapIdx] = setTimeout(() => fetchDemoStatus(mapIdx), 3000);
      } else {
        if (demoPollingRef.current[mapIdx]) {
          clearTimeout(demoPollingRef.current[mapIdx]);
          delete demoPollingRef.current[mapIdx];
        }
      }
    } catch (_) { }
  }, [id]);

  // Request demo download for a map
  const handleRequestDemo = React.useCallback(async (mapIdx) => {
    setDemoStatus(prev => ({ ...prev, [mapIdx]: { status: 'downloading' } }));
    try {
      await axios.post(`/api/matches/${id}/maps/${mapIdx}/demo/request`);
      // Start polling
      fetchDemoStatus(mapIdx);
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.error ? err.response.data.error : 'Request failed';
      setDemoStatus(prev => ({ ...prev, [mapIdx]: { status: 'error', error: msg } }));
    }
  }, [id, fetchDemoStatus]);

  // Download demo file (auth-aware native browser download)
  const handleDemoDownload = React.useCallback((mapIdx) => () => {
    try {
      // Use query token for direct authenticated download
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const downloadUrl = `${backendUrl}/api/matches/${id}/maps/${mapIdx}/demo/download?token=${token}`;

      const link = document.createElement('a');
      link.href = downloadUrl;
      const ds = demoStatus[mapIdx] || {};
      link.setAttribute('download', ds.filename || `match_${id}_map${parseInt(mapIdx) + 1}.dem`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Demo download failed:', err);
    }
  }, [id, demoStatus]);

  // Retry upload for a local demo — tries Gofile then Pixeldrain
  const handleRetryUpload = React.useCallback(async (mapIdx) => {
    const token = localStorage.getItem('token');
    setDemoStatus(prev => ({ ...prev, [mapIdx]: { ...prev[mapIdx], status: 'uploading' } }));
    try {
      const res = await axios.post(`/api/matches/${id}/maps/${mapIdx}/demo/retry-upload`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Server will broadcast the new status — fetch it to sync
      await fetchDemoStatus(mapIdx);
      if (res.data.url) {
        window.open(res.data.url, '_blank');
      }
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.error ? err.response.data.error : 'Retry failed';
      setDemoStatus(prev => ({ ...prev, [mapIdx]: { ...prev[mapIdx], status: 'ready', error: msg } }));
    }
  }, [id, fetchDemoStatus]);

  useEffect(() => {
    fetchMatch();
    fetchPlayerStatus();
    fetchRoundHistory();
    fetchAvailableMaps();
    fetchKillFeed();
    if (isAdminView && isOwner) fetchTempAdmins();
    mountedRef.current = true;

    const statusInterval = setInterval(() => {
      if (!mountedRef.current) return clearInterval(statusInterval);
      // Stop player-status polling for finished/cancelled matches — no in-game state to track
      const currentMatch = matchRef?.current;
      if (currentMatch && (currentMatch.status === 'finished' || currentMatch.status === 'cancelled')) {
        return clearInterval(statusInterval);
      }
      fetchPlayerStatus();
    }, 2000);

    // Polling fallback: refresh match data every 3s for live matches.
    // This guarantees score/stage/stats update even if WebSocket drops.
    let pollInterval = null;
    const startPoll = () => {
      if (pollInterval) clearInterval(pollInterval);
      pollInterval = setInterval(() => {
        if (!mountedRef.current) return clearInterval(pollInterval);
        const currentMatch = matchRef?.current;
        // Stop polling once match is done — nothing changes after finish
        if (currentMatch && (currentMatch.status === 'finished' || currentMatch.status === 'cancelled')) {
          return clearInterval(pollInterval);
        }
        fetchMatch();
        fetchRoundHistory();
      }, 3000);
    };
    startPoll();

    function connectWs() {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.hostname;
      const port = (host === 'localhost' || host === '127.0.0.1') ? ':5000' : (window.location.port ? `:${window.location.port}` : '');
      const token = localStorage.getItem('token');
      const wsUrl = `${protocol}//${host}${port}/ws${token ? `?token=${token}` : ''}`;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('✅ [MatchDetail] WebSocket Connected to:', wsUrl);
        if (versionManager && typeof versionManager.checkVersion === 'function') {
          versionManager.checkVersion();
        }
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          // live_log: handled separately — filter by server_id or match_id
          if (message.type === 'live_log' && message.data) {
            // Skip live log processing for non-owners (they can't see these tabs)
            const currentIsOwner = matchRef.current?.is_owner;
            if (!currentIsOwner && isAdminView) return;
            const currentMatch = matchRef.current;
            const serverMatch = currentMatch && message.data.server_id && String(message.data.server_id) === String(currentMatch.server_id);
            const matchMatch = message.data.match_id && String(message.data.match_id) === String(id);
            if (serverMatch || matchMatch) {
              const entry = { line: message.data.line, ts: message.data.ts, is_chat: message.data.is_chat, is_team: !!message.data.is_team, team_no: message.data.team_no ?? null, steam_id: message.data.steam_id || null, player_name: message.data.player_name, chat_message: message.data.chat_message, team: message.data.team, map_index: message.data.map_index ?? 0 };
              setServerLogs(prev => { const next = [...prev, entry]; return next.length > 500 ? next.slice(-500) : next; });
              if (message.data.is_chat) {
                setChatLogs(prev => { const next = [...prev, entry]; return next.length > 1000 ? next.slice(-1000) : next; });
              }
              setLogCount(c => c + 1);
            }
            return;
          }

          // map_logs_finalized: refresh log metadata so download button appears
          if (message.type === 'map_logs_finalized' && message.data && String(message.data.match_id) === String(id)) {
            if (matchRef.current?.is_owner) fetchServerLogMeta();
            return;
          }

          // demo_status_update: auto-refresh demo button when status changes (e.g. auto-download completes)
          if (message.type === 'demo_status_update' && message.data && String(message.data.match_id) === String(id)) {
            const { map_index, ...statusData } = message.data;
            delete statusData.match_id;
            setDemoStatus(prev => ({ ...prev, [map_index]: statusData }));
            // If still in progress, start polling for progress updates
            if (statusData.status === 'downloading' || statusData.status === 'uploading') {
              fetchDemoStatus(map_index);
            }
            return;
          }

          // round_restored: server archived/un-archived kills & rounds — refetch everything
          if (message.type === 'round_restored' && message.data && String(message.data.match_id) === String(id)) {
            fetchKillFeed();
            fetchMatch();
            fetchRoundHistory();
            fetchBackupRounds();
            return;
          }

          // backup_available: new backup file created after round end — auto-refresh backup list
          if (message.type === 'backup_available' && message.data && String(message.data.match_id) === String(id)) {
            fetchBackupRounds();
            return;
          }

          // damage_update: fired by the server on every hit (and every chat line) — the
          // live round in the Restore Checker must show damage as it happens. Hits come
          // in bursts, so throttle to one refetch per ~1.5s (with a trailing call so the
          // last hit of a burst is never missed).
          if (message.type === 'damage_update' && message.data && String(message.data.match_id) === String(id)) {
            if (!matchRef.current?.is_owner) return;
            // Live HP bars (Connected Players): refresh on hits too, not just the 2s poll.
            // Separate ~700ms throttle — player-status is cheap (vitals are in memory).
            {
              const nowPs = Date.now();
              if (nowPs - (vitalsFetchAtRef.current || 0) >= 700) {
                vitalsFetchAtRef.current = nowPs;
                fetchPlayerStatus();
              }
            }
            const now = Date.now();
            const wait = 1500 - (now - (restoreFetchAtRef.current || 0));
            if (wait <= 0) {
              restoreFetchAtRef.current = now;
              fetchDamageEvents();
            } else if (!restoreFetchTimerRef.current) {
              restoreFetchTimerRef.current = setTimeout(() => {
                restoreFetchTimerRef.current = null;
                restoreFetchAtRef.current = Date.now();
                fetchDamageEvents();
              }, wait);
            }
            return;
          }

          // restore_check_update: round ended OR a keyword chat arrived — refresh restore tab
          if (message.type === 'restore_check_update' && message.data && String(message.data.match_id) === String(id)) {
            if (matchRef.current?.is_owner) fetchDamageEvents();
            return;
          }

          // chat_message: legacy path, kept for back-compat in case other
          // panels still emit it. Restore Checker now listens to
          // `restore_check_update` (see above) which is sent on every round
          // end and on every keyword chat.
          if (message.type === 'chat_message' && message.data && String(message.data.match_id) === String(id)) {
            if (matchRef.current?.is_owner) fetchDamageEvents();
            return;
          }

          // roster_progress: live step tracker for player management (add/remove/spec)
          if (message.type === 'roster_progress' && message.data && String(message.data.match_id) === String(id)) {
            const { step, status } = message.data;
            setPmProgress(prev => {
              if (!prev) return prev; // no active action locally - ignore stray messages
              if (!(step in prev.steps)) return prev;
              return { ...prev, steps: { ...prev.steps, [step]: status } };
            });
            return;
          }

          // match_setup_progress: update setup overlay
          if (message.type === 'match_setup_progress' && message.data && String(message.data.match_id) === String(id)) {
            setSetupStep(message.data.step);
            setSetupStepLabel(message.data.label || '');
            if (message.data.step >= 8 || message.data.step === -1) {
              setTimeout(() => {
                setBracketStarting(false);
                setSetupStep(0);
                if (message.data.step >= 8) fetchMatch();
              }, message.data.step === -1 ? 3000 : 1500);
            }
            return;
          }

          if (message.data && message.data.match_id == id) {
            // Migration progress
            if (message.type === 'migration_progress') {
              const { step, total_steps, message: msg } = message.data;
              if (step === -1) {
                // Migration failed
                setMigrating(false);
                setMigrationProgress({ step: -1, total_steps, message: msg });
                setTimeout(() => setMigrationProgress(null), 8000);
              } else if (step >= total_steps) {
                // Migration complete
                setMigrating(false);
                setMigrationProgress({ step, total_steps, message: msg });
                setTimeout(() => { setMigrationProgress(null); fetchMatch(); }, 3000);
              } else {
                setMigrationProgress({ step, total_steps, message: msg });
              }
              return;
            }
            // Rehost progress
            if (message.type === 'rehost_progress') {
              const { step, total_steps, message: msg } = message.data;
              if (step === -1) {
                setRehosting(false);
                setRehostProgress({ step: -1, total_steps, message: msg });
                setTimeout(() => setRehostProgress(null), 8000);
              } else if (step >= total_steps) {
                setRehosting(false);
                setRehostProgress({ step, total_steps, message: msg });
                setTimeout(() => { setRehostProgress(null); fetchMatch(); }, 3000);
              } else {
                setRehostProgress({ step, total_steps, message: msg });
              }
              return;
            }
            if (message.type === 'kill_event') {
              setKillFeed(prev => [...prev, message.data]);
              setFeedTick(n => n + 1);
              return;
            }
            if (message.type === 'tech_usage_update') {
              setMatch(prev => prev ? {
                ...prev,
                tech_used_team1: message.data.tech_used_team1,
                tech_used_team2: message.data.tech_used_team2,
                tech_budget_seconds: message.data.tech_budget_seconds
              } : prev);
              return;
            }
            if (message.type === 'score_update') {
              const s1 = message.data.score_team1;
              const s2 = message.data.score_team2;
              if (message.data.restoreBlock) setBackupRestoreBlock(message.data.restoreBlock);
              setMatch(prev => ({ ...prev, score_team1: s1, score_team2: s2 }));
              fetchRoundHistory();
              fetchMatch();
              // Inject backup entry instantly from score data — no server round-trip needed
              const mapIdx = matchRef.current?.current_map_index ?? 0;
              const roundNum = s1 + s2;
              if (roundNum > 0) {
                setBackupRounds(prev => {
                  const exists = prev.some(r => r.map_id === mapIdx && r.round === roundNum);
                  if (exists) return prev;
                  const entry = { map_id: mapIdx, round: roundNum, score1: s1, score2: s2, filename: null, pending: true, created_at: new Date().toISOString() };
                  const next = [...prev, entry];
                  next.sort((a, b) => a.map_id - b.map_id || a.round - b.round);
                  return next;
                });
              }
            } else if (message.type === 'player_stats_update') {
              fetchMatch(); // Re-fetch stats (important after round restore rollback)
            } else if (message.type === 'match_update' || message.type === 'match_finished' || message.type === 'match_started') {
              fetchMatch();
            } else if (message.type === 'player_status_update') {
              setPlayerStatus(message.data);
            } else if (message.type === 'match_stage_change') {
              setMatch(prev => prev ? { ...prev, match_stage: message.data.stage } : null);
            } else if (message.type === 'match_status_change') {
              setMatch(prev => prev ? { ...prev, status: message.data.status } : null);
            } else if (message.type === 'match_timer_update') {
              setMatchTimer(message.data);
            }
          }
        } catch (error) {
          console.error('WS Parse Error:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('❌ [MatchDetail] WS Error:', error);
      };

      ws.onclose = (event) => {
        console.warn('⚠️ [MatchDetail] WS Closed:', event.code, event.reason);
        wsRef.current = null;
        if (mountedRef.current) reconnectRef.current = setTimeout(connectWs, 3000);
      };
    }
    connectWs();

    return () => {
      mountedRef.current = false;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) wsRef.current.close();
      clearInterval(statusInterval);
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [id, fetchMatch, fetchPlayerStatus, isSuperAdmin, fetchDamageEvents]);

  // ── Owner-only fetches: wait until match.is_owner is known ──
  useEffect(() => {
    if (!match || !isAdminView) return;
    if (match.is_owner !== true) return;
    fetchChatHistory();
    fetchServerLogMeta();
    // Restore Checker is owner/super-admin (super admins are is_owner too) — was
    // previously fetched for super admins only, so match owners always saw it empty.
    fetchDamageEvents();
  }, [match?.is_owner, isAdminView, fetchChatHistory, fetchServerLogMeta, fetchDamageEvents]);

  // ── CSTV viewers poll ── owner + Operations tab only, and only while the match
  // is live (it hits RCON `status` on the server, so keep it off finished matches
  // and off the other tabs). Dedicated 5s interval so it doesn't add RCON load to
  // the 2s player-status poll.
  useEffect(() => {
    if (!match || !isAdminView || match.is_owner !== true) return undefined;
    if (adminMatchTab !== 'operations') return undefined;
    // Ended match: no polling and no RCON — one fetch for the "match over" state plus
    // the CSTV viewer name history (the server answers it from the DB).
    if (match.status === 'finished' || match.status === 'cancelled') {
      fetchCstvStatus();
      return undefined;
    }
    fetchCstvStatus();
    const timer = setInterval(() => {
      if (!mountedRef.current) return clearInterval(timer);
      fetchCstvStatus();
    }, 5000);
    return () => clearInterval(timer);
  }, [match?.id, match?.is_owner, match?.status, isAdminView, adminMatchTab, fetchCstvStatus]);

  // Re-fetch the map picker list scoped to this match's pool once it's known,
  // so the manual map dropdown only shows that pool's maps (no cross-pool dupes).
  useEffect(() => {
    if (!match) return;
    fetchAvailableMaps(match.map_pool || 'active_duty');
  }, [match?.id, match?.map_pool, fetchAvailableMaps]);

  // When the user switches map tabs OR match data loads, fetch historical logs for that map (owner only)
  useEffect(() => {
    if (!match || selectedMapIndex == null) return;
    if (!match.is_owner) return;
    fetchHistoricalLogs(selectedMapIndex);
  }, [match?.id, match?.is_owner, selectedMapIndex, fetchHistoricalLogs]);

  // Fetch demo status for all maps on mount (so "Download Demo" shows if already uploaded to Gofile)
  useEffect(() => {
    if (!match || !isAdminView) return;
    if (!match.is_owner) return;
    const bestOf = match.best_of || 1;
    const totalMaps = bestOf === 1 ? 1 : bestOf;
    for (let i = 0; i < totalMaps; i++) {
      fetchDemoStatus(i);
    }
  }, [match?.id, match?.is_owner, match?.best_of, isAdminView, fetchDemoStatus]);

  // Disable auto-scroll when match is finished/cancelled (no new events to scroll to)
  useEffect(() => {
    if (!match) return;
    if (!autoScrollInitRef.current && (match.status === 'finished' || match.status === 'cancelled')) {
      setAutoScroll(false);
      autoScrollInitRef.current = true;
    }
  }, [match?.status]);

  // Live countdown ticker — re-renders every second while any demo is in 'ready' state
  useEffect(() => {
    const anyReady = Object.values(demoStatus).some(ds => ds && ds.status === 'ready');
    if (!anyReady) return;
    const tick = setInterval(() => setDemoCountdown(c => c + 1), 1000);
    return () => clearInterval(tick);
  }, [demoStatus]);

  // Smart-default the selected map tab once match data is available
  useEffect(() => {
    if (!match) return;
    const currentIdx = typeof match.current_map_index === 'number' ? match.current_map_index : parseInt(match.current_map_index, 10) || 0;
    if (match.status === 'finished' || match.status === 'cancelled') {
      // finished/cancelled: always show Map 1 (index 0)
      setSelectedMapIndex(0);
    } else {
      // live/pending: show the currently active map
      setSelectedMapIndex(currentIdx);
    }
  }, [match?.id, match?.status, match?.current_map_index]);

  const getTeamSides = () => {
    if (!match) return { team1Side: 'CT', team2Side: 'T', startingSide1: 'CT', startingSide2: 'T' };

    // Select the correct map index for the tab being viewed
    const targetIndex = typeof selectedMapIndex === 'number'
      ? selectedMapIndex
      : (typeof match.current_map_index === 'number' ? match.current_map_index : 0);

    let mapSides = null;
    try { mapSides = typeof match.map_sides === 'string' ? JSON.parse(match.map_sides) : match.map_sides; } catch (e) { }

    // map_sides[targetIndex] = 1st-half STARTING side for this map (never overwritten at halftime).
    // Compute the CURRENT side by applying MR12 flip from total rounds played.
    const startingValue = (Array.isArray(mapSides) && mapSides[targetIndex])
      ? mapSides[targetIndex]
      : (match.starting_side || 'team1_ct');

    const startingTeam1IsCt = (startingValue === 'team1_ct');

    const r = (match.score_team1 || 0) + (match.score_team2 || 0);

    const mpMaxRounds = match?.mp_maxrounds || 24;
    const mpOtMaxRounds = match?.mp_overtime_maxrounds || 6;
    const roundsPerHalf = mpMaxRounds / 2;
    const roundsPerOTHalf = mpOtMaxRounds / 2;

    let flipped = r >= roundsPerHalf;
    if (r >= mpMaxRounds) {
      const otRound = r - mpMaxRounds;
      const otHalf = Math.floor(otRound / roundsPerOTHalf);
      if (otHalf % 2 === 1) flipped = !flipped;
    }

    const team1IsCt = flipped ? !startingTeam1IsCt : startingTeam1IsCt;
    const team1Side = team1IsCt ? 'CT' : 'T';
    const team2Side = team1IsCt ? 'T' : 'CT';

    // startingSide = the side each team had in the 1st half (for scoreboard header)
    const startingSide1 = startingTeam1IsCt ? 'CT' : 'T';
    const startingSide2 = startingTeam1IsCt ? 'T' : 'CT';

    return { team1Side, team2Side, startingSide1, startingSide2 };
  };

  const fetchBackups = async () => {
    try {
      const response = await axios.get(`/api/matches/${id}/backups`);
      const allRounds = response.data.rounds || [];
      setBackupRestoreBlock(response.data.restoreBlock || { blocked: false });
      if (response.data.restoreBlock?.blocked) {
        setAlertModal({ show: true, message: `Round restore is disabled during round restart delay. Try again in ${response.data.restoreBlock.remainingSeconds || 1}s.`, type: 'error' });
        return;
      }
      // Only the CURRENT map's rounds. Restoring another map's backup would load it on the wrong map
      // (score/series/logs all go wrong) — an earlier map goes through Map controls → Replay.
      const currentMap = match.current_map_index ?? 0;
      setBackupFiles(allRounds.filter(r => Number(r.map_id) === Number(currentMap)));
      setShowBackupModal(true);
    } catch (error) {
      setAlertModal({ show: true, message: 'Error fetching backups', type: 'error' });
    }
  };

  const handleRestore = async () => {
    if (!selectedBackup) return;
    if (backupRestoreBlock?.blocked) {
      setAlertModal({ show: true, message: `Round restore is disabled during round restart delay. Try again in ${backupRestoreBlock.remainingSeconds || 1}s.`, type: 'error' });
      return;
    }
    const round = backupFiles.find(r => r.filename === selectedBackup);
    const label = round
      ? `Round ${round.round}: ${match.team1_name} ${round.score1 ?? '?'} - ${round.score2 ?? '?'} ${match.team2_name}`
      : selectedBackup;
    setConfirmModal({
      show: true,
      message: `Restore to ${label}? The match will be rolled back to this point.`,
      onConfirm: async () => {
        setConfirmModal({ show: false, message: '', onConfirm: null });
        try {
          await axios.post(`/api/matches/${id}/restore`, { filename: selectedBackup });
          setAlertModal({ show: true, message: `${label} restored successfully!`, type: 'success' });
          setShowBackupModal(false);
          setSelectedBackup('');
          fetchMatch();
        } catch (error) {
          setAlertModal({ show: true, message: error.response?.data?.error || 'Restoration failed', type: 'error' });
        }
      }
    });
  };

  const handleCustomRcon = async () => {
    const cmd = (rconCustom || '').trim();
    if (!cmd) return;
    try {
      await axios.post(`/api/matches/${id}/rcon`, { command: cmd });
      setLastAction('Command sent');
      setRconCustom('');
      setTimeout(() => setLastAction(null), 2500);
    } catch (error) {
      setAlertModal({ show: true, message: 'RCON failed', type: 'error' });
    }
  };

  const handleAdminChat = async () => {
    if (!adminChatMessage.trim()) return;
    try {
      await axios.post(`/api/matches/${id}/admin-chat`, { message: adminChatMessage.trim() });
      setLastAction('Message sent');
      setAdminChatMessage('');
      setTimeout(() => setLastAction(null), 2500);
    } catch (error) {
      setAlertModal({ show: true, message: 'Chat failed', type: 'error' });
    }
  };

  const updateChatPrefix = async () => {
    try {
      if (!chatPrefix.trim()) return;
      const res = await axios.patch(`/api/matches/${id}/chat-prefix`, { chat_prefix: chatPrefix });
      setMatch(prev => ({ ...prev, chat_prefix: res.data.chat_prefix }));
      setAlertModal({ show: true, message: `Chat prefix updated to ${res.data.chat_prefix}. Will take effect within 5 seconds.`, type: 'success' });
    } catch (err) {
      setAlertModal({ show: true, message: err.response?.data?.error || 'Failed to update prefix', type: 'error' });
    }
  };

  const handleStage = async (stage) => {
    setConfirmModal({
      show: true,
      message: `Force match to ${stage} stage?`,
      onConfirm: async () => {
        setConfirmModal({ show: false, message: '', onConfirm: null });
        try {
          await axios.post(`/api/matches/${id}/stage`, { stage });
          setAlertModal({ show: true, message: `State changed to ${stage}`, type: 'success' });
          fetchMatch();
        } catch (error) {
          setAlertModal({ show: true, message: 'Stage change failed', type: 'error' });
        }
      }
    });
  };

  const handlePause = async (action) => {
    try {
      await axios.post(`/api/matches/${id}/pause`, { action });
      const friendly = action === 'prod-pause' ? 'production paused' : action === 'pause' ? 'paused' : 'unpaused';
      setAlertModal({ show: true, message: `Match ${friendly}`, type: 'success' });
      fetchMatch();
    } catch (error) {
      setAlertModal({ show: true, message: error.response?.data?.error || 'Pause action failed', type: 'error' });
    }
  };

  // Toggle whether the in-game !tech command is available for a team (anti-abuse).
  // slot is 'team1' or 'team2'; the value flips the current match flag.
  const handleTechDisableToggle = async (slot) => {
    try {
      const current = slot === 'team1' ? match.tech_disabled_team1 : match.tech_disabled_team2;
      const next = current ? false : true;
      const res = await axios.put(`/api/matches/${id}/tech-disable`, { [slot]: next });
      setMatch(prev => prev ? { ...prev, tech_disabled_team1: res.data.tech_disabled_team1, tech_disabled_team2: res.data.tech_disabled_team2 } : prev);
    } catch (error) {
      setAlertModal({ show: true, message: error.response?.data?.error || 'Failed to update tech pause availability', type: 'error' });
    }
  };

  // Update tech pause budget (seconds per team)
  const handleTechBudgetChange = async (seconds) => {
    try {
      const res = await axios.put(`/api/matches/${id}/tech-budget`, { tech_budget_seconds: seconds });
      setMatch(prev => prev ? {
        ...prev,
        tech_budget_seconds: res.data.tech_budget_seconds,
        tech_used_team1: res.data.tech_used_team1,
        tech_used_team2: res.data.tech_used_team2
      } : prev);
    } catch (error) {
      setAlertModal({ show: true, message: error.response?.data?.error || 'Failed to update tech pause budget', type: 'error' });
    }
  };

  // Build an initial step-tracker state. All steps start 'pending'; WS messages
  // flip them to running/done/skipped/error in real time.
  const initPmProgress = (action, order, name) => ({
    action,
    order,
    name: name || '',
    steps: order.reduce((acc, k) => { acc[k] = 'pending'; return acc; }, {}),
    error: null,
    finished: false,
  });

  const handleAddToRoster = async () => {
    if (!rosterSteamId.trim()) return;
    setAddingToRoster(true);
    // The server stages the player's name, avatar, and side-backup files on the
    // game server FIRST, then admits the player to the match (writes match_players)
    // as the final step — so a sub can't connect before their files are ready.
    setPmProgress(initPmProgress('add', ['name', 'avatar', 'backup', 'admit'], rosterName.trim() || rosterSteamId.trim()));
    try {
      const res = await axios.post(`/api/matches/${id}/roster-player`, {
        steam_id: rosterSteamId.trim(),
        name: rosterName.trim() || undefined,
        team_side: rosterTeamSide
      });
      const { synced = [], sync_failed = [], has_backup_host, name } = res.data;

      let msg = t('matchDetail.rosterAdded', 'Player added for this match only (not permanent roster)');
      if (has_backup_host && synced.length) msg += ` - ${t('matchDetail.rosterSynced', 'synced {{items}} to server', { items: synced.join(', ') })}`;
      const type = sync_failed.length ? 'warning' : 'success';
      if (sync_failed.length) msg += ` - ${t('matchDetail.rosterSyncFailed', 'failed to sync {{items}} (player still admitted)', { items: sync_failed.join(', ') })}`;
      // Mark the tracker finished (WS should have filled steps; ensure a clean final state).
      setPmProgress(prev => prev ? { ...prev, name: name || prev.name, finished: true } : prev);
      setTimeout(() => setPmProgress(null), 2500);
      setAlertModal({ show: true, message: msg, type });
      setRosterSteamId('');
      setRosterName('');
      fetchMatch();
    } catch (error) {
      const errMsg = error.response?.data?.error || t('matchDetail.rosterAddFailed', 'Failed to add player');
      setPmProgress(prev => prev ? { ...prev, error: errMsg, finished: true } : prev);
      setTimeout(() => setPmProgress(null), 4000);
      setAlertModal({ show: true, message: errMsg, type: 'error' });
    } finally {
      setAddingToRoster(false);
    }
  };

  const handleRemovePlayer = async () => {
    if (!removeSteamId.trim()) return;
    setConfirmModal({
      show: true,
      message: t('matchDetail.removeConfirm', 'Remove this player from the match? They will be kicked from the server and treated as a non-roster player if they rejoin.'),
      onConfirm: async () => {
        setConfirmModal({ show: false, message: '', onConfirm: null });
        setRemovingPlayer(true);
        setPmProgress(initPmProgress('remove', ['kick', 'remove', 'resync'], removeSteamId.trim()));
        try {
          const res = await axios.delete(`/api/matches/${id}/roster-player`, {
            data: { steam_id: removeSteamId.trim() }
          });
          const { removed_from_match_players, removed_from_roster, kicked } = res.data;
          const parts = [];
          if (removed_from_match_players) parts.push(t('matchDetail.removePartMatch', 'match roster'));
          if (removed_from_roster) parts.push(t('matchDetail.removePartTeam', 'team roster'));
          let msg = t('matchDetail.removedFrom', 'Removed from {{parts}}', { parts: parts.join(' + ') });
          if (kicked) msg += ` ${t('matchDetail.removeKicked', 'and kicked from server')}`;
          else if (match.server_status === 'online') msg += ` ${t('matchDetail.removeNoKick', '(player was not on server - no kick issued)')}`;
          setPmProgress(prev => prev ? { ...prev, finished: true } : prev);
          setTimeout(() => setPmProgress(null), 2500);
          setAlertModal({ show: true, message: msg, type: 'success' });
          setRemoveSteamId('');
          fetchMatch();
          if (typeof fetchTempAdmins === 'function') fetchTempAdmins();
        } catch (error) {
          const errMsg = error.response?.data?.error || t('matchDetail.removeFailed', 'Failed to remove player');
          setPmProgress(prev => prev ? { ...prev, error: errMsg, finished: true } : prev);
          setTimeout(() => setPmProgress(null), 4000);
          setAlertModal({ show: true, message: errMsg, type: 'error' });
        } finally {
          setRemovingPlayer(false);
        }
      }
    });
  };

  const handleAddSpec = async () => {
    if (!specSteamId.trim()) return;
    setConfirmModal({
      show: true,
      message: t('matchDetail.specConfirm', 'Add this player to spec and grant temp admin powers for this match? They will be able to use !fp, !fup, !restore, !asay, !start until the match ends.'),
      onConfirm: async () => {
        setConfirmModal({ show: false, message: '', onConfirm: null });
        setAddingSpec(true);
        setPmProgress(initPmProgress('spec', ['spec', 'tempadmin'], specSteamId.trim()));
        try {
          const res = await axios.post(`/api/matches/${id}/add-spec-player`, {
            steam_id: specSteamId.trim()
          });
          setPmProgress(prev => prev ? { ...prev, name: res.data?.name || prev.name, finished: true } : prev);
          setTimeout(() => setPmProgress(null), 2500);
          setAlertModal({
            show: true,
            message: t('matchDetail.specAdded', 'Added to spec + temp admin enabled. They can now issue admin commands in chat.'),
            type: 'success'
          });
          setSpecSteamId('');
          fetchMatch();
          if (typeof fetchTempAdmins === 'function') fetchTempAdmins();
        } catch (error) {
          const errMsg = error.response?.data?.error || t('matchDetail.specFailed', 'Failed to add spec player');
          setPmProgress(prev => prev ? { ...prev, error: errMsg, finished: true } : prev);
          setTimeout(() => setPmProgress(null), 4000);
          setAlertModal({ show: true, message: errMsg, type: 'error' });
        } finally {
          setAddingSpec(false);
        }
      }
    });
  };

  const handleRevokeTempAdmin = async (steam64) => {
    setConfirmModal({
      show: true,
      message: 'Revoke temp admin powers? The player remains a spectator but will no longer be able to use admin commands.',
      onConfirm: async () => {
        setConfirmModal({ show: false, message: '', onConfirm: null });
        try {
          await axios.post(`/api/matches/${id}/temp-admin/revoke`, { steam_id: steam64 });
          setAlertModal({ show: true, message: 'Temp admin revoked.', type: 'success' });
          if (typeof fetchTempAdmins === 'function') fetchTempAdmins();
        } catch (error) {
          const errMsg = error.response?.data?.error || 'Failed to revoke temp admin';
          setAlertModal({ show: true, message: errMsg, type: 'error' });
        }
      }
    });
  };

  const fetchTempAdmins = async () => {
    setTempAdminsLoading(true);
    try {
      const res = await axios.get(`/api/matches/${id}/temp-admins`);
      setTempAdmins(res.data.temp_admins || []);
    } catch (_) {
      setTempAdmins([]);
    } finally {
      setTempAdminsLoading(false);
    }
  };

  const handleStartMatch = async () => {
    setConfirmModal({
      show: true,
      message: 'Force Start Match? This ends warmup.',
      onConfirm: async () => {
        setConfirmModal({ show: false, message: '', onConfirm: null });
        try {
          await axios.post(`/api/matches/${id}/force-ready`, { team_side: 'all' });
          setAlertModal({ show: true, message: 'Match started', type: 'success' });
          fetchMatch();
        } catch (error) {
          setAlertModal({ show: true, message: 'Start failed', type: 'error' });
        }
      }
    });
  };

  const handleForceReady = async (side) => {
    try {
      await axios.post(`/api/matches/${id}/force-ready`, { team_side: side });
      setAlertModal({ show: true, message: `Force ready ${side} sent`, type: 'success' });
    } catch (error) { setAlertModal({ show: true, message: 'Failed', type: 'error' }); }
  };

  const handleForceTeams = async (mapIndex) => {
    setConfirmModal({
      show: true,
      message: `Force Teams? This reloads the backup for this map and keeps the match in warmup.`,
      onConfirm: async () => {
        setConfirmModal({ show: false, message: '', onConfirm: null });
        try {
          await axios.post(`/api/matches/${id}/force-teams`, { mapIndex });
          setAlertModal({ show: true, message: 'Teams forced - warmup continues', type: 'success' });
          fetchMatch();
        } catch (e) { setAlertModal({ show: true, message: 'Failed', type: 'error' }); }
      }
    });
  };

  const handleSwapTeams = async (mapIndex) => {
    setConfirmModal({
      show: true,
      message: `Swap Team Names? This will manually swap the team names on the server and update the overlay.`,
      onConfirm: async () => {
        setConfirmModal({ show: false, message: '', onConfirm: null });
        try {
          await axios.post(`/api/matches/${id}/maps/${mapIndex}/swap-teams`);
          setAlertModal({ show: true, message: 'Team Names Swapped successfully', type: 'success' });
          fetchMatch();
        } catch (e) {
          const err = e.response?.data?.error || 'Failed to swap teams';
          setAlertModal({ show: true, message: err, type: 'error' });
        }
      }
    });
  };

  const handleFinishMatch = async () => {
    setConfirmModal({
      show: true,
      message: 'Mark match as FINISHED?',
      onConfirm: async () => {
        setConfirmModal({ show: false, message: '', onConfirm: null });
        try {
          await axios.put(`/api/matches/${id}/status`, { status: 'finished' });
          setAlertModal({ show: true, message: 'Match finished', type: 'success' });
          fetchMatch();
        } catch (e) { setAlertModal({ show: true, message: 'Failed', type: 'error' }); }
      }
    });
  };

  const openForfeitModal = () => {
    const mapList = JSON.parse(match.map_list || '[]');
    const existingStats = match.map_stats ? JSON.parse(match.map_stats) : [];
    const scores = mapList.map((_, i) => {
      const existing = existingStats.find(s => s.map_index === i);
      return {
        score_team1: existing ? existing.score_team1 : 0,
        score_team2: existing ? existing.score_team2 : 0
      };
    });
    setForfeitMapScores(scores);
    setForfeitMatchScore({
      team1: match.map_wins_team1 || 0,
      team2: match.map_wins_team2 || 0
    });
    setForfeitWinner('team1');
    setForfeitSubmitting(false);
    setShowForfeitModal(true);
  };

  const handleConfirmForfeit = async () => {
    setForfeitSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/matches/${id}/forfeit`, {
        winner_team_id: forfeitWinner === 'team1' ? match.team1_id : match.team2_id,
        map_scores: forfeitMapScores,
        match_score: forfeitMatchScore
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowForfeitModal(false);
      setAlertModal({ show: true, title: 'Success', message: 'Match forfeited successfully', type: 'success' });
      fetchMatch();
    } catch (err) {
      setAlertModal({ show: true, title: 'Error', message: err.response?.data?.error || 'Failed to forfeit match', type: 'error' });
    } finally {
      setForfeitSubmitting(false);
    }
  };

  // ── Per-Map Forfeit helpers ──
  const getMapList = () => {
    try { return JSON.parse(match.map_list || '[]'); } catch { return []; }
  };

  const getMapStats = () => {
    try { return JSON.parse(match.map_stats || '[]'); } catch { return []; }
  };

  const getMapForfeitEntry = (idx) => {
    return getMapStats().find(s => s.map_index === idx && s.forfeit);
  };

  const openMapForfeitModal = (mapIdx) => {
    const existing = getMapStats().find(s => s.map_index === mapIdx);
    setMapForfeitIndex(mapIdx);
    setMapForfeitScore1(existing ? existing.score_team1 : 0);
    setMapForfeitScore2(existing ? existing.score_team2 : 0);
    setMapForfeitSubmitting(false);
    setShowMapForfeitModal(true);
  };

  const openRemoveForfeitModal = (mapIdx) => {
    setRemoveForfeitIndex(mapIdx);
    setRemoveForfeitSubmitting(false);
    setShowRemoveForfeitModal(true);
  };

  const handleMapForfeit = async (advance) => {
    setMapForfeitSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/matches/${id}/forfeit-map`, {
        map_index: mapForfeitIndex,
        score_team1: mapForfeitScore1,
        score_team2: mapForfeitScore2,
        advance
      }, { headers: { Authorization: `Bearer ${token}` } });
      setShowMapForfeitModal(false);
      setAlertModal({ show: true, title: 'Success', message: `Map ${mapForfeitIndex + 1} forfeited`, type: 'success' });
      fetchMatch();
    } catch (err) {
      setAlertModal({ show: true, title: 'Error', message: err.response?.data?.error || 'Failed to forfeit map', type: 'error' });
    } finally {
      setMapForfeitSubmitting(false);
    }
  };

  const handleRemoveForfeit = async (goToMap) => {
    setRemoveForfeitSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/matches/${id}/remove-forfeit`, {
        map_index: removeForfeitIndex,
        go_to_map: goToMap
      }, { headers: { Authorization: `Bearer ${token}` } });
      setShowRemoveForfeitModal(false);
      setAlertModal({ show: true, title: 'Success', message: `Forfeit removed for Map ${removeForfeitIndex + 1}`, type: 'success' });
      fetchMatch();
    } catch (err) {
      setAlertModal({ show: true, title: 'Error', message: err.response?.data?.error || 'Failed to remove forfeit', type: 'error' });
    } finally {
      setRemoveForfeitSubmitting(false);
    }
  };

  const handleToggleKillfeedVisibility = async (show) => {
    try {
      await axios.put(`/api/matches/${id}/killfeed-visibility`, { show });
      setMatch(prev => ({ ...prev, show_killfeed: show ? 1 : 0 }));
    } catch (error) {
      setAlertModal({ show: true, message: 'Failed to update visibility', type: 'error' });
    }
  };

  const handleTogglePlayerVisibility = async (steamId, mapIndex, isHidden) => {
    try {
      await axios.post(`/api/matches/${id}/players/${steamId}/visibility`, { mapIndex, isHidden });
      // WS will trigger fetchMatch() via player_visibility_changed event if it exists, but we can optimistically fetch
      fetchMatch();
    } catch (error) {
      setAlertModal({ show: true, message: 'Failed to hide/show player', type: 'error' });
    }
  };

  const handleUpdateStreamDelay = async () => {
    try {
      const delayValue = parseInt(streamDelay, 10);
      if (isNaN(delayValue) || delayValue < 0) {
        setAlertModal({ show: true, message: 'Please enter a valid number of seconds', type: 'error' });
        return;
      }
      await axios.put(`/api/matches/${id}/stream-delay`, { delay: delayValue });
      setAlertModal({ show: true, message: `Stream delay updated to ${delayValue} seconds`, type: 'success' });
      setMatch(prev => ({ ...prev, stream_delay: delayValue }));
    } catch (error) {
      setAlertModal({ show: true, message: 'Failed to update stream delay', type: 'error' });
    }
  };

  const handleCancelMatch = async () => {
    setConfirmModal({
      show: true,
      message: 'CANCEL MATCH? This cannot be undone.',
      onConfirm: async () => {
        setConfirmModal({ show: false, message: '', onConfirm: null });
        try {
          await axios.post(`/api/matches/${id}/cancel`);
          navigate('/admin/matches');
        } catch (e) { setAlertModal({ show: true, message: 'Failed', type: 'error' }); }
      }
    });
  };

  const handleResetMap = async (mapIdx) => {
    const displayIdx = (mapIdx != null ? mapIdx : (match.current_map_index || 0)) + 1;
    const bo = parseInt(match.best_of, 10) || 1;
    const mapLabel = bo > 1 ? `Map ${displayIdx}` : 'the current map';
    setConfirmModal({
      show: true,
      message: `RESET ${mapLabel.toUpperCase()}? This will wipe ALL scores, rounds, kills, stats, logs, and chat for this map and return the match to warmup. This cannot be undone.`,
      onConfirm: async () => {
        setConfirmModal({ show: false, message: '', onConfirm: null });
        try {
          const res = await axios.post(`/api/matches/${id}/reset-map`, { map_index: mapIdx != null ? mapIdx : (match.current_map_index || 0) });
          setAlertModal({ show: true, message: res.data.message || 'Map reset successfully', type: 'success' });
        } catch (e) {
          setAlertModal({ show: true, message: e.response?.data?.error || 'Failed to reset map', type: 'error' });
        }
      }
    });
  };

  // ── Server Migration handlers ──
  const openMigrationModal = async () => {
    try {
      // Fetch all available servers (including those without FTP/SFTP)
      const serversRes = await axios.get('/api/servers/available');
      const servers = (serversRes.data || []).filter(s => s.id !== match.server_id);
      setAvailableServers(servers);
      setMigrationTargetServer(null);
      setIsManualMigration(false);
      setManualFilesConfirmed(false);
      setMigrationFiles(null);
      setSourceHasBackup(true);

      // Fetch migration file info (includes source server backup status)
      try {
        const filesRes = await axios.get(`/api/matches/${id}/migration-files`);
        setMigrationFiles(filesRes.data);
        setSourceHasBackup(filesRes.data.sourceHasBackup);
      } catch (_) {
        // Non-fatal — modal can still open without file info
      }

      setShowMigrationModal(true);
    } catch (err) {
      setAlertModal({ show: true, message: 'Failed to fetch available servers', type: 'error' });
    }
  };

  // When target server selection changes, determine if manual migration is needed
  const handleMigrationTargetChange = (serverId) => {
    const srv = availableServers.find(s => String(s.id) === serverId);
    if (srv) {
      setMigrationTargetServer({ value: srv.id, label: `${srv.name} (${srv.ip}:${srv.port})` });
      const targetHasBackup = srv.has_backup;
      const needsManual = !sourceHasBackup || !targetHasBackup;
      setIsManualMigration(needsManual);
      setManualFilesConfirmed(false);
    } else {
      setMigrationTargetServer(null);
      setIsManualMigration(false);
      setManualFilesConfirmed(false);
    }
  };

  const handleMigrateMatch = async () => {
    if (!migrationTargetServer) return;
    // For manual migration, user must confirm files are copied
    if (isManualMigration && !manualFilesConfirmed) return;
    setShowMigrationModal(false);
    setMigrating(true);
    setMigrationProgress({ step: 0, total_steps: 10, message: isManualMigration ? 'Starting manual migration...' : 'Starting migration...' });
    try {
      await axios.post(`/api/matches/${id}/migrate`, {
        target_server_id: migrationTargetServer.value,
        manual: isManualMigration || undefined
      });
    } catch (err) {
      setMigrating(false);
      setMigrationProgress(null);
      const msg = err.response?.data?.error || 'Failed to start migration';
      setAlertModal({ show: true, message: msg, type: 'error' });
    }
  };

  // ── Rehost handlers ──
  const openRehostModal = async () => {
    try {
      const res = await axios.get(`/api/matches/${id}/rehost-scores`);
      setRehostScores(res.data.scores || []);
      setRehostMatchInfo({ map: res.data.map, team1_name: res.data.team1_name, team2_name: res.data.team2_name });
      setRehostSelectedRound(null);
      setShowRehostModal(true);
    } catch (err) {
      setAlertModal({ show: true, message: err.response?.data?.error || 'Failed to fetch rehost scores', type: 'error' });
    }
  };

  const handleRehost = () => {
    if (rehostSelectedRound == null) return;
    const selectedScore = rehostScores.find(s => s.round === rehostSelectedRound);
    const scoreDisplay = selectedScore ? selectedScore.display : `${rehostSelectedRound}`;
    const mapName = rehostMatchInfo?.map || match.map || 'unknown';

    setConfirmModal({
      show: true,
      message: `Are you sure you want to REHOST this match?\n\nMap: ${mapName}\nScore: ${rehostMatchInfo?.team1_name || 'Team 1'} ${scoreDisplay} ${rehostMatchInfo?.team2_name || 'Team 2'}\n\nThe server will reload the map and restore to this score when teams ready up.`,
      onConfirm: async () => {
        setConfirmModal({ show: false, message: '', onConfirm: null });
        setShowRehostModal(false);
        setRehosting(true);
        setRehostProgress({ step: 0, total_steps: 10, message: 'Starting rehost...' });
        try {
          await axios.post(`/api/matches/${id}/rehost`, { restore_round: rehostSelectedRound });
        } catch (err) {
          setRehosting(false);
          setRehostProgress(null);
          const msg = err.response?.data?.error || 'Failed to start rehost';
          setAlertModal({ show: true, message: msg, type: 'error' });
        }
      }
    });
  };

  if (loading) return <MatchDetailSkeleton />;
  if (!match) return <div className="text-secondary container" style={{ paddingTop: '2rem' }}>Match not found</div>;

  const { team1Side, team2Side, startingSide1, startingSide2 } = getTeamSides();
  const CT_COLOR = '#22d3ee';
  const T_COLOR = '#fb923c';
  const team1Color = team1Side === 'CT' ? CT_COLOR : T_COLOR;
  const team2Color = team2Side === 'CT' ? CT_COLOR : T_COLOR;
  const getBadgeClass = (s) => {
    const status = s || 'pending';
    if (status === 'live') return 'badge-success';
    if (status === 'finished') return 'badge-secondary';
    if (status === 'cancelled') return 'badge-danger';
    if (status === 'paused') return 'badge-warning';
    return 'badge-info';
  };

  // Determine which stats and rounds to show based on selectedMapIndex
  // We want to ensure the player list is consistent across all tabs (showing the full roster),
  // so we always use the main match.team1_players/team2_players list as the base.
  // Then we merge in the specific map stats if a map is selected.
  const getMergedStats = (basePlayers, mapIndex, teamKey) => {
    if (!basePlayers) return { visible: [], hidden: [] };

    const mapStatsObj = (match.stats_by_map && match.stats_by_map[mapIndex]) || {};
    const specificPlayers = mapStatsObj[teamKey] || [];

    // Create lookup for specific stats
    const statsLookup = new Map();
    specificPlayers.forEach(p => statsLookup.set(p.steam_id, p));

    // Map base players to their specific stats (or 0s)
    const merged = basePlayers.map(p => {
      const specific = statsLookup.get(p.steam_id);
      if (specific) {
        return { ...p, ...specific }; // Use specific stats
      }
      // Fallback to 0s but keep name/id
      return {
        ...p,
        kills: 0, deaths: 0, assists: 0, mvps: 0, score: 0, damage: 0,
        headshots: 0, entry_kills: 0, trade_kills: 0,
        multikill_2k: 0, multikill_3k: 0, multikill_4k: 0, multikill_5k: 0,
        kast_rounds: 0
      };
    }).sort((a, b) => (b.score || 0) - (a.score || 0)); // Re-sort by score for this map

    // On a FINISHED/cancelled match viewing a SPECIFIC map, a roster player with
    // zero activity on that map (no kills, deaths, assists, damage, or score) never
    // actually played it — so hide them. This is the safety net for players whose
    // server-side hidden_maps was never set (missed disconnect / status_json hiccup
    // on older builds), which is why "ghost" 0/0/0 players linger on the scoreboard.
    // Scoped to finished/cancelled + a concrete map index so it never hides someone
    // mid-warmup or in the aggregated "all maps" view (where a bad map could be 0s).
    const matchDone = match.status === 'finished' || match.status === 'cancelled';
    const specificMap = typeof selectedMapIndex === 'number';
    const didNotPlayMap = (p) => (
      (p.kills || 0) === 0 && (p.deaths || 0) === 0 && (p.assists || 0) === 0 &&
      (p.damage || 0) === 0 && (p.score || 0) === 0
    );

    // Separate based on visibility
    const visible = [];
    const hidden = [];
    merged.forEach(p => {
      const hiddenMaps = p.hidden_maps || [];
      if (hiddenMaps.includes(selectedMapIndex) || hiddenMaps.includes('all')) {
        // Explicit server-side hide always wins.
        hidden.push(p);
      } else if (matchDone && specificMap && didNotPlayMap(p) && !forceShownSteamIds.has(p.steam_id)) {
        // Auto ghost-player hide, unless the admin explicitly restored this player.
        hidden.push(p);
      } else {
        visible.push(p);
      }
    });

    return { visible, hidden };
  };

  const currentStats = {
    team1: getMergedStats(match.team1_players, selectedMapIndex, 'team1_players'),
    team2: getMergedStats(match.team2_players, selectedMapIndex, 'team2_players')
  };

  // Build O(1) lookup of connected steam_ids from live player status
  const connectedSteamIds = (() => {
    const set = new Set();
    if (playerStatus?.players) {
      playerStatus.players.forEach(p => set.add(p.steam_id));
    }
    return set;
  })();

  // Only show connection dots during active match stages
  const showConnectionDots = ['warmup', 'knife', 'live'].includes(match?.match_stage);

  // Calculate connected player counts per team
  const team1ConnectedCount = showConnectionDots && currentStats.team1.visible
    ? currentStats.team1.visible.filter(p => connectedSteamIds.has(p.steam_id)).length
    : 0;
  const team1TotalCount = currentStats.team1.visible?.length || 0;
  const team2ConnectedCount = showConnectionDots && currentStats.team2.visible
    ? currentStats.team2.visible.filter(p => connectedSteamIds.has(p.steam_id)).length
    : 0;
  const team2TotalCount = currentStats.team2.visible?.length || 0;

  const renderFaceitBadge = (faceit) => {
    if (!faceit) return null;
    const hasLevel = faceit.skill_level !== null && faceit.skill_level !== undefined;
    const hasElo = faceit.faceit_elo !== null && faceit.faceit_elo !== undefined;
    if (!hasLevel && !hasElo) return null;

    const level = hasLevel ? faceit.skill_level : '?';
    const levelNumber = Number(level);
    const iconLevel = Number.isInteger(levelNumber) && levelNumber >= 1 && levelNumber <= 10 ? levelNumber : null;
    const elo = hasElo ? Math.round(Number(faceit.faceit_elo)) : null;
    const title = `FACEIT${faceit.nickname ? ` ${faceit.nickname}` : ''}: Level ${level}${elo !== null ? `, ${elo} ELO` : ''}`;

    return (
      <span
        title={title}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          height: 20,
          padding: '0 5px 0 3px',
          borderRadius: 'var(--radius-sm, 4px)',
          background: 'rgba(255,255,255,0.035)',
          border: '1px solid rgba(255,255,255,0.08)',
          color: 'var(--accent-amber, #f59e0b)',
          fontSize: '0.62rem',
          fontWeight: 700,
          fontFamily: 'var(--font-mono)',
          lineHeight: 1,
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {iconLevel ? (
          <img
            src={`/faceit-levels/skill_level_${iconLevel}.png`}
            alt={`FACEIT Level ${iconLevel}`}
            style={{ width: 16, height: 16, display: 'block', flexShrink: 0 }}
          />
        ) : (
          <span>F{level}</span>
        )}
        {elo !== null && <span style={{ color: 'var(--text-secondary, #8b8b9e)' }}>{elo}</span>}
      </span>
    );
  };

  const filteredRounds = selectedMapIndex !== null
    ? roundHistory.filter(r => r.map_index === selectedMapIndex)
    : roundHistory;

  // For "All Maps" view, show series score (map wins)
  // For specific maps, show round score for that map
  let displayScore1, displayScore2;
  let scoreLabel;

  // Parse map stats
  let mapStats = [];
  try { mapStats = match.map_stats ? (typeof match.map_stats === 'string' ? JSON.parse(match.map_stats) : match.map_stats) : []; } catch (e) { }

  if (selectedMapIndex === 'all' && match.best_of > 1) {
    // Series score = map wins (only for BO3+)
    displayScore1 = match.map_wins_team1 || 0;
    displayScore2 = match.map_wins_team2 || 0;
    scoreLabel = `Best of ${match.best_of} (Match Score)`;
  } else if (selectedMapIndex === 'all' && match.best_of == 1) {
    // BO1: Show live round score
    displayScore1 = match.score_team1 || 0;
    displayScore2 = match.score_team2 || 0;
    scoreLabel = match.map ? match.map.replace(/^(de_|cs_)/, '') : 'Live Score';
  } else {
    // Check if we have saved stats for this map
    if (mapStats[selectedMapIndex]) {
      displayScore1 = mapStats[selectedMapIndex].score_team1;
      displayScore2 = mapStats[selectedMapIndex].score_team2;
    } else {
      // For the current map being played, use the live score
      // For other maps, we'd need historical scores which aren't stored separately purely by rounds
      const currentMapIndex = typeof match.current_map_index === 'number' ? match.current_map_index : 0;

      if (selectedMapIndex === currentMapIndex) {
        // Current map - use live scores
        displayScore1 = match.score_team1 || 0;
        displayScore2 = match.score_team2 || 0;
      } else if (filteredRounds.length === 0) {
        // No round data available for this map
        // Check if this map was forfeited — use forfeit scores
        const forfeitEntry = getMapForfeitEntry(selectedMapIndex);
        if (forfeitEntry) {
          displayScore1 = forfeitEntry.score_team1 ?? 0;
          displayScore2 = forfeitEntry.score_team2 ?? 0;
        } else if (mapStats[selectedMapIndex]) {
          // Use map_stats entry scores (forfeit or otherwise)
          displayScore1 = mapStats[selectedMapIndex].score_team1 ?? 0;
          displayScore2 = mapStats[selectedMapIndex].score_team2 ?? 0;
        } else {
          displayScore1 = 0;
          displayScore2 = 0;
        }
      } else {
        // Historical or future map - count rounds won by each team
        // We need to account for side switches at halftime
        const MR = (match?.mp_maxrounds || 24) / 2; // rounds per half (dynamic from match settings)
        displayScore1 = 0;
        displayScore2 = 0;

        filteredRounds.forEach((r) => {
          const roundNum = r.round_number || 0;
          const isFirstHalf = roundNum <= MR;

          // Use startingSide1/2 from getTeamSides() — these are always the first-half
          // (pre-flip) sides, so this loop stays correct regardless of current half.
          const currentSide1 = isFirstHalf ? startingSide1 : (startingSide1 === 'CT' ? 'TERRORIST' : 'CT');
          const currentSide2 = isFirstHalf ? startingSide2 : (startingSide2 === 'CT' ? 'TERRORIST' : 'CT');

          // Normalize winner_side
          const winnerSide = r.winner_side === 'T' ? 'TERRORIST' : r.winner_side;

          if (winnerSide === currentSide1) {
            displayScore1++;
          } else if (winnerSide === currentSide2) {
            displayScore2++;
          }
        });
      }
    }

    scoreLabel = `Map ${selectedMapIndex + 1} Score`;
  }

  // Series score (map wins) — used per-team under each team name.
  // Live-compute from map_stats for up-to-date values; fall back to persisted map_wins_team*.
  // Only meaningful when best_of > 1 (BO3/BO5).
  const _bo = Number(match.best_of) || 1;
  let _series1 = match.map_wins_team1 || 0;
  let _series2 = match.map_wins_team2 || 0;
  if (Array.isArray(mapStats) && mapStats.length > 0) {
    let s1 = 0, s2 = 0;
    for (const m of mapStats) {
      if (!m) continue;
      const s1m = Number(m.score_team1) || 0;
      const s2m = Number(m.score_team2) || 0;
      if (s1m === 0 && s2m === 0 && !m.forfeit) continue;
      if (s1m > s2m) s1++;
      else if (s2m > s1m) s2++;
    }
    _series1 = s1;
    _series2 = s2;
  }
  const _showSeries = _bo > 1;

  // Calculate Header Map Name and dynamic background
  let headerMapName = match.map ? match.map.replace(/^(de_|cs_)/, '') : 'Unknown Map';
  let activeMapKey = match.map || 'Unknown';
  if (selectedMapIndex !== 'all') {
    try {
      const maps = match.map_list ? (typeof match.map_list === 'string' ? JSON.parse(match.map_list) : match.map_list) : [];
      if (maps[selectedMapIndex]) {
        activeMapKey = maps[selectedMapIndex];
        headerMapName = maps[selectedMapIndex].replace(/^(de_|cs_)/, '');
      }
    } catch (e) { }
  }

  // Lookup the image URL from our `/api/maps` dictionary
  const activeMapRecord = availableMaps.find(m => m.name.toLowerCase() === activeMapKey.toLowerCase());
  const headerMapImageUrl = activeMapRecord ? activeMapRecord.image_url : (match.map_image_url || null);

  // Build the connect string. If the server has a password set, append ";password <pw>"
  // so admins can paste the whole thing into CS2 console. sv_password response: null = no password.
  const buildConnectString = (ip, port, password) => {
    if (!ip) return '';
    const base = `connect ${ip}:${port || '27015'}`;
    return password ? `${base};password ${password}` : base;
  };

  // The IP to display to admins on this page. Prefers server_public_ip (LAN IP) when set;
  // falls back to server_ip (main IP). Both come from the API via the s JOIN in
  // GET /api/matches/:id. Main IP is still used for RCON/SFTP/FTP — see CLAUDE.md §13.
  const displayIp = match?.server_public_ip || match?.server_ip || '';
  const displayConnectString = buildConnectString(displayIp, match?.server_port, match?.server_password);

  const copyServerAddress = () => {
    if (!match?.ip_string) return;
    const text = displayConnectString;
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopyFeedback(text);
      setTimeout(() => setCopyFeedback(null), 2000);
    }).catch(() => { });
  };

  const renderRestoreCheckerContent = () => {
    if (!isAdminView || !(isOwner || user?.role === 'super_admin')) return null;

    if (damageEvents.length === 0 && match.status !== 'live') {
      return (
        <div style={{ color: 'var(--text-muted, #4a4a5a)', textAlign: 'center', padding: '2rem', fontSize: '0.78rem', fontFamily: 'var(--font-sans)' }}>
          {t('restoreChecker.emptyState', 'No rounds recorded yet. Each live round shows its damage and any restore requests (.restore, nl, …) from player chat.')}
        </div>
      );
    }

    const byMap = {};
    for (const e of damageEvents) {
      const key = e.map_index ?? 0;
      if (!byMap[key]) byMap[key] = {};
      byMap[key][e.round_number ?? 1] = e;
    }

    let mapLabels = [];
    try {
      mapLabels = match.map_list
        ? (typeof match.map_list === 'string' ? JSON.parse(match.map_list) : match.map_list)
        : [];
    } catch (_) { }
    if (!Array.isArray(mapLabels) || mapLabels.length === 0) {
      mapLabels = [match.map].filter(Boolean);
    }

    const liveMapIdx = (typeof match.current_map_index === 'number')
      ? match.current_map_index
      : parseInt(match.current_map_index, 10) || 0;
    // Current map always gets a section (even before its first round has data),
    // listed first; previous maps go behind the "Previous maps" toggle, newest first.
    if (!byMap[liveMapIdx]) byMap[liveMapIdx] = {};
    const allMapEntries = Object.entries(byMap);
    const currentMapEntries = allMapEntries.filter(([idx]) => Number(idx) === Number(liveMapIdx));
    const prevMapEntries = allMapEntries
      .filter(([idx]) => Number(idx) !== Number(liveMapIdx))
      .sort((a, b) => Number(b[0]) - Number(a[0]));
    const multiMap = (Number(match.best_of) || 1) > 1 || prevMapEntries.length > 0;

    const toggleMap = (idx) => {
      setExpandedRestoreMaps(prev => {
        const next = new Set(prev);
        if (next.has(idx)) next.delete(idx);
        else next.add(idx);
        return next;
      });
    };

    return (
      <div style={{ fontFamily: 'var(--font-sans)' }}>
        <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--border-subtle, rgba(255,255,255,0.06))', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary, #e8e8ed)' }}>{t('match.restoreChecker', 'Restore Checker')}</div>
            <div style={{ fontSize: '0.65rem', color: 'var(--text-muted, #4a4a5a)', marginTop: '2px' }}>{t('restoreChecker.subtitle', 'Shows if damage was dealt before a player requested a restore')}</div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.6rem', fontWeight: 600 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} /> {t('restoreChecker.legendSafe', 'Safe')}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} /> {t('restoreChecker.legendDamage', 'Damage')}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--text-muted, #4a4a5a)', display: 'inline-block' }} /> {t('restoreChecker.legendNone', 'No request')}</span>
          </div>
        </div>

        {(() => {
        const renderMapSection = ([mapIdx, roundData]) => {
          const mapLabel =(mapLabels[Number(mapIdx)] || `Map ${Number(mapIdx) + 1}`).replace(/^(de_|cs_)/, '');
          const roundEntries = Object.entries(roundData).sort((a, b) => Number(b[0]) - Number(a[0]));
          // Counts are per ROUND WITH A REQUEST: 'safe' = request before any damage,
          // 'damage' = damage dealt before the request. Rounds nobody asked about
          // don't count either way (every real round has damage).
          let damageCount = 0, safeCount = 0, requestRounds = 0;
          for (const [, d] of roundEntries) {
            if (d.verdict === 'damage') damageCount += 1;
            else if (d.verdict === 'safe') safeCount += 1;
            if ((d.chat_flags || []).length > 0) requestRounds += 1;
          }
          const isLive = Number(mapIdx) === Number(liveMapIdx) && match.status === 'live';
          const expanded = !multiMap || expandedRestoreMaps.has(Number(mapIdx));

          return (
            <div key={mapIdx}>
              {multiMap && (
                <div
                  onClick={() => toggleMap(Number(mapIdx))}
                  style={{
                    padding: '0.45rem 1rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem',
                    fontSize: '0.82rem', fontWeight: 700, textTransform: 'capitalize',
                    color: isLive ? 'var(--accent-blue, #3b82f6)' : 'var(--text-muted, #4a4a5a)',
                    background: isLive ? 'rgba(59,130,246,0.08)' : 'rgba(59,130,246,0.04)',
                    borderBottom: '1px solid rgba(59,130,246,0.1)',
                    cursor: 'pointer', userSelect: 'none',
                  }}
                  role="button"
                  aria-expanded={expanded}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.7rem', width: 12, display: 'inline-block', transition: 'transform 0.15s', transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>{'>'}</span>
                    <span>{mapLabel}</span>
                    {isLive && <span style={{ fontSize: '0.55rem', padding: '1px 6px', borderRadius: 4, background: 'rgba(59,130,246,0.18)', color: 'var(--accent-blue, #3b82f6)', letterSpacing: '0.08em' }}>LIVE</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>
                    {requestRounds === 0 ? (
                      <span style={{ color: 'var(--text-muted, #4a4a5a)' }}>{t('restoreChecker.noRequests', 'No restore requests')}</span>
                    ) : (
                      <>
                        <span style={{ color: 'var(--text-muted, #4a4a5a)' }}>{t('restoreChecker.requestRounds', 'Rounds with requests: {{count}}', { count: requestRounds })}</span>
                        {safeCount > 0 && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#22c55e' }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
                            {t('restoreChecker.safeCount', 'Safe: {{count}}', { count: safeCount })}
                          </span>
                        )}
                        {damageCount > 0 && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#ef4444' }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} />
                            {t('restoreChecker.damageCount', 'Damage: {{count}}', { count: damageCount })}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {expanded && roundEntries.map(([roundNum, data]) => {
                const sortedDamage = [...(data.damage_events || [])].sort((a, b) => Number(a.dmg_second || 0) - Number(b.dmg_second || 0));
                const hasDamage = data.has_damage;
                const hasFlags = (data.chat_flags || []).length > 0;
                // Server verdict: compares the first request against damage timestamps.
                const safeToRestore = data.verdict === 'safe';
                const statusColor = data.verdict === 'damage' ? '#ef4444'
                  : data.verdict === 'safe' ? '#22c55e'
                  : 'var(--text-muted, #4a4a5a)';
                const flags = (data.chat_flags || []).map(f => ({ name: f.player_name, keyword: f.keyword, message: f.message, ts: f.ts }));

                return (
                  <RestoreRoundRow
                    key={`${mapIdx}_${roundNum}`}
                    roundNum={roundNum}
                    hasDamage={hasDamage}
                    sortedDamage={sortedDamage}
                    hasFlags={hasFlags}
                    safeToRestore={safeToRestore}
                    statusColor={statusColor}
                    flags={flags}
                    requestAt={data.request_at}
                    damageBeforeRequest={data.damage_before_request || 0}
                    isLive={data.is_live}
                  />
                );
              })}
              {expanded && roundEntries.length === 0 && (
                <div style={{ padding: '0.75rem 1rem', fontSize: '0.7rem', color: 'var(--text-muted, #4a4a5a)', textAlign: 'center' }}>
                  {t('restoreChecker.noRoundsYet', 'No rounds played on this map yet')}
                </div>
              )}
            </div>
          );
        };

        return (
          <>
            {currentMapEntries.map(renderMapSection)}
            {prevMapEntries.length > 0 && (
              <>
                <div
                  onClick={() => setShowPrevRestoreMaps(v => !v)}
                  role="button"
                  aria-expanded={showPrevRestoreMaps}
                  style={{
                    padding: '0.45rem 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem',
                    fontSize: '0.65rem', fontWeight: 600, color: 'var(--text-muted, #4a4a5a)',
                    borderTop: '1px solid var(--border-subtle, rgba(255,255,255,0.06))',
                    borderBottom: showPrevRestoreMaps ? '1px solid var(--border-subtle, rgba(255,255,255,0.06))' : 'none',
                    cursor: 'pointer', userSelect: 'none',
                  }}
                >
                  <span style={{ fontSize: '0.7rem', width: 12, display: 'inline-block', transition: 'transform 0.15s', transform: showPrevRestoreMaps ? 'rotate(90deg)' : 'rotate(0deg)' }}>{'>'}</span>
                  {showPrevRestoreMaps
                    ? t('restoreChecker.hidePrevMaps', 'Hide previous maps ({{count}})', { count: prevMapEntries.length })
                    : t('restoreChecker.showPrevMaps', 'Show previous maps ({{count}})', { count: prevMapEntries.length })}
                </div>
                {showPrevRestoreMaps && prevMapEntries.map(renderMapSection)}
              </>
            )}
          </>
        );
        })()}
      </div>
    );
  };

  const content = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', paddingBottom: '2rem' }}>

      {/* ── Scoreboard Header Card ── */}
      <div style={{
        background: 'var(--bg-card, #111118)',
        border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))',
        borderRadius: 'var(--radius-lg, 12px)',
        padding: '0.85rem 1.5rem',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Map background gradient overlay */}
        {headerMapImageUrl && (
          <div ref={el => {
            if (!el) return;
            const img = new Image();
            img.src = headerMapImageUrl;
            img.onerror = () => {
              const alt = headerMapImageUrl.endsWith('.png') ? headerMapImageUrl.replace('.png', '.jpg') : headerMapImageUrl.replace('.jpg', '.png');
              el.style.backgroundImage = `url(${alt})`;
            };
          }} style={{
            position: 'absolute', inset: 0, zIndex: 0,
            backgroundImage: `url(${headerMapImageUrl})`,
            backgroundSize: 'cover', backgroundPosition: 'center',
            opacity: 0.25,
            maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)',
          }} />
        )}
        {/* Top row: Back + Status - laid over the card's top corners (no row of its own);
            on narrow screens it is a normal row again (.md-head-top in PublicMatches.css) */}
        <div className="md-head-top" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', position: 'absolute', top: '0.85rem', left: '1.5rem', right: '1.5rem', zIndex: 2, pointerEvents: 'none' }}>
          <Link to={isAdminView ? "/admin/matches" : "/"} style={{ color: 'var(--text-muted, #4a4a5a)', textDecoration: 'none', fontSize: '0.8rem', fontFamily: 'var(--font-sans)', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4, pointerEvents: 'auto' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
            Back
          </Link>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, pointerEvents: 'auto' }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '4px 12px', borderRadius: 'var(--radius-sm, 6px)', fontSize: '0.78rem', fontWeight: 600,
            letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: 'var(--font-mono)',
            background: match.status === 'live' ? 'rgba(34,197,94,0.1)' : match.status === 'finished' ? 'rgba(255,255,255,0.04)' : match.status === 'cancelled' ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)',
            color: match.status === 'live' ? 'var(--accent-green, #22c55e)' : match.status === 'finished' ? 'var(--text-secondary, #8b8b9e)' : match.status === 'cancelled' ? 'var(--accent-red, #ef4444)' : 'var(--accent-amber, #f59e0b)',
            border: `1px solid ${match.status === 'live' ? 'rgba(34,197,94,0.2)' : match.status === 'finished' ? 'var(--border-subtle, rgba(255,255,255,0.06))' : match.status === 'cancelled' ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)'}`,
          }}>
            {match.status === 'live' && (
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent-green, #22c55e)', animation: 'pub-pulse 1.4s ease-in-out infinite', display: 'inline-block' }} />
            )}
            {(['finished', 'cancelled'].includes(match.status) ? match.status : (match.match_stage || match.status)).toUpperCase()}
          </span>
          {/* time of the map shown (live → finish), total on "All maps" */}
          <MapDuration matchId={match.id} status={match.status} anonymous={!isAdminView} times={mapTimes}
            mapIndex={selectedMapIndex === 'all' ? 'all' : (selectedMapIndex ?? match.current_map_index ?? 0)} />
          </div>
        </div>

        {/* Centered scoreboard */}
        <div className="match-score-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: _showSeries ? '1rem' : '2rem', marginBottom: '0.45rem', position: 'relative', zIndex: 1 }}>
          {/* Team 1 */}
          <div className="match-team match-team-1" style={{ textAlign: 'right', flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <div style={{ minWidth: 0 }}>
              {showConnectionDots && team1TotalCount > 0 && (
                <div style={{ fontSize: '0.65rem', fontWeight: 600, color: team1ConnectedCount === team1TotalCount ? 'var(--accent-green, #22c55e)' : team1ConnectedCount === 0 ? 'var(--accent-red, #ef4444)' : 'var(--accent-amber, #f59e0b)', fontFamily: 'var(--font-mono)', letterSpacing: '0.04em', marginBottom: '4px' }} title={`${team1ConnectedCount} of ${team1TotalCount} players connected`}>
                  {team1TotalCount} players
                </div>
              )}
              <div className="match-team-name" style={{ fontSize: '1.1rem', fontWeight: 700, color: team1Color, fontFamily: 'var(--font-display)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {!isAdminView && match.team1_exists ? (
                  <Link to={`/teams/${match.team1_id}`} style={{ color: 'inherit', textDecoration: 'none', transition: 'opacity 0.15s' }} onMouseEnter={e => e.currentTarget.style.opacity = '0.75'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>{match.team1_name}</Link>
                ) : match.team1_name}
              </div>
              <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: '0.35rem', marginTop: 4, fontFamily: 'var(--font-mono)', letterSpacing: '0.04em', lineHeight: 1 }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: team1Color, opacity: 0.7 }}>{team1Side}</span>
              </div>
            </div>
            {match.team1_avatar && match.team1_exists ? (
              <img
                src={`/api/teams/${match.team1_id}/avatar?v=${match.team1_id}`}
                alt=""
                style={{ width: 40, height: 40, borderRadius: '8px', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}
                onError={e => { e.target.style.display = 'none'; }}
              />
            ) : null}
          </div>

          {/* Team 1 Series Score (BO3/BO5 only) */}
          {_showSeries && (
            <div title={`${match.team1_name} map wins`} style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, minWidth: 44,
            }}>
              <span style={{
                fontSize: '1.1rem', fontWeight: 800, fontFamily: 'var(--font-mono)',
                color: match.status === 'finished' ? (_series1 > _series2 ? '#22c55e' : _series1 < _series2 ? '#ef4444' : 'var(--text-primary, #e8e8ed)') : 'var(--text-primary, #e8e8ed)', lineHeight: 1,
                textShadow: _series1 > 0 ? `0 0 20px ${team1Color}40` : 'none',
              }}>{_series1}</span>
            </div>
          )}

          {/* Score */}
          <div className="match-score-center" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }}>
            {/* Map Navigation for BO3/BO5 */}
            {_showSeries && (() => {
              const maps = match.map_list ? JSON.parse(match.map_list) : [];
              const totalMaps = maps.length;
              const currentIdx = selectedMapIndex === 'all' ? -1 : (selectedMapIndex ?? match.current_map_index ?? 0);
              const canGoPrev = currentIdx > 0;
              const canGoNext = currentIdx < totalMaps - 1;
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.15rem' }}>
                  <button
                    onClick={() => setSelectedMapIndex(Math.max(0, currentIdx - 1))}
                    disabled={!canGoPrev}
                    style={{
                      background: canGoPrev ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${canGoPrev ? 'rgba(59,130,246,0.3)' : 'rgba(255,255,255,0.08)'}`,
                      borderRadius: 'var(--radius-sm)',
                      color: canGoPrev ? '#3b82f6' : 'var(--text-muted)',
                      cursor: canGoPrev ? 'pointer' : 'not-allowed',
                      padding: '4px 12px',
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      opacity: canGoPrev ? 1 : 0.3,
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { if (canGoPrev) e.currentTarget.style.background = 'rgba(59,130,246,0.25)'; }}
                    onMouseLeave={e => { if (canGoPrev) e.currentTarget.style.background = 'rgba(59,130,246,0.15)'; }}
                    title="Previous map"
                  >
                    ‹
                  </button>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)', minWidth: 70, textAlign: 'center', letterSpacing: '0.02em' }}>
                    Map {currentIdx + 1}/{totalMaps}
                  </span>
                  <button
                    onClick={() => setSelectedMapIndex(Math.min(totalMaps - 1, currentIdx + 1))}
                    disabled={!canGoNext}
                    style={{
                      background: canGoNext ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${canGoNext ? 'rgba(59,130,246,0.3)' : 'rgba(255,255,255,0.08)'}`,
                      borderRadius: 'var(--radius-sm)',
                      color: canGoNext ? '#3b82f6' : 'var(--text-muted)',
                      cursor: canGoNext ? 'pointer' : 'not-allowed',
                      padding: '4px 12px',
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      opacity: canGoNext ? 1 : 0.3,
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { if (canGoNext) e.currentTarget.style.background = 'rgba(59,130,246,0.25)'; }}
                    onMouseLeave={e => { if (canGoNext) e.currentTarget.style.background = 'rgba(59,130,246,0.15)'; }}
                    title="Next map"
                  >
                    ›
                  </button>
                </div>
              );
            })()}
            <div className="match-score-digits" style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem' }}>
              <span style={{ fontSize: '2.6rem', fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-primary, #e8e8ed)', lineHeight: 1 }}>{displayScore1}</span>
              <span style={{ fontSize: '1.6rem', color: 'var(--text-muted, #4a4a5a)', fontWeight: 300 }}>:</span>
              <span style={{ fontSize: '2.6rem', fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-primary, #e8e8ed)', lineHeight: 1 }}>{displayScore2}</span>
            </div>
            {_showSeries && (
              <div
                title={`Best of ${_bo} series score`}
                style={{
                  display: 'inline-flex', alignItems: 'center',
                  padding: '2px 10px', borderRadius: 'var(--radius-sm, 6px)',
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  fontFamily: 'var(--font-mono)', fontSize: '0.72rem', fontWeight: 700,
                  color: 'var(--text-secondary, #8b8b9e)',
                  letterSpacing: '0.04em', lineHeight: 1.4,
                }}
              >
                BO{_bo}
              </div>
            )}
            {/* Round Timer Display */}
            <MatchTimerPill match={match} matchTimer={matchTimer} />
          </div>

          {/* Team 2 Series Score (BO3/BO5 only) */}
          {_showSeries && (
            <div title={`${match.team2_name} map wins`} style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, minWidth: 44,
            }}>
              <span style={{
                fontSize: '1.1rem', fontWeight: 800, fontFamily: 'var(--font-mono)',
                color: match.status === 'finished' ? (_series2 > _series1 ? '#22c55e' : _series2 < _series1 ? '#ef4444' : 'var(--text-primary, #e8e8ed)') : 'var(--text-primary, #e8e8ed)', lineHeight: 1,
                textShadow: _series2 > 0 ? `0 0 20px ${team2Color}40` : 'none',
              }}>{_series2}</span>
            </div>
          )}

          {/* Team 2 */}
          <div className="match-team match-team-2" style={{ textAlign: 'left', flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {match.team2_avatar && match.team2_exists ? (
              <img
                src={`/api/teams/${match.team2_id}/avatar?v=${match.team2_id}`}
                alt=""
                style={{ width: 40, height: 40, borderRadius: '8px', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}
                onError={e => { e.target.style.display = 'none'; }}
              />
            ) : null}
            <div style={{ minWidth: 0 }}>
              {showConnectionDots && team2TotalCount > 0 && (
                <div style={{ fontSize: '0.65rem', fontWeight: 600, color: team2ConnectedCount === team2TotalCount ? 'var(--accent-green, #22c55e)' : team2ConnectedCount === 0 ? 'var(--accent-red, #ef4444)' : 'var(--accent-amber, #f59e0b)', fontFamily: 'var(--font-mono)', letterSpacing: '0.04em', marginBottom: '4px' }} title={`${team2ConnectedCount} of ${team2TotalCount} players connected`}>
                  {team2TotalCount} players
                </div>
              )}
              <div className="match-team-name" style={{ fontSize: '1.1rem', fontWeight: 700, color: team2Color, fontFamily: 'var(--font-display)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {!isAdminView && match.team2_exists ? (
                  <Link to={`/teams/${match.team2_id}`} style={{ color: 'inherit', textDecoration: 'none', transition: 'opacity 0.15s' }} onMouseEnter={e => e.currentTarget.style.opacity = '0.75'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>{match.team2_name}</Link>
                ) : match.team2_name}
              </div>
              <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: '0.35rem', marginTop: 4, fontFamily: 'var(--font-mono)', letterSpacing: '0.04em', lineHeight: 1 }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: team2Color, opacity: 0.7 }}>{team2Side}</span>
              </div>
            </div>
          </div>
        </div>
        {/* Half-score breakdown - placed under the map score */}
        {filteredRounds.length > 0 && selectedMapIndex !== 'all' && (() => {
          const mpMaxRounds = match?.mp_maxrounds || 24;
          const mpOtMaxRounds = match?.mp_overtime_maxrounds || 6;
          const roundsPerHalf = mpMaxRounds / 2;
          const roundsPerOTHalf = mpOtMaxRounds / 2;

          const h1 = filteredRounds.filter(r => r.round_number >= 1 && r.round_number <= roundsPerHalf);
          const h2 = filteredRounds.filter(r => r.round_number >= roundsPerHalf + 1 && r.round_number <= mpMaxRounds);
          const ot = filteredRounds.filter(r => r.round_number > mpMaxRounds);

          // Normalize startingSide1 so 'T' becomes 'TERRORIST' to match winner_side normalization
          const ss1 = startingSide1 === 'T' ? 'TERRORIST' : startingSide1;

          const countWins = (rounds, isFirstHalf) => {
            let t1 = 0, t2 = 0;
            rounds.forEach(r => {
              const ws = r.winner_side === 'T' ? 'TERRORIST' : r.winner_side;
              const s1 = isFirstHalf ? ss1 : (ss1 === 'CT' ? 'TERRORIST' : 'CT');
              if (ws === s1) t1++; else t2++;
            });
            return [t1, t2];
          };

          const [h1t1, h1t2] = countWins(h1, true);
          const [h2t1, h2t2] = countWins(h2, false);

          const parts = [];
          if (h1.length > 0) parts.push(`${h1t1}:${h1t2}`);
          if (h2.length > 0) parts.push(`${h2t1}:${h2t2}`);

          for (let i = 0; i < ot.length; i += mpOtMaxRounds) {
            const otSlice = ot.slice(i, i + mpOtMaxRounds);
            let ot1 = 0, ot2 = 0;
            otSlice.forEach((r, idx) => {
              const otHalfIdx = Math.floor(idx / roundsPerOTHalf);
              const isOtFirstHalf = otHalfIdx % 2 === 0;
              // OT starts on the opposite side from 2nd half, then alternates every OT half
              const s1 = isOtFirstHalf
                ? (ss1 === 'CT' ? 'TERRORIST' : 'CT')
                : ss1;
              const ws = r.winner_side === 'T' ? 'TERRORIST' : r.winner_side;
              if (ws === s1) ot1++; else ot2++;
            });
            parts.push(`${ot1}:${ot2}`);
          }

          if (parts.length > 0) {
            return (
              <div style={{ textAlign: 'center', position: 'relative', zIndex: 1, marginTop: -2, marginBottom: 4 }}>
                <span style={{
                  fontSize: '0.78rem', color: 'var(--text-secondary)', fontWeight: 600,
                  letterSpacing: '0.03em',
                  background: 'rgba(0,0,0,0.25)', padding: '2px 10px',
                  borderRadius: 9999, display: 'inline-block',
                  fontFamily: 'var(--font-mono)',
                }}>
                  ({parts.join('; ')})
                </span>
              </div>
            );
          }
          return null;
        })()}

        {/* Meta row */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', position: 'relative', zIndex: 1 }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary, #8b8b9e)', fontFamily: 'var(--font-sans)' }}>{headerMapName}</span>
          <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--text-muted, #4a4a5a)', display: 'inline-block' }} />
          <span style={{ fontSize: '0.72rem', fontWeight: 500, color: 'var(--text-muted, #4a4a5a)', fontFamily: 'var(--font-mono)' }}>BO{match.best_of || 1}</span>
          {match.bracket_id && (
            <>
              <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--text-muted, #4a4a5a)', display: 'inline-block' }} />
              <Link
                to={isAdminView ? `/admin/seasons/${match.season_id}/brackets` : `/bracket/${match.bracket_id}`}
                style={{ color: '#c084fc', fontSize: '0.72rem', fontWeight: 600, textDecoration: 'none', fontFamily: 'var(--font-sans)' }}
                title="View bracket"
              >Bracket</Link>
            </>
          )}
          {match.veto_session_id && (
            <>
              <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--text-muted, #4a4a5a)', display: 'inline-block' }} />
              <Link
                to={`/veto/${match.veto_session_id}`}
                style={{ color: 'var(--accent-blue, #3b82f6)', fontSize: '0.72rem', fontWeight: 600, textDecoration: 'none', fontFamily: 'var(--font-sans)' }}
                title="View map veto"
              >Map Veto</Link>
            </>
          )}
          {isAdminView && match.stage_seating_enabled && match.team1_id && match.team2_id && (
            <>
              <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--text-muted, #4a4a5a)', display: 'inline-block' }} />
              <Link
                to={`/admin/matches/${match.id}/seating`}
                style={{ color: 'var(--accent-green, #22c55e)', fontSize: '0.72rem', fontWeight: 600, textDecoration: 'none', fontFamily: 'var(--font-sans)' }}
                title={t('seating.matchLinkHint', 'Which team sits on which side of the stage, and who sits where')}
              >{t('seating.matchLink', 'Stage seating')}</Link>
            </>
          )}
          {isAdminView && (
            <>
              <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--text-muted, #4a4a5a)', display: 'inline-block' }} />
              <Link
                to={`/admin/matches/${match.id}/live`}
                style={{ color: 'var(--accent-cyan, #06b6d4)', fontSize: '0.72rem', fontWeight: 600, textDecoration: 'none', fontFamily: 'var(--font-sans)' }}
                title={t('liveView.linkHint', 'Live scoreboard with equipment and money, and the full match feed with highlights')}
              >{t('liveView.link', 'Live view')}</Link>
            </>
          )}
          {(() => {
            // a single map shown → that map's live start / end (mapTimes); "All maps" → the match's own.
            // A map not reached yet shows no times (it used to repeat the match's start / end on every map).
            const shown = selectedMapIndex === 'all' ? 'all' : (selectedMapIndex ?? match.current_map_index ?? 0);
            let start = match.started_at; let end = match.finished_at;
            if (shown !== 'all' && Number(match.best_of) > 1) {
              const m = mapTimes && (mapTimes.maps || []).find(x => x.map_index === Number(shown));
              start = m ? m.started_at : null;
              end = m ? m.ended_at : null;
            }
            return (<>
              {start && (
                <>
                  <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--text-muted, #4a4a5a)', display: 'inline-block' }} />
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted, #4a4a5a)', fontFamily: 'var(--font-mono)' }}>
                    {formatShortDateTime(start)}
                  </span>
                </>
              )}
              {end && (
                <>
                  <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--text-muted, #4a4a5a)', display: 'inline-block' }} />
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted, #4a4a5a)', fontFamily: 'var(--font-mono)' }}>
                    {t('matchDuration.ended', 'ended {{time}}', { time: formatShortDateTime(end) })}
                  </span>
                </>
              )}
            </>);
          })()}
          {isAdminView && match.ip_string && !['finished', 'cancelled'].includes(match.status) && (
            <>
              <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--text-muted, #4a4a5a)', display: 'inline-block' }} />
              <button
                onClick={e => { e.stopPropagation(); copyServerAddress(); }}
                style={{ background: 'none', border: 'none', color: copyFeedback ? 'var(--accent-green, #22c55e)' : 'var(--text-secondary, #8b8b9e)', cursor: 'pointer', fontSize: '0.7rem', fontFamily: 'var(--font-mono)', padding: 0, fontWeight: 500 }}
                title="Copy connect command"
              >
                {copyFeedback ? '✓ Copied' : displayConnectString || match.ip_string}
              </button>
            </>
          )}
          {!isAdminView && match.server_country_code && (
            <>
              <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--text-muted, #4a4a5a)', display: 'inline-block' }} />
              <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary, #8b8b9e)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {match.server_city || match.server_country_name || ''}
                <img src={`https://flagcdn.com/20x15/${match.server_country_code.toLowerCase()}.png`} alt={match.server_country_code} style={{ width: 16, height: 12, borderRadius: 2 }} />
              </span>
            </>
          )}
          <span style={{ fontSize: '0.78rem', color: 'var(--text-muted, #4a4a5a)', fontFamily: 'var(--font-mono)' }}>
            {scoreLabel}
          </span>
          {/* Demo button - per map, admin only */}
          {isAdminView && isOwner && (() => {
              const mapIdx = typeof selectedMapIndex === 'number' ? selectedMapIndex : 0;
              const ds = demoStatus[mapIdx] || {};
              const isDownloading = ds.status === 'downloading';
              const isUploading = ds.status === 'uploading';
              const isReady = ds.status === 'ready';
              // A map's demo is complete once that map ended: an earlier map of the series, or any map of a finished / cancelled match
              const mapEnded = match.status === 'finished' || match.status === 'cancelled'
                || mapIdx < (parseInt(match.current_map_index, 10) || 0);
              const secsLeft = isReady && !ds.gofileUrl && ds.expiresAt ? Math.max(0, Math.floor((ds.expiresAt - Date.now()) / 1000)) : null;
              const expiryLabel = secsLeft !== null
                ? (secsLeft >= 3600
                    ? `${Math.floor(secsLeft / 3600)}h ${Math.floor((secsLeft % 3600) / 60)}m`
                    : `${String(Math.floor(secsLeft / 60)).padStart(2, '0')}:${String(secsLeft % 60).padStart(2, '0')}`)
                : null;
              const sizeMB = (isReady && ds.fileSize) ? (ds.fileSize / (1024 * 1024)).toFixed(1) + ' MB' : null;
              const demoBtnBase = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 14px', borderRadius: 'var(--radius-sm, 6px)', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-sans)', border: 'none', letterSpacing: '0.01em' };

              if (ds.gofileUrl) {
                return (
                  <button onClick={() => window.open(ds.gofileUrl, '_blank')} style={{ ...demoBtnBase, background: 'var(--accent-blue, #3b82f6)', color: '#fff' }} title={`Download Map ${mapIdx + 1} demo from Gofile`}>
                    Download Demo
                  </button>
                );
              }
              return isReady ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <button onClick={handleDemoDownload(mapIdx)} style={{ ...demoBtnBase, background: 'var(--accent-green, #22c55e)', color: '#fff' }} title={ds.pendingRetry ? `Download Map ${mapIdx + 1} demo - local copy (background retry in progress${ds.retryAttempts ? `, attempt ${ds.retryAttempts}` : ''})` : `Download Map ${mapIdx + 1} demo - expires in ${expiryLabel}`}>
                    Download Demo
                    {sizeMB && <span style={{ opacity: 0.75, fontSize: '0.75rem' }}>({sizeMB})</span>}
                    {ds.pendingRetry && <span style={{ opacity: 0.8, fontSize: '0.75rem' }}>↻ retrying</span>}
                    {expiryLabel && !ds.pendingRetry && <span style={{ opacity: 0.8, fontSize: '0.75rem' }}>⏱ {expiryLabel}</span>}
                  </button>
                  {ds.pendingRetry && (
                    <button
                      onClick={() => handleRetryUpload(mapIdx)}
                      style={{ ...demoBtnBase, background: 'var(--accent-amber, #f59e0b)', color: '#000', fontWeight: 700 }}
                      title={`Manually retry uploading Map ${mapIdx + 1} demo to Gofile/Pixeldrain`}
                    >
                      ↑ Retry Upload
                    </button>
                  )}
                </span>
              ) : isUploading ? (
                <span style={{ ...demoBtnBase, background: 'rgba(59,130,246,0.12)', color: 'var(--accent-blue, #3b82f6)', cursor: 'default' }}>
                  <span className="demo-spin-icon">↻</span> Uploading…
                </span>
              ) : isDownloading ? (() => {
                const p = ds.progress;
                const hasProgress = p && p.total > 0;
                const pct = hasProgress ? `${p.percentage}%` : '';
                const eta = hasProgress && p.etaSeconds > 0 ? `${Math.floor(p.etaSeconds / 60)}:${String(p.etaSeconds % 60).padStart(2, '0')}` : null;
                const speedObj = p && p.speedBytesPerSec > 0 ? (p.speedBytesPerSec / (1024 * 1024)).toFixed(1) + ' MB/s' : null;
                return (
                  <span style={{ ...demoBtnBase, background: 'rgba(245,158,11,0.1)', color: 'var(--accent-amber, #f59e0b)', cursor: 'default' }}>
                    <span className="demo-spin-icon">↻</span>
                    {hasProgress ? `Downloading ${pct}` : 'Downloading…'}
                    {speedObj && <span style={{ opacity: 0.8, fontSize: '0.75rem' }}>({speedObj})</span>}
                    {eta && <span style={{ opacity: 0.8, fontSize: '0.75rem' }}>⏱ {eta}</span>}
                  </span>
                );
              })() : !mapEnded ? null : !match.allowDemoRequests ? (
                <button disabled style={{ ...demoBtnBase, opacity: 0.4, background: 'rgba(255,255,255,0.04)', color: 'var(--text-muted, #4a4a5a)', cursor: 'not-allowed' }} title="Demo requests disabled">
                  Requests Disabled
                </button>
              ) : (
                <button
                  onClick={() => handleRequestDemo(mapIdx)}
                  className={ds.status !== 'error' && ds.status !== 'expired' ? 'demo-btn-request' : ''}
                  style={{ ...demoBtnBase, background: ds.status === 'error' ? 'var(--accent-red, #ef4444)' : ds.status === 'expired' ? 'rgba(255,255,255,0.06)' : 'var(--accent-cyan, #06b6d4)', color: '#fff' }}
                  title={ds.status === 'error' ? `Error: ${ds.error} - click to retry` : ds.status === 'expired' ? 'Demo expired - click to re-request' : `Request Map ${mapIdx + 1} demo`}
                >
                  {ds.status === 'error' ? 'Retry Demo' : ds.status === 'expired' ? 'Re-request Demo' : 'Request Demo'}
                </button>
              );
          })()}
        </div>

      {/* ── Created by (owner / super admin) - links to the creator's Steam profile ── */}
      {isAdminView && (isOwner || isSuperAdmin) && match.created_by_steam && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: '0.75rem', fontFamily: 'var(--font-sans)', marginTop: '0.25rem', position: 'relative', zIndex: 1 }}>
          <span style={{ color: 'var(--text-muted, #4a4a5a)', fontWeight: 600 }}>{t('matchDetail.createdBy', 'Created by')}</span>
          <a
            href={`https://steamcommunity.com/profiles/${match.created_by_steam}`}
            target="_blank"
            rel="noopener noreferrer"
            title={t('matchDetail.openCreatorSteam', 'Open Steam profile')}
            style={{ color: 'var(--accent-blue, #3b82f6)', textDecoration: 'none', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            {match.created_by_name || match.created_by_steam}
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
          </a>
          {match.created_by_name && (
            <span style={{ color: 'var(--text-muted, #4a4a5a)', fontFamily: 'var(--font-mono)' }}>{match.created_by_steam}</span>
          )}
        </div>
      )}

      </div>

      {/* ── Match access: server connect info, coach tablet links, live log stream ── */}
      {((isOwner && match.server_ip) || (isSuperAdmin && match.status !== 'finished' && match.status !== 'cancelled')) && (
        <MatchAccessPanel
          match={match}
          matchId={id}
          displayIp={displayIp}
          showServer={!!(isOwner && match.server_ip)}
          showCoach={!!(isAdminView && (isOwner || isSuperAdmin) && match.status !== 'finished' && match.status !== 'cancelled')}
          showStream={!!(isAdminView && (isOwner || isSuperAdmin))}
        />
      )}

      {/* ═══ Two-Column Grid: Left = Stats, Right = Panel ═══ */}
      {isAdminView && isOwner && (
        <div className="match-admin-tabs" role="tablist" aria-label="Admin match sections">
          <button
            type="button"
            role="tab"
            aria-selected={adminMatchTab === 'operations'}
            className={`match-admin-tab ${adminMatchTab === 'operations' ? 'active' : ''}`}
            onClick={() => setAdminMatchTab('operations')}
          >
            {t('matchDetail.operations', 'Operations')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={adminMatchTab === 'scoreboard'}
            className={`match-admin-tab ${adminMatchTab === 'scoreboard' ? 'active' : ''}`}
            onClick={() => setAdminMatchTab('scoreboard')}
          >
            {t('matchDetail.scoreboardDemos', 'Scoreboard and Demos')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={adminMatchTab === 'players'}
            className={`match-admin-tab ${adminMatchTab === 'players' ? 'active' : ''}`}
            onClick={() => setAdminMatchTab('players')}
          >
            {t('matchDetail.playerManagement', 'Player Management')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={adminMatchTab === 'admin-logs'}
            className={`match-admin-tab ${adminMatchTab === 'admin-logs' ? 'active' : ''}`}
            onClick={() => setAdminMatchTab('admin-logs')}
          >
            {t('matchDetail.adminLogs', 'Admin Logs')}
          </button>
        </div>
      )}

      <div className={[
        'two-col',
        'match-admin-layout',
        isAdminView && isOwner ? `match-admin-layout-${adminMatchTab}` : '',
      ].filter(Boolean).join(' ')}>

        {/* ── LEFT: Stats + Admin Controls ── */}
        <div className="match-main-column">

          {/* Map Stats */}
          <div className="match-stats-card" style={{ background: 'var(--bg-card, #111118)', border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))', borderRadius: 'var(--radius-md, 8px)', overflow: 'hidden' }}>
            <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--border-subtle, rgba(255,255,255,0.06))' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-secondary, #8b8b9e)', fontFamily: 'var(--font-sans)' }}>
                  {t('matchDetail.statistics', 'Statistics')}
                </span>
                {match.bracket_id && (
                  <Link
                    to={isAdminView ? `/admin/seasons/${match.season_id}/brackets` : `/bracket/${match.bracket_id}`}
                    style={{ color: '#c084fc', fontSize: '0.78rem', fontWeight: 600, textDecoration: 'none', fontFamily: 'var(--font-sans)' }}
                    title="View bracket"
                  >Bracket</Link>
                )}
              </div>
              {(() => {
                let mapList = [];
                try { mapList = match.map_list ? (typeof match.map_list === 'string' ? JSON.parse(match.map_list) : match.map_list) : []; } catch (_) { }
                if (mapList.length === 0) mapList = [match.map || 'Unknown'];
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {mapList.map((m, i) => {
                      const mn = (m || '').replace(/^(de_|cs_)/, '') || `Map ${i + 1}`;
                      const isForfeit = getMapForfeitEntry(i);
                      const ds = (isAdminView && isOwner) ? (demoStatus[i] || {}) : {};
                      const hasGofile = ds.gofileUrl;
                      const isDownloading = ds.status === 'downloading';
                      const isUploading = ds.status === 'uploading';
                      const isReady = ds.status === 'ready';
                      const secsLeft = isReady && ds.expiresAt ? Math.max(0, Math.floor((ds.expiresAt - Date.now()) / 1000)) : null;
                      const expiryLabel = secsLeft !== null
                        ? (secsLeft >= 3600
                            ? `${Math.floor(secsLeft / 3600)}h ${Math.floor((secsLeft % 3600) / 60)}m`
                            : `${Math.floor(secsLeft / 60)}m`)
                        : null;
                      const logMeta = (isAdminView && isOwner) ? serverLogMeta[i] : null;
                      const tabBtnStyle = { fontSize: '0.72rem', padding: '5px 14px', borderRadius: 'var(--radius-sm, 6px)', border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))', cursor: 'pointer', fontWeight: 500, fontFamily: 'var(--font-sans)', transition: 'all 0.15s' };
                      const smallBtnStyle = { fontSize: '0.78rem', padding: '4px 10px', borderRadius: 'var(--radius-sm, 6px)', border: 'none', cursor: 'pointer', fontWeight: 500, fontFamily: 'var(--font-sans)' };
                      return (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                          <button
                            onClick={() => { setSelectedMapIndex(i); setSelectedOtPeriod(null); }}
                            style={{
                              ...tabBtnStyle,
                              background: selectedMapIndex === i ? 'var(--accent-blue, #3b82f6)' : 'var(--bg-elevated, #1a1a24)',
                              color: selectedMapIndex === i ? '#fff' : 'var(--text-secondary, #8b8b9e)',
                              borderColor: selectedMapIndex === i ? 'var(--accent-blue, #3b82f6)' : 'var(--border-subtle, rgba(255,255,255,0.06))',
                              ...(isForfeit ? { borderColor: 'var(--accent-amber, #f59e0b)' } : {}),
                            }}
                          >
                            {mn}{isForfeit ? <span style={{ marginLeft: 4, fontSize: '0.7rem', color: 'var(--accent-amber, #f59e0b)' }}>(FF)</span> : null}
                          </button>
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            {isAdminView && isOwner && (
                              <>
                                {hasGofile ? (
                                  <button onClick={() => window.open(ds.gofileUrl, '_blank')} style={{ ...smallBtnStyle, background: 'var(--accent-blue, #3b82f6)', color: '#fff' }} title={`Download M${i + 1} demo from Gofile`}>Demo M{i + 1}</button>
                                ) : isReady ? (
                                  <span style={{ display: 'inline-flex', gap: 4 }}>
                                    <button onClick={handleDemoDownload(i)} style={{ ...smallBtnStyle, background: 'var(--accent-green, #22c55e)', color: '#fff' }} title={ds.pendingRetry ? `Download M${i + 1} demo - local (retry in progress)` : `Download M${i + 1} demo`}>
                                      Demo M{i + 1} <span style={{ fontSize: '0.7rem', opacity: 0.8 }}>({ds.pendingRetry ? '↻ local' : expiryLabel})</span>
                                    </button>
                                    {ds.pendingRetry && (
                                      <button onClick={() => handleRetryUpload(i)} style={{ ...smallBtnStyle, background: 'var(--accent-amber, #f59e0b)', color: '#000', fontWeight: 700 }} title={`Retry upload for M${i + 1}`}>↑</button>
                                    )}
                                  </span>
                                ) : isDownloading ? (
                                  <span style={{ ...smallBtnStyle, color: 'var(--accent-amber, #f59e0b)', background: 'rgba(245,158,11,0.08)' }}>Fetching M{i + 1}</span>
                                ) : isUploading ? (
                                  <span style={{ ...smallBtnStyle, color: 'var(--accent-blue, #3b82f6)', background: 'rgba(59,130,246,0.08)' }}>Uploading M{i + 1}</span>
                                ) : !(match.status === 'finished' || match.status === 'cancelled' || i < (parseInt(match.current_map_index, 10) || 0)) ? null : (
                                  <button onClick={() => handleRequestDemo(i)} style={{ ...smallBtnStyle, background: 'var(--bg-elevated, #1a1a24)', color: 'var(--text-secondary, #8b8b9e)' }} title={ds.status === 'expired' ? 'Demo expired - request again' : ds.status === 'error' ? `Error: ${ds.error}` : `Request M${i + 1} demo`}>
                                    {ds.status === 'error' ? 'Retry' : ds.status === 'expired' ? 'Re-request' : 'Demo'} M{i + 1}
                                  </button>
                                )}
                                {logMeta && logMeta.line_count > 0 && (
                                  <button onClick={handleDownloadLogs(i)} style={{ ...smallBtnStyle, background: 'var(--bg-elevated, #1a1a24)', color: 'var(--text-secondary, #8b8b9e)' }} title={`Download Map ${i + 1} server logs (${logMeta.line_count} lines)`}>
                                    Logs M{i + 1}
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
            <div style={{ padding: '1rem 1.25rem' }}>
              {/* Order: CT team on top, T team on bottom - always */}
              {(() => {
                const teamEntries = [
                  { players: currentStats.team1.visible, name: match.team1_name, side: team1Side, teamId: match.team1_id, teamExists: match.team1_exists, score: displayScore1 },
                  { players: currentStats.team2.visible, name: match.team2_name, side: team2Side, teamId: match.team2_id, teamExists: match.team2_exists, score: displayScore2 },
                ];
                const ctTeam = teamEntries.find(t => t.side === 'CT') || teamEntries[0];
                const tTeam = teamEntries.find(t => t.side === 'T') || teamEntries[1];

                // Flux 3.0 + Swing columns only when the API sent them (not under a broadcast delay)
                const hasFlux3 = (list) => (list || []).some(p => p.flux3_rating != null);
                const renderTeamTable = ({ players, name, side, teamId, teamExists, score }, showMapName = false) => (
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(120px, auto) minmax(32px, 1fr)', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem', padding: '0 2px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: side === 'CT' ? CT_COLOR : T_COLOR, fontSize: '0.8rem', fontWeight: 600, fontFamily: 'var(--font-sans)' }}>
                        <span>
                          {!isAdminView && teamExists ? (
                            <Link to={`/teams/${teamId}`} style={{ color: 'inherit', textDecoration: 'none', transition: 'opacity 0.15s' }} onMouseEnter={e => e.currentTarget.style.opacity = '0.75'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>{name}</Link>
                          ) : name}
                        </span>
                        <span
                          title={selectedMapIndex === 'all' ? `Map wins (Best of ${match.best_of})` : `Map ${selectedMapIndex + 1} round score`}
                          style={{
                            fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '0.85rem',
                            color: 'var(--text-primary, #e8e8ed)',
                          }}
                        >
                          {score}
                        </span>
                      </span>
                      <span
                        title={showMapName ? headerMapName : ''}
                        style={{
                          justifySelf: 'center',
                          maxWidth: 'min(36vw, 420px)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: showMapName ? 'var(--text-primary, #e8e8ed)' : 'transparent',
                          fontFamily: 'var(--font-display)',
                          fontSize: '0.80rem',
                          fontWeight: 800,
                          letterSpacing: '0.08em',
                          lineHeight: 1,
                          textTransform: 'uppercase',
                          textShadow: showMapName ? '0 0 18px rgba(59,130,246,0.22)' : 'none',
                          pointerEvents: showMapName ? 'auto' : 'none',
                        }}
                      >
                        {showMapName ? headerMapName : 'Map'}
                      </span>
                      <span style={{ justifySelf: 'end', fontSize: '0.7rem', fontWeight: 600, color: side === 'CT' ? CT_COLOR : T_COLOR, fontFamily: 'var(--font-mono)', letterSpacing: '0.05em' }}>{side}</span>
                    </div>
                    <div style={{ borderRadius: 'var(--radius-sm, 6px)', overflow: 'hidden', border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem', fontFamily: 'var(--font-sans)', tableLayout: 'auto' }}>
                        <thead>
                          <tr style={{ background: 'var(--bg-elevated, #1a1a24)' }}>
                            {['PLAYER', 'K', 'D', 'A', 'K/D', 'DMG', 'ADR', 'KAST', 'RATING', ...(hasFlux3(players) ? ['FLUX3', 'SWING'] : []), 'HS%', 'HS', 'FK', 'TK', '2K', '3K', '4K', '5K'].map(h => {
                              const isRating = h === 'RATING' || h === 'FLUX3';
                              const titles = {
                                RATING: t('matchDetail.ratingHltvTitle', 'HLTV Rating 2.0 (estimate)'),
                                FLUX3: t('matchDetail.ratingFlux3Title', 'Flux 3.0 - HLTV 3.0-style rating with Round Swing'),
                                SWING: t('matchDetail.swingTitle', 'Round Swing: how much the player changes their team\'s chance to win the round, on average per round'),
                              };
                              return (
                                <th
                                  key={h}
                                  title={titles[h]}
                                  style={{ padding: '5px 6px', textAlign: h === 'PLAYER' ? 'left' : 'center', fontWeight: 600, fontSize: '0.65rem', letterSpacing: '0.04em', color: isRating ? 'var(--accent-cyan, #06b6d4)' : 'var(--text-muted, #4a4a5a)', borderBottom: '1px solid var(--border-subtle, rgba(255,255,255,0.06))', whiteSpace: 'nowrap', cursor: 'default', userSelect: 'none', ...(h === 'PLAYER' ? { width: '18%' } : {}) }}
                                >
                                  {h === 'RATING' ? 'HLTV 2.0' : h === 'FLUX3' ? 'FLUX 3.0' : h}
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {(players || []).length > 0 ? (players || []).map((p, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid var(--border-subtle, rgba(255,255,255,0.06))', transition: 'background 0.12s' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover, #16161f)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                              <td style={{ padding: '6px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                                  {showConnectionDots && (
                                    <span
                                      className={`conn-dot${connectedSteamIds.has(p.steam_id) ? '' : ' disconnected'}`}
                                      style={{
                                        width: 7, height: 7, borderRadius: '50%', flexShrink: 0, display: 'inline-block',
                                        backgroundColor: connectedSteamIds.has(p.steam_id) ? 'var(--accent-green, #22c55e)' : 'var(--accent-red, #ef4444)',
                                      }}
                                      title={connectedSteamIds.has(p.steam_id) ? 'Connected' : 'Disconnected'}
                                    />
                                  )}
                                  <span style={{ color: 'var(--text-primary, #e8e8ed)', fontWeight: 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {!isAdminView && p.steam_id ? (
                                      <a href={`https://steamcommunity.com/profiles/${p.steam_id}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-primary, #e8e8ed)', textDecoration: 'none', transition: 'color 0.15s' }} onMouseEnter={e => e.currentTarget.style.color = 'var(--accent-cyan, #06b6d4)'} onMouseLeave={e => e.currentTarget.style.color = 'var(--text-primary, #e8e8ed)'}>
                                        {p.name || p.steam_id || 'Unknown'}
                                      </a>
                                    ) : (p.name || p.steam_id || 'Unknown')}
                                  </span>
                                  {renderFaceitBadge(p.faceit)}
                                </span>
                                {isAdminView && isOwner && (
                                  <button
                                    onClick={() => {
                                      // Clear any prior force-show so the hide sticks.
                                      setForceShownSteamIds(prev => { const n = new Set(prev); n.delete(p.steam_id); return n; });
                                      handleTogglePlayerVisibility(p.steam_id, selectedMapIndex, true);
                                    }}
                                    style={{ background: 'none', border: 'none', color: 'var(--accent-red, #ef4444)', opacity: 0.4, cursor: 'pointer', fontSize: '0.85rem', lineHeight: 1, padding: '0 4px' }}
                                    title={`Hide player from M${selectedMapIndex === 'all' ? 'All' : selectedMapIndex + 1}`}
                                    className="pub-player-hide-btn hover:opacity-100"
                                  >−</button>
                                )}
                              </td>
                              {(() => {
                                const k = p.kills ?? 0, d = p.deaths ?? 0, a = p.assists ?? 0;
                                const hs = p.headshots ?? 0, fk = p.entry_kills ?? 0, tk = p.trade_kills ?? 0;
                                const mk2 = p.multikill_2k ?? 0, mk3 = p.multikill_3k ?? 0, mk4 = p.multikill_4k ?? 0, mk5 = p.multikill_5k ?? 0;
                                const kastRounds = p.kast_rounds ?? 0;
                                const dmg = p.damage ?? 0;
                                const rounds = Math.max(filteredRounds.length, 1);
                                const adr = p.adr != null ? Number(p.adr).toFixed(1) : (dmg / rounds).toFixed(1);
                                const hsPercent = k > 0 ? Math.round((hs / k) * 100) : 0;
                                const kd = (k / Math.max(d, 1)).toFixed(2);
                                const kastPct = p.kast_pct != null ? Number(p.kast_pct) : (rounds > 0 ? Math.round((kastRounds / rounds) * 100) : 0);
                                const rating = Number((p.hltv_rating ?? p.rating) ?? 0);
                                const ratingDisplay = filteredRounds.length > 0 ? Math.max(0, rating).toFixed(2) : '0.00';
                                const ratingColor = rating >= 1.4 ? '#22c55e' : rating >= 1.2 ? '#facc15' : rating >= 1.0 ? 'var(--text-secondary, #8b8b9e)' : '#ef4444';
                                const cellStyle = { padding: '5px 6px', textAlign: 'center', fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: '0.7rem' };
                                return (<>
                                  <td style={{ ...cellStyle, color: 'var(--accent-green, #22c55e)' }}>{k}</td>
                                  <td style={{ ...cellStyle, color: 'var(--accent-red, #ef4444)' }}>{d}</td>
                                  <td style={{ ...cellStyle, color: 'var(--accent-blue, #3b82f6)' }}>{a}</td>
                                  <td style={{ ...cellStyle, color: kd >= 1 ? 'var(--accent-green, #22c55e)' : 'var(--accent-red, #ef4444)' }}>{kd}</td>
                                  <td style={{ ...cellStyle, color: 'var(--text-secondary, #8b8b9e)' }}>{dmg}</td>
                                  <td style={{ ...cellStyle, color: adr >= 80 ? 'var(--accent-green, #22c55e)' : 'var(--text-secondary, #8b8b9e)' }}>{adr}</td>
                                  <td style={{ ...cellStyle, color: kastPct >= 70 ? 'var(--accent-green, #22c55e)' : 'var(--text-secondary, #8b8b9e)' }}>{kastPct}%</td>
                                  <td style={{ ...cellStyle, color: ratingColor, fontWeight: 700 }}>{ratingDisplay}</td>
                                  {hasFlux3(players) && (() => {
                                    const f3 = Number(p.flux3_rating ?? 0);
                                    const sw = Number(p.swing_pct ?? 0);
                                    const f3Color = f3 >= 1.4 ? '#22c55e' : f3 >= 1.2 ? '#facc15' : f3 >= 1.0 ? 'var(--text-secondary, #8b8b9e)' : '#ef4444';
                                    return (<>
                                      <td style={{ ...cellStyle, color: f3Color, fontWeight: 700 }}>{filteredRounds.length > 0 ? Math.max(0, f3).toFixed(2) : '0.00'}</td>
                                      <td style={{ ...cellStyle, color: sw > 0 ? 'var(--accent-green, #22c55e)' : sw < 0 ? 'var(--accent-red, #ef4444)' : 'var(--text-muted, #4a4a5a)' }}>{sw > 0 ? '+' : ''}{sw.toFixed(2)}%</td>
                                    </>);
                                  })()}
                                  <td style={{ ...cellStyle, color: hsPercent >= 50 ? '#facc15' : 'var(--text-secondary, #8b8b9e)' }}>{hsPercent}%</td>
                                  <td style={{ ...cellStyle, color: 'var(--text-secondary, #8b8b9e)' }}>{hs}</td>
                                  <td style={{ ...cellStyle, color: fk > 0 ? '#fb923c' : 'var(--text-muted, #4a4a5a)' }}>{fk}</td>
                                  <td style={{ ...cellStyle, color: tk > 0 ? '#a78bfa' : 'var(--text-muted, #4a4a5a)' }}>{tk}</td>
                                  <td style={{ ...cellStyle, color: mk2 > 0 ? 'var(--text-secondary, #8b8b9e)' : 'var(--text-muted, #4a4a5a)' }}>{mk2}</td>
                                  <td style={{ ...cellStyle, color: mk3 > 0 ? '#22d3ee' : 'var(--text-muted, #4a4a5a)' }}>{mk3}</td>
                                  <td style={{ ...cellStyle, color: mk4 > 0 ? '#facc15' : 'var(--text-muted, #4a4a5a)' }}>{mk4}</td>
                                  <td style={{ ...cellStyle, color: mk5 > 0 ? '#ef4444' : 'var(--text-muted, #4a4a5a)' }}>{mk5}</td>
                                </>);
                              })()}
                            </tr>
                          )) : (
                            <tr><td colSpan={hasFlux3(players) ? 19 : 17} style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted, #4a4a5a)', fontSize: '0.75rem' }}>No players connected</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );

                // Round history bar helper
                const renderRoundHistoryBar = () => {
                  if (filteredRounds.length === 0 || selectedMapIndex === 'all') return null;
                  const ss1 = startingSide1 === 'T' ? 'TERRORIST' : startingSide1;
                  const mpMaxRounds = match?.mp_maxrounds || 24;
                  const mpOtMaxRounds = match?.mp_overtime_maxrounds || 6;
                  const MR = mpMaxRounds / 2; // rounds per half

                  // Build round lookup map
                  const roundMap = {};
                  filteredRounds.forEach(r => {
                    const ws = r.winner_side === 'T' ? 'TERRORIST' : r.winner_side;
                    roundMap[r.round_number] = { winnerSide: ws, reason: r.win_reason || '' };
                  });

                  // Count OT periods
                  const otRounds = filteredRounds.filter(r => r.round_number > mpMaxRounds);
                  const otPeriodCount = otRounds.length > 0 ? Math.ceil(otRounds.length / mpOtMaxRounds) : 0;

                  // Determine which rounds to show
                  let displaySlots = [];
                  let halfDividerAfter = MR; // divider after this round number

                  if (!selectedOtPeriod) {
                    // Regulation: show mpMaxRounds slots
                    for (let i = 1; i <= mpMaxRounds; i++) displaySlots.push(i);
                  } else {
                    // OT period: show mpOtMaxRounds slots
                    const otStart = mpMaxRounds + (selectedOtPeriod - 1) * mpOtMaxRounds + 1;
                    for (let i = otStart; i < otStart + mpOtMaxRounds; i++) displaySlots.push(i);
                    halfDividerAfter = otStart + mpOtMaxRounds / 2 - 1;
                  }

                  const getReasonIcon = (reason, side) => {
                    const iconMap = {
                      'bomb_exploded': '/killfeed/explosion.svg',
                      'bomb_defused': '/killfeed/defuse.svg',
                      'ct_elimination': '/killfeed/skull_ct.svg',
                      't_elimination': '/killfeed/skull_t.svg',
                      'time': '/killfeed/stopwatch.svg',
                    };
                    const src = iconMap[reason];
                    if (src) return <img src={src} alt={reason} style={{ width: 12, height: 12, opacity: 0.9 }} />;
                    const color = side === 'CT' ? '#22d3ee' : '#fb923c';
                    return <div style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />;
                  };

                  const cellW = 18, cellH = 20, dividerW = 2;
                  const btnStyle = (active) => ({
                    padding: '2px 8px', fontSize: '0.6rem', fontWeight: 600, fontFamily: 'var(--font-mono)',
                    border: 'none', borderRadius: 3, cursor: 'pointer', flexShrink: 0,
                    background: active ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.04)',
                    color: active ? '#e8e8ed' : 'rgba(255,255,255,0.35)',
                    transition: 'all 0.15s',
                  });

                  // Determine if top team (ctTeam) is team1 or team2
                  const topIsTeam1 = team1Side === 'CT';

                  // For each round, determine if team1 won (team-based, not side-based)
                  const getTeamWinnerForRound = (rn) => {
                    const data = roundMap[rn];
                    if (!data) return null;
                    let team1SideForRound;
                    if (rn <= MR) {
                      team1SideForRound = ss1;
                    } else if (rn <= mpMaxRounds) {
                      team1SideForRound = ss1 === 'CT' ? 'TERRORIST' : 'CT';
                    } else {
                      const otRd = rn - mpMaxRounds;
                      const otHalf = Math.ceil(otRd / (mpOtMaxRounds / 2));
                      team1SideForRound = (otHalf % 2 === 1)
                        ? (ss1 === 'CT' ? 'TERRORIST' : 'CT')
                        : ss1;
                    }
                    const team1Won = data.winnerSide === team1SideForRound;
                    return { team1Won, side: data.winnerSide, reason: data.reason };
                  };

                  // Top row = ctTeam wins, bottom row = tTeam wins
                  const topBg = 'rgba(34,211,238,0.06)';
                  const botBg = 'rgba(251,146,60,0.06)';

                  // Split the shown rounds at the half-divider so half 1 sits on the LEFT
                  // of centre and half 2 on the RIGHT. OT periods split the same way.
                  const leftSlots = displaySlots.filter(rn => rn <= halfDividerAfter);
                  const rightSlots = displaySlots.filter(rn => rn > halfDividerAfter);

                  const renderCell = (rn, isTop) => {
                    const result = getTeamWinnerForRound(rn);
                    const isWin = result && (isTop
                      ? (topIsTeam1 ? result.team1Won : !result.team1Won)
                      : (topIsTeam1 ? !result.team1Won : result.team1Won));
                    // Natural cell width (icons hug the centre divider), but shrinkable so
                    // the two halves still fit a narrow (mobile) viewport instead of overflowing.
                    return (
                      <div key={rn} style={{ flex: `0 1 ${cellW}px`, maxWidth: cellW, minWidth: 0, height: cellH, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {isWin ? getReasonIcon(result.reason, result.side) : null}
                      </div>
                    );
                  };

                  // Each row: [left half → hugs centre] [divider] [right half → hugs centre].
                  // Both halves are flex:1 so the divider stays pinned to the true centre.
                  const renderRow = (isTop) => (
                    <div style={{ display: 'flex', alignItems: 'center', background: isTop ? topBg : botBg, padding: '0 6px', minHeight: cellH, borderBottom: isTop ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                      <div style={{ flex: '1 1 0', display: 'flex', justifyContent: 'flex-end', overflow: 'hidden' }}>
                        {leftSlots.map(rn => renderCell(rn, isTop))}
                      </div>
                      <div style={{ width: dividerW, height: cellH, background: 'rgba(255,255,255,0.1)', margin: '0 4px', flexShrink: 0 }} />
                      <div style={{ flex: '1 1 0', display: 'flex', justifyContent: 'flex-start', overflow: 'hidden' }}>
                        {rightSlots.map(rn => renderCell(rn, isTop))}
                      </div>
                    </div>
                  );

                  return (
                    <div style={{ margin: '6px 0', overflow: 'hidden', borderRadius: 4 }}>
                      {renderRow(true)}
                      {renderRow(false)}
                      {/* OT toggle buttons - below the bar so they never shift the centre divider */}
                      {otPeriodCount > 0 && (
                        <div style={{ display: 'flex', gap: 3, justifyContent: 'center', marginTop: 4 }}>
                          <button style={btnStyle(!selectedOtPeriod)} onClick={() => setSelectedOtPeriod(null)}>REG</button>
                          {Array.from({ length: otPeriodCount }, (_, i) => (
                            <button key={i + 1} style={btnStyle(selectedOtPeriod === i + 1)} onClick={() => setSelectedOtPeriod(i + 1)}>OT{i + 1}</button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                };

                return (
                  <>
                    {renderTeamTable(ctTeam, true)}
                    {renderRoundHistoryBar()}
                    {renderTeamTable(tTeam)}
                  </>
                );
              })()}
            </div>

            {/* Admin: Hidden Players */}
            {isAdminView && isOwner && (currentStats.team1.hidden.length > 0 || currentStats.team2.hidden.length > 0) && (
              <div style={{ padding: '0 1.25rem 1rem' }}>
                <details style={{ background: 'var(--bg-elevated, #1a1a24)', border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))', borderRadius: 'var(--radius-sm, 6px)', padding: '0.75rem' }}>
                  <summary style={{ fontSize: '0.86rem', fontWeight: 600, color: 'var(--text-secondary, #8b8b9e)', cursor: 'pointer', userSelect: 'none', fontFamily: 'var(--font-sans)' }}>
                    Hidden Roster ({currentStats.team1.hidden.length + currentStats.team2.hidden.length})
                  </summary>
                  <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    {[...currentStats.team1.hidden, ...currentStats.team2.hidden].map(p => (
                      <div key={p.steam_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--bg-input, #0e0e15)', padding: '6px 12px', borderRadius: 'var(--radius-sm, 6px)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {showConnectionDots && (
                            <span
                              className={`conn-dot${connectedSteamIds.has(p.steam_id) ? '' : ' disconnected'}`}
                              style={{
                                width: 7, height: 7, borderRadius: '50%', flexShrink: 0, display: 'inline-block',
                                backgroundColor: connectedSteamIds.has(p.steam_id) ? 'var(--accent-green, #22c55e)' : 'var(--accent-red, #ef4444)',
                              }}
                              title={connectedSteamIds.has(p.steam_id) ? 'Connected' : 'Disconnected'}
                            />
                          )}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                            <span style={{ fontSize: '0.78rem', color: 'var(--text-primary, #e8e8ed)', fontWeight: 600, fontFamily: 'var(--font-sans)' }}>{p.name || p.steam_id}</span>
                            {renderFaceitBadge(p.faceit)}
                            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted, #4a4a5a)', marginLeft: 8, fontFamily: 'var(--font-mono)' }}>{p.steam_id}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            // Mark as explicitly shown so the auto ghost-filter can't
                            // re-hide a player who has no server-side hidden_maps entry.
                            setForceShownSteamIds(prev => { const n = new Set(prev); n.add(p.steam_id); return n; });
                            handleTogglePlayerVisibility(p.steam_id, selectedMapIndex, false);
                          }}
                          style={{ background: 'rgba(34,197,94,0.1)', color: 'var(--accent-green, #22c55e)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 'var(--radius-sm, 6px)', cursor: 'pointer', padding: '2px 8px', fontSize: '0.7rem', fontWeight: 600, fontFamily: 'var(--font-sans)' }}
                          title={`Restore player to M${selectedMapIndex === 'all' ? 'All' : selectedMapIndex + 1}`}
                        >
                          + Restore
                        </button>
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            )}
          </div>

          {/* ── Column-2 stack: Connected Players + Connected in CSTV (Operations tab). ──
              Wrapped in one flex-column cell so both cards sit at the top of column 2 and
              each auto-sizes to its roster (5 or 10 players), instead of the CSTV card being
              pushed to the bottom of the grid by the tall Console/Controls columns. */}
          {isAdminView && isOwner && adminMatchTab === 'operations' && (
            <div className="match-ops-col2">
            {(() => {
            // Group by the KNOWN ROSTER (match.team1_players / team2_players), NOT the
            // live team_side — the same approach the scoreboard uses. Live team_side is
            // unreliable during warmup/connect (players spawn unassigned or on the wrong
            // side), which was putting people under the wrong team. The roster is the
            // source of truth for which team a player belongs to.
            const statusBySteam = new Map();
            (playerStatus?.players || []).forEach(p => statusBySteam.set(p.steam_id, p));
            const isConnected = (steamId) => statusBySteam.has(steamId);

            // Two behaviors depending on stage:
            //  • WARMUP: show ONLY currently-connected roster players (tracking who is
            //    here and readying up). No absent players — a team of 5/10/15 only shows
            //    whoever is actually on the server.
            //  • LIVE / knife / finished: the lineup is locked. Show every roster player
            //    who has EVER been on the server this match (they're part of the game),
            //    with a green dot if still connected or a RED dot if they DC'd/crashed,
            //    so admins can spot disconnects. `is_connected` in match_players stays 1
            //    once seen and only flips to 0 on a real disconnect, so we key off the
            //    roster and the live connected set.
            const inWarmup = match.match_stage === 'warmup';
            // "Played this match" = has any recorded stats (kills/deaths/assists/damage)
            // OR is currently connected. Used to decide who to show once live, so a
            // player who DC'd/crashed still appears (with a red dot) because they have
            // stats from before they left.
            const statsBySteam = new Map();
            [...(currentStats.team1?.visible || []), ...(currentStats.team1?.hidden || []),
             ...(currentStats.team2?.visible || []), ...(currentStats.team2?.hidden || [])]
              .forEach(p => statsBySteam.set(p.steam_id, p));
            const playedMatch = (steamId) => {
              const s = statsBySteam.get(steamId);
              return !!s && ((s.kills || 0) + (s.deaths || 0) + (s.assists || 0) + (s.damage || 0) + (s.score || 0)) > 0;
            };
            // Side each team should be on right now (server rule, current map) - the ✓ / ✗ before a name
            const team1Expected = playerStatus?.team1_side || team1Side;
            const team2Expected = team1Expected === 'CT' ? 'T' : 'CT';
            const buildList = (roster, expectedSide) => (roster || [])
              .map(p => {
                const st = statusBySteam.get(p.steam_id) || {};
                return {
                  steam_id: p.steam_id,
                  player_name: st.player_name || p.name || p.steam_id,
                  // CT | T | SPECTATOR | UNASSIGNED from the logs; null = not known yet
                  live_side: st.live_side || null,
                  expected_side: expectedSide,
                  connected: isConnected(p.steam_id),
                  is_ready: !!st.is_ready,
                  ping: (typeof st.ping === 'number') ? st.ping : null,
                  loss: (typeof st.loss === 'number') ? st.loss : null,
                  // Live { hp, armor, helmet, alive } — owner-only, knife/live stages only.
                  vitals: playerStatus?.vitals?.[p.steam_id] || null,
                };
              })
              // Warmup: only currently-connected players. Live+: players who are
              // connected OR have match stats (so DC'd/crashed players still show, red).
              .filter(p => inWarmup ? p.connected : (p.connected || playedMatch(p.steam_id)))
              .sort((a, b) => (b.connected === a.connected ? 0 : b.connected ? 1 : -1));
            const team1List = buildList(match.team1_players, team1Expected);
            const team2List = buildList(match.team2_players, team2Expected);

            // Team-ready: a whole team forced ready (!forceready → force_ready flag), OR
            // every CONNECTED player on that side has individually !ready'd (min 1).
            // force_ready flags are keyed by CT/T side, so map them to team1/team2.
            const team1IsCt = team1Side === 'CT';
            const forceCt = !!playerStatus?.force_ready_ct;
            const forceT = !!playerStatus?.force_ready_t;
            const team1Forced = team1IsCt ? forceCt : forceT;
            const team2Forced = team1IsCt ? forceT : forceCt;
            const teamReady = (list, forced) => {
              const connectedPlayers = list.filter(p => p.connected);
              return forced || (connectedPlayers.length > 0 && connectedPlayers.every(p => !!p.is_ready));
            };
            const team1Ready = teamReady(team1List, team1Forced);
            const team2Ready = teamReady(team2List, team2Forced);
            // Ready/unready only matters during WARMUP (before go-live / force-start).
            // Once the match is knife/live/finished, drop the ready badges and just
            // show who is connected vs disconnected.
            const showReady = match.match_stage === 'warmup';

            const READY = 'var(--accent-green, #22c55e)';
            const UNREADY = 'var(--accent-amber, #f59e0b)';
            const CONNECTED = 'var(--accent-green, #22c55e)';
            const NOT_CONNECTED = 'var(--accent-red, #ef4444)';
            // Finished/cancelled: the server endpoint returns no live players, so every
            // dot would be red ("disconnected") — misleading on an old match. Show the
            // lineup with neutral grey dots and a "match over" label instead.
            const matchOver = match.status === 'finished' || match.status === 'cancelled';
            const OVER = 'var(--text-muted, #4a4a5a)';

            // ✓ on his team's side · ✗ on the other side / spectator / no team · ? side not known yet
            // (just connected, still picking a team). Only for connected players of an open match.
            const sideName = (s) => ({
              CT: 'CT', T: 'T',
              SPECTATOR: t('matchDetail.sideSpectator', 'Spectator'),
              UNASSIGNED: t('matchDetail.sideUnassigned', 'no team'),
            }[s] || s);
            const sideState = (p) => (!p.live_side ? 'unknown' : p.live_side === p.expected_side ? 'ok' : 'wrong');
            const SideMark = ({ p }) => {
              if (matchOver || !p.connected) return <span style={{ width: 12, flexShrink: 0 }} />;
              const state = sideState(p);
              const title = state === 'ok'
                ? t('matchDetail.sideOk', 'On the right side ({{side}})', { side: p.expected_side })
                : state === 'wrong'
                  ? t('matchDetail.sideWrong', 'On {{actual}} - should be {{side}}', { actual: sideName(p.live_side), side: p.expected_side })
                  : t('matchDetail.sideUnknown', 'Side not known yet - still picking a team or hasn\'t spawned');
              const c = state === 'ok' ? 'var(--accent-green, #22c55e)' : state === 'wrong' ? 'var(--accent-red, #ef4444)' : 'var(--text-muted, #4a4a5a)';
              return (
                <span title={title} aria-label={title} style={{ width: 12, flexShrink: 0, display: 'inline-flex', justifyContent: 'center', color: c }}>
                  {state === 'unknown' ? <span style={{ fontSize: '0.66rem', fontWeight: 700, lineHeight: 1 }}>?</span> : (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
                      {state === 'ok' ? <path d="M5 12.5l4.5 4.5L19 7.5" /> : <path d="M6 6l12 12M18 6L6 18" />}
                    </svg>
                  )}
                </span>
              );
            };

            // Score badge next to the team name (where warmup shows Ready / Not ready):
            // live rounds → the live map score; match over → maps won (BO3/BO5) or the final score (BO1).
            const isSeries = (parseInt(match.best_of, 10) || 1) > 1;
            const showScore = match.status === 'finished' || (match.status === 'live' && match.match_stage === 'live');
            const scoreOf = (n) => parseInt(match.status === 'finished' && isSeries ? match[`map_wins_team${n}`] : match[`score_team${n}`], 10) || 0;
            const ScoreBadge = ({ mine, theirs }) => {
              const c = mine > theirs ? 'var(--accent-green, #22c55e)' : mine < theirs ? 'var(--accent-red, #ef4444)' : 'var(--text-secondary, #8b8b9e)';
              const title = match.status === 'finished'
                ? (isSeries ? t('matchDetail.scoreMapsWon', 'Maps won') : t('matchDetail.scoreFinal', 'Final score'))
                : t('matchDetail.scoreLive', 'Live map score');
              return (
                <span title={title} style={{
                  fontSize: '0.66rem', fontWeight: 800, fontFamily: 'var(--font-mono)', minWidth: 18, textAlign: 'center',
                  padding: '0 5px', borderRadius: 4, flexShrink: 0, lineHeight: '15px',
                  color: c, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                }}>{mine}</span>
              );
            };

            const TeamBlock = ({ name, color, list, ready, forced, score, oppScore }) => (
              <div style={{ marginBottom: '0.85rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.4rem' }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.78rem', fontWeight: 700, color, fontFamily: 'var(--font-display)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                  {/* Team ready badge - only during warmup */}
                  {showReady && (
                    <span style={{
                      fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
                      padding: '1px 5px', borderRadius: 4, flexShrink: 0,
                      color: ready ? READY : UNREADY,
                      background: ready ? 'rgba(34,197,94,0.1)' : 'rgba(245,158,11,0.1)',
                      border: `1px solid ${ready ? 'rgba(34,197,94,0.25)' : 'rgba(245,158,11,0.25)'}`,
                    }} title={forced ? 'Team force-readied (!forceready)' : ready ? 'All connected players ready' : 'Waiting for players to ready up'}>
                      {ready ? t('matchDetail.teamReady', 'Ready') : t('matchDetail.teamNotReady', 'Not Ready')}
                    </span>
                  )}
                  {!showReady && showScore && <ScoreBadge mine={score} theirs={oppScore} />}
                  {(() => {
                    const wrong = matchOver ? 0 : list.filter(p => p.connected && sideState(p) === 'wrong').length;
                    return wrong > 0 && (
                      <span title={t('matchDetail.wrongSideHint', 'Players of this team on the other side, in spectator or without a team')} style={{
                        fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
                        padding: '1px 5px', borderRadius: 4, flexShrink: 0,
                        color: 'var(--accent-red, #ef4444)', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
                      }}>{t('matchDetail.wrongSideCount', '{{count}} wrong side', { count: wrong })}</span>
                    );
                  })()}
                  <span style={{ marginLeft: 'auto', fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted, #4a4a5a)', fontFamily: 'var(--font-mono)' }}>{matchOver ? list.length : `${list.filter(p => p.connected).length}/${list.length}`}</span>
                </div>
                {list.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    {list.map(p => {
                      const pReady = forced || !!p.is_ready;
                      // Dot = CONNECTION status: green connected, red not connected.
                      const dotColor = matchOver ? OVER : (p.connected ? CONNECTED : NOT_CONNECTED);
                      return (
                        <div key={p.steam_id} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '3px 6px', borderRadius: 'var(--radius-sm, 6px)', fontSize: '0.8rem', color: p.connected ? 'var(--text-secondary, #8b8b9e)' : 'var(--text-muted, #4a4a5a)', fontFamily: 'var(--font-sans)' }}>
                          <span style={{ width: 5, height: 5, borderRadius: '50%', background: dotColor, display: 'inline-block', flexShrink: 0, boxShadow: matchOver ? 'none' : `0 0 5px ${dotColor}` }} />
                          <SideMark p={p} />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: (!matchOver && p.connected && p.vitals && !p.vitals.alive) ? 0.45 : 1 }}>{p.player_name || p.steam_id}</span>
                          {/* HP bar + armor, then ping (from `status`, ~30s) + ready state. */}
                          <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '0.45rem', flexShrink: 0 }}>
                            {!matchOver && p.connected && p.vitals && <VitalsBadge v={p.vitals} />}
                            {!matchOver && p.connected && p.ping != null && (
                              <span
                                title={t('matchDetail.pingTitle', 'Ping (updates about every 30s)')}
                                style={{
                                  fontSize: '0.62rem', fontWeight: 600, fontFamily: 'var(--font-mono)',
                                  color: p.ping < 50 ? 'var(--accent-green, #22c55e)' : p.ping < 100 ? 'var(--accent-amber, #f59e0b)' : 'var(--accent-red, #ef4444)',
                                }}
                              >{t('matchDetail.pingMs', '{{ping}} ms', { ping: p.ping })}</span>
                            )}
                            {/* Packet loss (%) from the same `status` row: green 0, amber ≥1, red ≥5 */}
                            {!matchOver && p.connected && p.ping != null && p.loss != null && (
                              <span
                                title={t('matchDetail.lossTitle', 'Packet loss (updates about every 30s)')}
                                style={{
                                  fontSize: '0.62rem', fontWeight: 600, fontFamily: 'var(--font-mono)',
                                  color: p.loss >= 5 ? 'var(--accent-red, #ef4444)' : p.loss >= 1 ? 'var(--accent-amber, #f59e0b)' : 'var(--accent-green, #22c55e)',
                                }}
                              >{t('matchDetail.lossPct', '{{loss}}% loss', { loss: p.loss })}</span>
                            )}
                            {showReady && p.connected && (
                              <span style={{ fontSize: '0.6rem', fontWeight: 600, color: pReady ? READY : UNREADY }}>{pReady ? t('matchDetail.playerReady', 'READY') : t('matchDetail.playerUnready', 'UNREADY')}</span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted, #4a4a5a)', fontStyle: 'italic', padding: '3px 6px' }}>{t('matchDetail.noneConnected', 'None connected')}</div>
                )}
              </div>
            );
            return (
              <div className="match-connected-card" style={{ background: 'var(--bg-card, #111118)', border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))', borderRadius: 'var(--radius-md, 8px)', padding: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.85rem' }}>
                  <span style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-secondary, #8b8b9e)', fontFamily: 'var(--font-sans)' }}>{t('matchDetail.connectedPlayers', 'Connected Players')}</span>
                  {(() => { const onlineCount = [...team1List, ...team2List].filter(p => p.connected).length; return (
                    <span style={{ fontSize: '0.7rem', fontWeight: 600, color: !matchOver && onlineCount > 0 ? 'var(--accent-green, #22c55e)' : 'var(--text-muted, #4a4a5a)', fontFamily: 'var(--font-mono)' }}>
                      {matchOver
                        ? (match.status === 'cancelled' ? t('matchDetail.connMatchCancelled', 'Match cancelled') : t('matchDetail.connMatchFinished', 'Match finished'))
                        : t('matchDetail.onlineCount', '{{count}} online', { count: onlineCount })}
                    </span>
                  ); })()}
                </div>
                <TeamBlock name={match.team1_name} color={team1Color} list={team1List} ready={team1Ready} forced={team1Forced} score={scoreOf(1)} oppScore={scoreOf(2)} />
                <TeamBlock name={match.team2_name} color={team2Color} list={team2List} ready={team2Ready} forced={team2Forced} score={scoreOf(2)} oppScore={scoreOf(1)} />
              </div>
            );
          })()}

            {/* ── Connected in CSTV ── GOTV viewers via RCON `status` (relay clients
                connect to the GOTV port, so they never appear in Connected Players). */}
            {(() => {
            const viewers = cstvStatus?.viewers || [];
            const relays = cstvStatus?.relays || [];
            const count = cstvStatus?.available ? (cstvStatus.spectators ?? viewers.length) : 0;
            const purple = '#a78bfa';
            return (
              <div className="match-cstv-card" style={{ background: 'var(--bg-card, #111118)', border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))', borderRadius: 'var(--radius-md, 8px)', padding: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.85rem' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-secondary, #8b8b9e)', fontFamily: 'var(--font-sans)' }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <path d="M23 7l-7 5 7 5V7z" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                    </svg>
                    {t('matchDetail.connectedCstv', 'Connected in CSTV')}
                  </span>
                  {/* Count only when we actually have live CSTV data - "0 watching" on an
                      ended/unreachable match read like a real reading. */}
                  {cstvStatus?.available && (
                    <span style={{ fontSize: '0.7rem', fontWeight: 600, color: count > 0 ? purple : 'var(--text-muted, #4a4a5a)', fontFamily: 'var(--font-mono)' }}>
                      {count} {t('matchDetail.cstvWatching', 'watching')}
                    </span>
                  )}
                </div>
                {cstvStatus == null ? (
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted, #4a4a5a)', fontStyle: 'italic', padding: '3px 6px' }}>{t('matchDetail.cstvLoading', 'Checking CSTV…')}</div>
                ) : !cstvStatus.available ? (
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted, #4a4a5a)', fontStyle: 'italic', padding: '3px 6px' }}>
                    {cstvStatus.reason === 'match_over'
                      ? (match.status === 'cancelled'
                        ? t('matchDetail.cstvMatchCancelled', 'Match cancelled - CSTV is no longer tracked')
                        : t('matchDetail.cstvMatchFinished', 'Match finished - CSTV is no longer tracked'))
                      : cstvStatus.reason === 'no_server'
                        ? t('matchDetail.cstvNoServer', 'No server assigned to this match')
                        : cstvStatus.reason === 'rcon_failed'
                          ? t('matchDetail.cstvRconFailed', 'Can’t reach the game server (RCON) - retrying…')
                          : t('matchDetail.cstvUnavailable', 'CSTV status unavailable')}
                  </div>
                ) : relays.length === 0 ? (
                  // No "SourceTV[n] Status" section in `status` at all → CSTV isn't running
                  // (a running relay with no viewers still has its own section, listed below).
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted, #4a4a5a)', fontStyle: 'italic', padding: '3px 6px' }}>{t('matchDetail.cstvNotRunning', 'CSTV is not running on this server (tv_enable 0)')}</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {relays.map((relay, ri) => {
                      const delaySec = relay.delay != null ? relay.delay : 0;
                      const relayViewers = relay.viewers || [];
                      return (
                        <div key={relay.index ?? ri} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          {/* Per-relay header: "Connected in CSTV N ( Xs delay )" */}
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem', padding: '0 6px', marginBottom: '1px' }}>
                            <span style={{ fontSize: '0.7rem', fontWeight: 700, color: purple, fontFamily: 'var(--font-sans)', letterSpacing: '0.02em' }}>
                              {t('matchDetail.cstvRelay', 'CSTV {{n}}', { n: (relay.index ?? ri) + 1 })}
                            </span>
                            <span style={{ fontSize: '0.66rem', fontWeight: 600, color: 'var(--text-muted, #4a4a5a)', fontFamily: 'var(--font-mono)' }}>
                              ({t('matchDetail.cstvDelay', '{{sec}}s delay', { sec: delaySec })})
                            </span>
                            <span style={{ marginLeft: 'auto', fontSize: '0.66rem', fontWeight: 600, color: relayViewers.length > 0 ? purple : 'var(--text-muted, #4a4a5a)', fontFamily: 'var(--font-mono)' }}>
                              {relayViewers.length} {t('matchDetail.cstvWatching', 'watching')}
                            </span>
                          </div>
                          {/* Per-relay connect info + public toggle (owner-only). IP/password come from
                              the match row (captured via RCON); the toggle exposes them on the public page. */}
                          {(() => {
                            const relayNum = (relay.index ?? ri) + 1;
                            const relayIp = relayNum === 1 ? match.cstv1_ip : match.cstv2_ip;
                            const relayPw = relayNum === 1 ? match.cstv1_password : match.cstv2_password;
                            const relayPublic = relayNum === 1 ? !!match.cstv1_public : !!match.cstv2_public;
                            if (!relayIp) return null;
                            const connectStr = `connect ${relayIp}${relayPw ? `;password ${relayPw}` : ''}`;
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '5px 6px', margin: '2px 0 4px', background: 'var(--bg-elevated, #1a1a24)', border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))', borderRadius: 'var(--radius-sm, 6px)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', minWidth: 0 }}>
                                  <span style={{ flex: 1, minWidth: 0, fontFamily: 'var(--font-mono)', fontSize: '0.7rem', color: 'var(--text-secondary, #8b8b9e)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{connectStr}</span>
                                  <button
                                    onClick={() => { navigator.clipboard.writeText(connectStr); setCstvCopyFeedback(relayNum); setTimeout(() => setCstvCopyFeedback(f => f === relayNum ? null : f), 1800); }}
                                    style={{ background: 'none', border: 'none', color: cstvCopyFeedback === relayNum ? 'var(--accent-green, #22c55e)' : 'var(--text-muted, #4a4a5a)', cursor: 'pointer', fontSize: '0.68rem', fontWeight: 600, fontFamily: 'var(--font-sans)', display: 'inline-flex', alignItems: 'center', gap: 3, padding: 0, flexShrink: 0 }}
                                    title={t('matchDetail.cstvCopyConnect', 'Copy connect command')}
                                  >
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                                    {cstvCopyFeedback === relayNum ? t('common.copied', 'Copied') : t('common.copy', 'Copy')}
                                  </button>
                                </div>
                                {/* Animated slide switch - smooth track fade + gliding knob. */}
                                <button
                                  onClick={() => cstvToggling !== relayNum && toggleCstvPublic(relayNum, !relayPublic)}
                                  disabled={cstvToggling === relayNum}
                                  role="switch"
                                  aria-checked={relayPublic}
                                  style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 8, padding: '2px 2px', background: 'none', border: 'none', cursor: cstvToggling === relayNum ? 'wait' : 'pointer', fontFamily: 'var(--font-sans)' }}
                                  title={relayPublic ? t('matchDetail.cstvPublicOnHint', 'Visible on the public match page. Click to hide.') : t('matchDetail.cstvPublicOffHint', 'Hidden from the public. Click to show on the public match page.')}
                                >
                                  <span
                                    aria-hidden="true"
                                    style={{
                                      position: 'relative', flexShrink: 0, width: 34, height: 18, borderRadius: 999,
                                      background: relayPublic ? 'var(--accent-green, #22c55e)' : 'rgba(255,255,255,0.14)',
                                      boxShadow: relayPublic ? '0 0 8px rgba(34,197,94,0.5), inset 0 0 0 1px rgba(34,197,94,0.6)' : 'inset 0 0 0 1px rgba(255,255,255,0.12)',
                                      transition: 'background 0.25s ease, box-shadow 0.25s ease',
                                      opacity: cstvToggling === relayNum ? 0.55 : 1,
                                    }}
                                  >
                                    <span style={{
                                      position: 'absolute', top: 2, left: 2, width: 14, height: 14, borderRadius: '50%',
                                      background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                                      transform: relayPublic ? 'translateX(16px)' : 'translateX(0)',
                                      transition: 'transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                    }} />
                                  </span>
                                  <span style={{
                                    fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.03em',
                                    color: relayPublic ? 'var(--accent-green, #22c55e)' : 'var(--text-muted, #4a4a5a)',
                                    transition: 'color 0.25s ease', whiteSpace: 'nowrap',
                                  }}>
                                    {relayPublic ? t('matchDetail.cstvPublicOn', 'Public') : t('matchDetail.cstvMakePublic', 'Make Public')}
                                  </span>
                                </button>
                              </div>
                            );
                          })()}
                          {relayViewers.length === 0 ? (
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted, #4a4a5a)', fontStyle: 'italic', padding: '2px 6px' }}>{t('matchDetail.cstvNoViewers', 'No one watching CSTV')}</div>
                          ) : relayViewers.map((v, i) => (
                            <div key={v.userid || i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '3px 6px', borderRadius: 'var(--radius-sm, 6px)', fontSize: '0.8rem', color: 'var(--text-secondary, #8b8b9e)', fontFamily: 'var(--font-sans)' }}>
                              <span style={{ width: 5, height: 5, borderRadius: '50%', background: purple, display: 'inline-block', flexShrink: 0, boxShadow: `0 0 5px ${purple}` }} />
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.name || t('matchDetail.cstvUnknownViewer', 'Viewer')}</span>
                              <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0, fontFamily: 'var(--font-mono)', fontSize: '0.68rem', color: 'var(--text-muted, #4a4a5a)' }}>
                                {v.connected && <span title={t('matchDetail.cstvWatchTime', 'Watch time')}>{v.connected}</span>}
                                {v.ping != null && <span title="ping">{v.ping}ms</span>}
                              </span>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* ── CSTV viewer name history: everyone ever seen on a relay for this
                    match (server-side, from the RCON status polls). Collapsed while
                    live, open once the match is over. ── */}
                {(() => {
                  const history = cstvStatus?.history || [];
                  if (history.length === 0) return null;
                  const ended = match.status === 'finished' || match.status === 'cancelled';
                  const open = showCstvHistory == null ? ended : showCstvHistory;
                  const multiRelay = new Set(history.map(h => h.relay_index)).size > 1;
                  return (
                    <div style={{ marginTop: '0.75rem', borderTop: '1px solid var(--border-subtle, rgba(255,255,255,0.06))', paddingTop: '0.6rem' }}>
                      <div
                        role="button"
                        aria-expanded={open}
                        onClick={() => setShowCstvHistory(!open)}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', userSelect: 'none', fontSize: '0.84rem', fontWeight: 700, color: 'var(--text-muted, #4a4a5a)', fontFamily: 'var(--font-sans)' }}
                      >
                        <span style={{ display: 'inline-block', width: 10, transition: 'transform 0.15s', transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}>{'>'}</span>
                        {t('matchDetail.cstvHistory', 'Viewer history')}
                        <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontWeight: 600, letterSpacing: 0 }}>{history.length}</span>
                      </div>
                      {open && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '0.4rem', maxHeight: 260, overflowY: 'auto' }}>
                          {history.map((h) => (
                            <div key={`${h.relay_index}:${h.name}`} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '3px 6px', fontSize: '0.78rem', color: 'var(--text-secondary, #8b8b9e)', fontFamily: 'var(--font-sans)', minWidth: 0 }}>
                              <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--text-muted, #4a4a5a)', display: 'inline-block', flexShrink: 0 }} />
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.name}</span>
                              {multiRelay && (
                                <span style={{ fontSize: '0.6rem', fontWeight: 700, color: purple, flexShrink: 0 }}>{t('matchDetail.cstvRelay', 'CSTV {{n}}', { n: (h.relay_index ?? 0) + 1 })}</span>
                              )}
                              <span
                                title={t('matchDetail.cstvHistorySeen', 'First seen – last seen')}
                                style={{ marginLeft: 'auto', flexShrink: 0, fontFamily: 'var(--font-mono)', fontSize: '0.66rem', color: 'var(--text-muted, #4a4a5a)' }}
                              >
                                {formatLocalTime(h.first_seen)}{h.last_seen !== h.first_seen ? ` – ${formatLocalTime(h.last_seen)}` : ''}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            );
          })()}
            </div>
          )}

          {/* Admin Controls */}
          {isAdminView && isOwner && adminMatchTab !== 'scoreboard' && (
            <div className="match-controls-card" style={{ background: 'var(--bg-card, #111118)', border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))', borderRadius: 'var(--radius-md, 8px)', padding: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.85rem' }}>
                <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-secondary, #8b8b9e)', fontFamily: 'var(--font-sans)' }}>
                  {adminMatchTab === 'players' ? t('matchDetail.playerManagement', 'Player Management') : t('matchDetail.matchControls', 'Match Controls')}
                </div>
                {adminMatchTab === 'operations' && (
                  <span style={{ fontSize: '0.68rem', color: 'var(--text-muted, #4a4a5a)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>{match.match_stage || match.status}</span>
                )}
              </div>
              {adminMatchTab === 'operations' && (
                <>
              {/* ── Veto Info (for matches with veto enabled) - Full Width ── */}
              {(!!match.veto_enabled) && (() => {
                const vetoId = match.veto_session_id;
                const keys = match.veto_keys;
                const vetoLinkItems = keys ? [
                  { label: match.team1_name || 'Team 1', url: `/veto/${vetoId}?key=${keys.A}`, color: 'var(--accent-blue, #3b82f6)' },
                  { label: match.team2_name || 'Team 2', url: `/veto/${vetoId}?key=${keys.B}`, color: 'var(--accent-red, #ef4444)' },
                  { label: 'Spectator', url: `/veto/${vetoId}`, color: 'var(--text-muted, #4a4a5a)' },
                ] : [];
                return (
                  <div style={{ background: 'var(--bg-elevated, #1a1a24)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 'var(--radius-md, 8px)', padding: '1rem', marginBottom: '1rem' }}>
                    <div
                      onClick={() => toggleSection('veto')}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', marginBottom: collapsedSections.veto ? 0 : '0.75rem', cursor: 'pointer', userSelect: 'none' }}
                      title={collapsedSections.veto ? 'Show section' : 'Hide section'}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.84rem', fontWeight: 700, color: 'var(--accent-blue, #3b82f6)', fontFamily: 'var(--font-sans)' }}>
                        <span style={{ fontSize: '0.6rem', transition: 'transform 0.15s', transform: collapsedSections.veto ? 'rotate(-90deg)' : 'rotate(0deg)', display: 'inline-block' }}>▾</span>
                        Map Veto {match.map ? '— Complete' : '— In Progress'}
                      </span>
                      <span style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--text-muted, #4a4a5a)' }}>
                        {collapsedSections.veto ? t('matchDetail.show', 'Show') : t('matchDetail.hide', 'Hide')}
                      </span>
                    </div>
                    {!collapsedSections.veto && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {keys ? vetoLinkItems.map(item => (
                        <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontSize: '0.72rem', fontWeight: 600, color: item.color, minWidth: 80, fontFamily: 'var(--font-sans)' }}>{item.label}</span>
                          <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ flex: 1, fontSize: '0.75rem', color: 'var(--accent-blue, #3b82f6)', textDecoration: 'none', fontFamily: 'var(--font-mono, monospace)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: 0.85 }}
                            onMouseEnter={e => e.currentTarget.style.opacity = '1'} onMouseLeave={e => e.currentTarget.style.opacity = '0.85'}>
                            {`${window.location.origin}${item.url}`}
                          </a>
                          <button type="button" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}${item.url}`); setCopyFeedback(item.label); setTimeout(() => setCopyFeedback(null), 1500); }}
                            style={{ padding: '3px 8px', borderRadius: 'var(--radius-sm, 4px)', border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))', background: copyFeedback === item.label ? 'rgba(34,197,94,0.15)' : 'var(--bg-card, #111118)', color: copyFeedback === item.label ? 'var(--accent-green, #22c55e)' : 'var(--text-secondary, #8b8b9e)', cursor: 'pointer', fontSize: '0.68rem', fontWeight: 600, transition: 'all 0.15s', whiteSpace: 'nowrap' }}>
                            {copyFeedback === item.label ? '✓ Copied' : 'Copy'}
                          </button>
                        </div>
                      )) : (
                        <a href={`/veto/${vetoId}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.8rem', color: 'var(--accent-blue, #3b82f6)', textDecoration: 'underline' }}>
                          Open Veto Page
                        </a>
                      )}
                      {!match.map && (
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted, #4a4a5a)', marginTop: '0.25rem' }}>
                          Waiting for teams to complete the veto...
                        </div>
                      )}
                      {keys && (
                        <button
                          onClick={() => {
                            setConfirmModal({
                              show: true,
                              message: 'Reset the veto? This will clear all bans, picks, and sides and start the veto from the beginning. This cannot be undone.',
                              onConfirm: () => {
                                setConfirmModal({ show: false, message: '', onConfirm: null });
                                axios.post(`/api/veto/${vetoId}/reset`, { key: keys.admin }).then(() => {
                                  setAlertModal({ show: true, message: 'Veto has been reset', type: 'success' });
                                }).catch(e => {
                                  setAlertModal({ show: true, message: e.response?.data?.error || 'Failed to reset veto', type: 'error' });
                                });
                              }
                            });
                          }}
                          disabled={match.status === 'finished' || match.status === 'cancelled'}
                          style={{ marginTop: '0.5rem', padding: '0.3rem 0.75rem', borderRadius: 'var(--radius-sm, 4px)', border: '1px solid rgba(239,68,68,0.25)', background: 'rgba(239,68,68,0.08)', color: 'var(--accent-red, #ef4444)', fontSize: '0.72rem', fontWeight: 600, cursor: (match.status === 'finished' || match.status === 'cancelled') ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-sans)', transition: 'all 0.15s', opacity: (match.status === 'finished' || match.status === 'cancelled') ? 0.4 : 1 }}
                          onMouseOver={e => { if (match.status !== 'finished' && match.status !== 'cancelled') { e.currentTarget.style.background = 'rgba(239,68,68,0.15)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.4)'; } }}
                          onMouseOut={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.25)'; }}
                        >
                          Reset Veto
                        </button>
                      )}
                    </div>
                    )}
                  </div>
                );
              })()}

              <div className="match-controls-grid">

                {/* ── Map Select & Start (for bracket-scheduled matches with no map) ── */}
                {match.status === 'pending' && !match.map && match.server_id && (() => {
                  const bo = parseInt(match.best_of, 10) || 1;
                  // Initialize bracketMaps/bracketSides if not already set to correct length
                  if (bracketMaps.length !== bo) {
                    const newMaps = Array(bo).fill('');
                    const newSides = Array(bo).fill('team1_ct');
                    setBracketMaps(newMaps);
                    setBracketSides(newSides);
                  }
                  const allMapsSelected = bracketMaps.length === bo && bracketMaps.every(m => m);

                  return (
                    <div style={{ background: 'var(--bg-elevated, #1a1a24)', border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))', borderRadius: 'var(--radius-md, 8px)', padding: '1rem' }}>
                      <div style={{ fontSize: '0.84rem', fontWeight: 700, color: 'var(--accent-blue, #3b82f6)', marginBottom: '0.75rem', fontFamily: 'var(--font-sans)' }}>
                        Select Maps & Sides - BO{bo}
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '10px' }}>
                        {Array.from({ length: bo }).map((_, i) => (
                          <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-secondary, #8b8b9e)', minWidth: '38px', fontFamily: 'var(--font-mono)' }}>M{i + 1}</span>
                            <select
                              value={bracketMaps[i] || ''}
                              onChange={e => {
                                const newMaps = [...bracketMaps];
                                newMaps[i] = e.target.value;
                                setBracketMaps(newMaps);
                              }}
                              style={{ flex: 1, minWidth: '140px', padding: '6px 10px', background: 'var(--bg-input, #0e0e15)', border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))', borderRadius: 'var(--radius-sm, 6px)', color: 'var(--text-primary, #e8e8ed)', fontSize: '0.78rem', fontFamily: 'var(--font-sans)' }}
                            >
                              <option value="" disabled>Select map...</option>
                              {availableMaps.map(m => (
                                <option key={m.id} value={m.name}>{m.name}</option>
                              ))}
                            </select>
                            <select
                              value={bracketSides[i] || 'team1_ct'}
                              onChange={e => {
                                const newSides = [...bracketSides];
                                newSides[i] = e.target.value;
                                setBracketSides(newSides);
                              }}
                              style={{ minWidth: '110px', padding: '6px 10px', background: 'var(--bg-input, #0e0e15)', border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))', borderRadius: 'var(--radius-sm, 6px)', color: 'var(--text-primary, #e8e8ed)', fontSize: '0.78rem', fontFamily: 'var(--font-sans)' }}
                            >
                              <option value="team1_ct">{match.team1_name || 'Team 1'} CT</option>
                              <option value="team2_ct">{match.team2_name || 'Team 2'} CT</option>
                              <option value="knife">🔪 Knife Round</option>
                            </select>
                          </div>
                        ))}
                      </div>

                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          disabled={!allMapsSelected || bracketStarting}
                          onClick={async () => {
                            if (!allMapsSelected) {
                              setAlertModal({ show: true, message: `Please select all ${bo} map(s)`, type: 'error' });
                              return;
                            }
                            const mapSummary = bracketMaps.map((m, i) => {
                              const sideLabel = bracketSides[i] === 'knife' ? 'Knife' : bracketSides[i] === 'team2_ct' ? `${match.team2_name || 'Team 2'} CT` : `${match.team1_name || 'Team 1'} CT`;
                              return `Map ${i + 1}: ${m} (${sideLabel})`;
                            }).join('\n');
                            setConfirmModal({
                              show: true,
                              message: `Start match on server?\n\n${mapSummary}`,
                              onConfirm: async () => {
                                setConfirmModal({ show: false, message: '', onConfirm: null });
                                setBracketStarting(true);
                                setSetupStep(0);
                                setSetupStepLabel('Submitting match...');
                                try {
                                  await axios.post(`/api/matches/${id}/set-map`, {
                                    maps: bracketMaps,
                                    map_sides: bracketSides,
                                  });
                                  await axios.post(`/api/matches/${id}/send-to-server`, {}, { timeout: 15000 });
                                } catch (err) {
                                  setBracketStarting(false);
                                  setSetupStep(0);
                                  setAlertModal({ show: true, message: err.response?.data?.error || 'Failed to start match', type: 'error' });
                                }
                              }
                            });
                          }}
                          style={{
                            padding: '0 16px', height: 34, fontSize: '0.78rem', fontWeight: 600, fontFamily: 'var(--font-sans)',
                            backgroundColor: allMapsSelected && !bracketStarting ? 'var(--accent-green, #22c55e)' : 'var(--bg-elevated, #1a1a24)',
                            color: allMapsSelected && !bracketStarting ? '#fff' : 'var(--text-muted, #4a4a5a)',
                            border: 'none', borderRadius: 'var(--radius-sm, 6px)', cursor: allMapsSelected && !bracketStarting ? 'pointer' : 'not-allowed',
                          }}
                        >
                          {bracketStarting ? 'Starting...' : 'Start Match'}
                        </button>
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted, #4a4a5a)', marginTop: '6px', fontFamily: 'var(--font-sans)' }}>
                        Select maps & sides, then start. This sends the match to the server with full setup.
                      </div>
                    </div>
                  );
                })()}

                {/* ── Match Flow and Pauses ── */}
                <div className="match-control-wide">
                  {renderSectionHeader('flow', t('matchDetail.matchFlowPauses', 'Match Flow and Pauses'))}
                  {!collapsedSections.flow && (() => {
                    const matchDone = match.status === 'finished' || match.status === 'cancelled';
                    let currentMapSide = null;
                    try {
                      const sides = typeof match.map_sides === 'string' ? JSON.parse(match.map_sides) : match.map_sides;
                      const idx = match.current_map_index || 0;
                      if (Array.isArray(sides) && sides[idx]) currentMapSide = sides[idx];
                    } catch (_) { }
                    const isKnifeMap = currentMapSide === 'knife';

                    const stage = match.match_stage || (match.status === 'pending' ? 'pre-game' : 'warmup');
                    const pauseDisabledReason = match.status === 'finished' || match.status === 'cancelled'
                      ? 'Match is finished'
                      : stage !== 'live' && stage !== 'knife'
                        ? `Pause is only available during knife and live rounds (current stage: ${stage})`
                        : '';
                    const pauseDisabled = !!pauseDisabledReason;

                    // Single row: each button grows to share the width but is never narrower
                    // than its own label (mc-grow), so "Prod Pause" never truncates.
                    return (
                      <div className="mc-row nowrap mc-flow">
                        {/* Match flow */}
                        <button onClick={() => handleStage('warmup')} disabled={matchDone} className="mc-btn mc-neutral"><McIcon name="warmup" /><span>{t('matchDetail.warmup', 'Warmup')}</span></button>
                        {isKnifeMap && <button onClick={() => handleStage('knife')} disabled={matchDone} className="mc-btn mc-solid mc-cyan"><McIcon name="knife" /><span>{t('matchDetail.knifeRound', 'Knife Round')}</span></button>}
                        {!isKnifeMap && <button onClick={() => handleStage('live')} disabled={matchDone} className="mc-btn mc-solid mc-green"><McIcon name="live" /><span>{t('matchDetail.goLive', 'Go Live')}</span></button>}
                        <span className="mc-divider" aria-hidden="true" />
                        {/* Pauses */}
                        <button onClick={() => handlePause('pause')} disabled={pauseDisabled} title={pauseDisabledReason || 'Force pause (admin only)'} className="mc-btn mc-amber"><McIcon name="pause" /><span>{t('matchDetail.pause', 'Pause')}</span></button>
                        <button onClick={() => handlePause('prod-pause')} disabled={pauseDisabled} title={pauseDisabledReason || 'Production pause - admin-only unpause, players notified to stay ready'} className="mc-btn mc-violet"><McIcon name="prod" /><span>{t('matchDetail.prodPause', 'Prod Pause')}</span></button>
                        <button onClick={() => handlePause('unpause')} disabled={pauseDisabled} title={pauseDisabledReason || 'Unpause the match'} className="mc-btn mc-green"><McIcon name="unpause" /><span>{t('matchDetail.unpause', 'Unpause')}</span></button>
                      </div>
                    );
                  })()}
                </div>

                {/* ── Server Migration and Rehost ── */}
                <div className="match-control-wide">
                  {renderSectionHeader('migration', t('matchDetail.migrationRehost', 'Server Migration and Rehost'))}
                  {!collapsedSections.migration && (
                  <>
                  <div className="mc-row nowrap">
                    <button
                      onClick={openMigrationModal}
                      disabled={migrating || match.status === 'finished' || match.status === 'cancelled'}
                      className="mc-btn mc-cyan mc-fill"
                    >
                      <McIcon name="migrate" /><span>{migrating ? t('matchDetail.migrating', 'Migrating...') : t('matchDetail.migrateServer', 'Migrate Server')}</span>
                    </button>
                    <button
                      onClick={openRehostModal}
                      disabled={rehosting || match.status === 'finished' || match.status === 'cancelled'}
                      className="mc-btn mc-purple mc-fill"
                    >
                      <McIcon name="rehost" /><span>{rehosting ? t('matchDetail.rehosting', 'Rehosting...') : t('matchDetail.rehost', 'Rehost')}</span>
                    </button>
                  </div>
                  {(migrationProgress || rehostProgress) && (
                    <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      {migrationProgress && (
                        <span style={{ fontSize: '0.7rem', color: migrationProgress.step === -1 ? 'var(--accent-red, #ef4444)' : 'var(--accent-green, #22c55e)', fontWeight: 500, fontFamily: 'var(--font-sans)' }}>
                          {migrationProgress.step === -1 ? '' : migrationProgress.step >= migrationProgress.total_steps ? '' : `(${migrationProgress.step}/${migrationProgress.total_steps}) `}
                          {migrationProgress.message}
                        </span>
                      )}
                      {rehostProgress && (
                        <span style={{ fontSize: '0.72rem', color: rehostProgress.step === -1 ? '#ef4444' : '#22c55e', fontWeight: 500 }}>
                          {rehostProgress.step === -1 ? '❌ ' : rehostProgress.step >= rehostProgress.total_steps ? '✅ ' : `(${rehostProgress.step}/${rehostProgress.total_steps}) `}
                          {rehostProgress.message}
                        </span>
                      )}
                    </div>
                  )}
                  </>
                  )}
                </div>

                {/* ── Match Actions ── */}
                <div>
                  {renderSectionHeader('danger', t('matchDetail.dangerZone', 'Warning - Danger Zone'), 'var(--accent-red, #ef4444)')}
                  {!collapsedSections.danger && (
                  <div className="mc-row">
                    {(() => {
                      const restoreActionDisabled = match.status === 'finished' || match.status === 'cancelled' || backupRestoreBlock?.blocked;
                      return (
                        <button
                          onClick={fetchBackups}
                          disabled={restoreActionDisabled}
                          title={backupRestoreBlock?.blocked ? `Restore disabled during round restart delay. Try again in ${backupRestoreBlock.remainingSeconds || 1}s.` : 'Restore Backup'}
                          className="mc-btn mc-neutral"
                        >
                          <McIcon name="restore" />
                          {backupRestoreBlock?.blocked ? `Restore Backup (${backupRestoreBlock.remainingSeconds || 1}s)` : 'Restore Backup'}
                        </button>
                      );
                    })()}
                    {restoreBlockNotice && (
                      <span style={{
                        minHeight: 34,
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '0 10px',
                        borderRadius: 'var(--radius-sm, 6px)',
                        background: 'rgba(245,158,11,0.08)',
                        border: '1px solid rgba(245,158,11,0.2)',
                        color: 'var(--accent-amber, #f59e0b)',
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        fontFamily: 'var(--font-sans)',
                      }}>
                        {restoreBlockNotice}
                      </span>
                    )}
                    <button onClick={openForfeitModal} disabled={match.status === 'finished' || match.status === 'cancelled'} className="mc-btn mc-amber"><McIcon name="forfeit" />{t('matchDetail.forfeitMatch', 'Forfeit Match')}</button>
                    <button onClick={handleCancelMatch} disabled={match.status === 'finished' || match.status === 'cancelled'} className="mc-btn mc-red"><McIcon name="cancel" />Cancel</button>
                  </div>
                  )}
                </div>

                {/* ── Map Controls (moved to the bottom, after Match Actions) ── */}
                <div className="match-control-wide">
                  {renderSectionHeader('maps', t('matchDetail.mapControls', 'Map Controls'))}
                  {!collapsedSections.maps && (
                  <>
                  <div className="mc-maps" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {(() => {
                      const bo = parseInt(match.best_of, 10) || 1;
                      const mapRows = typeof match.map_stats === 'string' ? JSON.parse(match.map_stats) : (match.map_stats || []);
                      // Replay: any map already reached, any time (also finished matches) — not before the start, not cancelled, not mid-rehost
                      const replayBlocked = rehosting || match.status === 'pending' || match.status === 'cancelled';
                      return bo > 1 ? Array.from({ length: bo }).map((_, i) => {
                        const isCurrentMap = i === (match.current_map_index || 0);
                        const isMapFinished = mapRows[i]?.status === 'finished';
                        const mapScore1 = isCurrentMap ? (parseInt(match.score_team1, 10) || 0) : (parseInt(mapRows[i]?.score_team1, 10) || 0);
                        const mapScore2 = isCurrentMap ? (parseInt(match.score_team2, 10) || 0) : (parseInt(mapRows[i]?.score_team2, 10) || 0);
                        const mapHasRounds = (mapScore1 + mapScore2) > 0;
                        const notCurrentOrDone = !isCurrentMap || match.status === 'finished' || match.status === 'cancelled';
                        return (
                          <div key={i} className="mc-maprow">
                            <span className={`mc-map-label${isCurrentMap ? ' current' : ''}`}>M{i + 1}</span>
                            <button onClick={() => handleForceTeams(i)} disabled={notCurrentOrDone || match.match_stage === 'live' || match.match_stage === 'knife'} className="mc-btn mc-neutral"><McIcon name="teams" /><span>Force Teams</span></button>
                            <button onClick={() => handleSwapTeams(i)} disabled={notCurrentOrDone || isMapFinished || mapHasRounds} className="mc-btn mc-orange"><McIcon name="swap" /><span>{t('matchDetail.swapTeam', 'Swap Team')}</span></button>
                            <button onClick={() => handleResetMap(i)} disabled={notCurrentOrDone} className="mc-btn mc-red"><McIcon name="reset" /><span>Reset Map</span></button>
                            {getMapForfeitEntry(i) ? (
                              <button onClick={() => openRemoveForfeitModal(i)} disabled={match.status === 'cancelled'} className="mc-btn mc-violet"><McIcon name="undo" /><span>Undo Forfeit</span></button>
                            ) : (
                              <button onClick={() => openMapForfeitModal(i)} disabled={notCurrentOrDone} className="mc-btn mc-amber"><McIcon name="forfeit" /><span>Forfeit Map</span></button>
                            )}
                            <button onClick={() => setReplayMapIdx(i)} disabled={replayBlocked || i > (match.current_map_index || 0)}
                              title={i > (match.current_map_index || 0) ? t('matchDetail.replayNotPlayed', 'This map has not been played yet') : t('matchDetail.replayHint', 'Play this map again from a round of your choice')}
                              className="mc-btn mc-cyan"><McIcon name="replay" /><span>{t('matchDetail.replay', 'Replay')}</span></button>
                          </div>
                        );
                      }) : (
                        <div className="mc-maprow no-label">
                          <button onClick={() => handleForceTeams(0)} disabled={match.status === 'live' || match.status === 'finished' || match.status === 'cancelled'} className="mc-btn mc-neutral"><McIcon name="teams" /><span>Force Teams</span></button>
                          <button onClick={() => handleSwapTeams(0)} disabled={match.status === 'finished' || match.status === 'cancelled' || ((parseInt(match.score_team1, 10) || 0) + (parseInt(match.score_team2, 10) || 0)) > 0} className="mc-btn mc-orange"><McIcon name="swap" /><span>{t('matchDetail.swapTeam', 'Swap Team')}</span></button>
                          <button onClick={() => handleResetMap(0)} disabled={match.status === 'finished' || match.status === 'cancelled'} className="mc-btn mc-red"><McIcon name="reset" /><span>Reset Map</span></button>
                          {getMapForfeitEntry(0) ? (
                            <button onClick={() => openRemoveForfeitModal(0)} disabled={match.status === 'cancelled'} className="mc-btn mc-violet"><McIcon name="undo" /><span>Undo Forfeit</span></button>
                          ) : (
                            <button onClick={() => openMapForfeitModal(0)} disabled={match.status === 'finished' || match.status === 'cancelled'} className="mc-btn mc-amber"><McIcon name="forfeit" /><span>Forfeit Map</span></button>
                          )}
                          <button onClick={() => setReplayMapIdx(0)} disabled={replayBlocked} title={t('matchDetail.replayHint', 'Play this map again from a round of your choice')}
                            className="mc-btn mc-cyan"><McIcon name="replay" /><span>{t('matchDetail.replay', 'Replay')}</span></button>
                        </div>
                      );
                    })()}
                  </div>
                  </>
                  )}
                </div>

                {/* ── Technical Pause Availability - independent collapsible section (own HIDE toggle, not nested under Map Controls) ── */}
                <div className="match-control-wide">
                  {(() => {
                    const team1Name = match.team1_name || 'Team 1';
                    const team2Name = match.team2_name || 'Team 2';
                    const matchDone = match.status === 'finished' || match.status === 'cancelled';
                    const techRow = (slot, teamName, disabled) => (
                      <button
                        type="button"
                        onClick={() => handleTechDisableToggle(slot)}
                        disabled={matchDone}
                        title={matchDone
                          ? t('matchDetail.matchFinished', 'Match is finished')
                          : disabled
                          ? t('matchDetail.techEnableHint', 'Click to allow this team to use !tech again')
                          : t('matchDetail.techDisableHint', 'Click to block this team from using !tech')}
                        className={`mc-btn mc-toggle mc-fill${disabled ? ' blocked' : ''}`}
                      >
                        <span className="mc-dot" />
                        <span>
                          {disabled ? t('matchDetail.techBlocked', '{{team}}: !tech blocked', { team: teamName }) : t('matchDetail.techAllowed', '{{team}}: !tech allowed', { team: teamName })}
                        </span>
                      </button>
                    );
                    return (
                      <div style={{ marginTop: 12 }}>
                        {renderSectionHeader('tech', t('matchDetail.techPauseAvailability', 'Technical Pause Availability'))}
                        {!collapsedSections.tech && (
                          <>
                            <div className="mc-row nowrap">
                              {techRow('team1', team1Name, !!match.tech_disabled_team1)}
                              {techRow('team2', team2Name, !!match.tech_disabled_team2)}
                            </div>
                            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted, #4a4a5a)', marginTop: 6, fontFamily: 'var(--font-sans)' }}>
                              {t('matchDetail.techPauseAvailabilityHint', 'Block a team from calling an in-game technical pause with !tech. Survives halftime side switches.')}
                            </div>

                            {/* Tech Pause Budget */}
                            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border-subtle, rgba(255,255,255,0.06))' }}>
                              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-primary, #e8e8ed)', marginBottom: 8 }}>
                                {t('matchDetail.techPauseBudget', 'Tech Pause Time Budget')}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                <input
                                  type="number"
                                  min="0"
                                  max="3600"
                                  value={match.tech_budget_seconds || 0}
                                  onChange={(e) => handleTechBudgetChange(parseInt(e.target.value, 10) || 0)}
                                  disabled={matchDone}
                                  style={{
                                    width: 80,
                                    padding: '4px 8px',
                                    fontSize: '0.75rem',
                                    background: 'var(--bg-elevated, #1a1a24)',
                                    border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))',
                                    borderRadius: 'var(--radius-sm, 6px)',
                                    color: 'var(--text-primary, #e8e8ed)',
                                    opacity: matchDone ? 0.4 : 1
                                  }}
                                />
                                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted, #4a4a5a)' }}>
                                  {t('matchDetail.secondsPerTeam', 'seconds per team (0 = unlimited)')}
                                </span>
                              </div>

                              {/* Display usage if budget is set */}
                              {match.tech_budget_seconds > 0 && (
                                <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                                  {[
                                    { label: team1Name, used: match.tech_used_team1 || 0, slot: 'team1' },
                                    { label: team2Name, used: match.tech_used_team2 || 0, slot: 'team2' }
                                  ].map(({ label, used, slot }) => {
                                    const remaining = Math.max(0, match.tech_budget_seconds - used);
                                    const percentage = Math.min(100, (used / match.tech_budget_seconds) * 100);
                                    const formatTime = (sec) => {
                                      const mins = Math.floor(sec / 60);
                                      const secs = sec % 60;
                                      return `${mins}:${secs.toString().padStart(2, '0')}`;
                                    };
                                    const isLow = remaining < 120 && remaining > 0;
                                    const isExhausted = remaining === 0;

                                    return (
                                      <div key={slot} style={{ flex: 1, background: 'rgba(255,255,255,0.02)', borderRadius: 'var(--radius-sm, 6px)', padding: 8 }}>
                                        <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-secondary, #d1d5db)', marginBottom: 4 }}>
                                          {label}
                                        </div>
                                        <div style={{ fontSize: '0.68rem', color: isExhausted ? 'var(--accent-red, #ef4444)' : isLow ? 'var(--accent-amber, #f59e0b)' : 'var(--text-muted, #9ca3af)', marginBottom: 4 }}>
                                          {formatTime(remaining)} / {formatTime(match.tech_budget_seconds)} {t('matchDetail.remaining', 'remaining')}
                                        </div>
                                        <div style={{ height: 4, background: 'rgba(255,255,255,0.05)', borderRadius: 2, overflow: 'hidden' }}>
                                          <div style={{
                                            height: '100%',
                                            width: `${percentage}%`,
                                            background: isExhausted ? 'var(--accent-red, #ef4444)' : isLow ? 'var(--accent-amber, #f59e0b)' : 'var(--accent-green, #22c55e)',
                                            transition: 'width 0.3s ease'
                                          }} />
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted, #4a4a5a)', marginTop: 8 }}>
                                {t('matchDetail.techPauseBudgetHint', 'Maximum total duration each team can use for technical pauses. Once exhausted, !tech is blocked for that team.')}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })()}
                </div>

              </div>

              {/* Public Feed Toggle */}
              <div style={{ padding: '0.75rem', background: 'var(--bg-elevated, #1a1a24)', borderRadius: 'var(--radius-sm, 6px)', marginTop: '0.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-primary, #e8e8ed)', fontFamily: 'var(--font-sans)' }}>Public Kill Feed</span>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-muted, #4a4a5a)', fontFamily: 'var(--font-sans)' }}>If enabled, anonymous users can see the kill feed</span>
                </div>
                <label className="switch" style={{ position: 'relative', display: 'inline-block', width: 34, height: 20 }}>
                  <input
                    type="checkbox"
                    checked={!!match.show_killfeed}
                    onChange={(e) => handleToggleKillfeedVisibility(e.target.checked)}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span className="slider round" style={{
                    position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: match.show_killfeed ? 'var(--accent-green, #22c55e)' : 'rgba(255,255,255,0.08)', transition: '.4s', borderRadius: 34
                  }}>
                    <span style={{
                      position: 'absolute', content: '""', height: 14, width: 14, left: match.show_killfeed ? 17 : 3, bottom: 3,
                      backgroundColor: 'white', transition: '.4s', borderRadius: '50%'
                    }}></span>
                  </span>
                </label>
              </div>

              {/* Stream Delay Setting */}
              <div style={{ padding: '0.75rem', background: 'var(--bg-elevated, #1a1a24)', borderRadius: 'var(--radius-sm, 6px)', marginTop: '0.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', opacity: (match.status === 'finished' || match.status === 'cancelled') ? 0.4 : 1, pointerEvents: (match.status === 'finished' || match.status === 'cancelled') ? 'none' : 'auto' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-primary, #e8e8ed)', fontFamily: 'var(--font-sans)' }}>Stream Broadcast Delay</span>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-muted, #4a4a5a)', fontFamily: 'var(--font-sans)' }}>Seconds to delay kill feed and stats updates</span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="number"
                    className="input"
                    style={{ width: '70px', fontSize: '0.82rem', padding: '4px 8px', textAlign: 'center' }}
                    value={streamDelay}
                    onChange={e => setStreamDelay(e.target.value)}
                    onFocus={() => { streamDelayEditingRef.current = true; }}
                    onBlur={() => { streamDelayEditingRef.current = false; }}
                    disabled={match.status === 'finished' || match.status === 'cancelled'}
                    min="0"
                  />
                  <button
                    onClick={() => { streamDelayEditingRef.current = false; handleUpdateStreamDelay(); }}
                    className="btn btn-secondary btn-sm"
                    style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                    disabled={match.status === 'finished' || match.status === 'cancelled'}
                  >
                    Save
                  </button>
                </div>
              </div>
                </>
              )}
              {/* Player Management - three feature cards: Add / Remove / Temp Admin */}
              {adminMatchTab === 'players' && (() => {
                const matchEnded = match.status === 'finished' || match.status === 'cancelled';
                const busy = addingToRoster || removingPlayer || addingSpec;
                const spinner = (color) => (
                  <span style={{ width: 13, height: 13, borderRadius: '50%', border: `2px solid ${color}33`, borderTopColor: color, animation: 'pmSpin 0.7s linear infinite', display: 'inline-block' }} />
                );
                const iconAdd = <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" /></svg>;
                const iconRemove = <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="22" y1="11" x2="16" y2="11" /></svg>;
                const iconShield = <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>;
                return (
                <div className="match-control-wide" style={{ marginTop: '0.5rem', opacity: matchEnded ? 0.4 : 1, pointerEvents: matchEnded ? 'none' : 'auto' }}>
                  <style>{`@keyframes pmSpin { to { transform: rotate(360deg); } }`}</style>

                  {/* Three cards side by side */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.8rem' }}>
                    {/* ── Card 1: Add Player ── */}
                    <PmFeatureCard accent="#22c55e" icon={iconAdd} title={t('matchDetail.pmAddTitle', 'Add Player')} desc={t('matchDetail.pmAddHint', 'Match only - not permanent roster')}>
                    <input className="input" style={{ fontSize: '0.82rem', padding: '7px 10px' }} placeholder={t('matchDetail.pmSteamIdPlaceholder', 'Steam ID (64, Steam3, or Profile URL)')} value={rosterSteamId} onChange={e => setRosterSteamId(e.target.value)} disabled={matchEnded || busy} />
                    <input className="input" style={{ fontSize: '0.82rem', padding: '7px 10px' }} placeholder={t('matchDetail.pmNamePlaceholder', 'Player Name (optional)')} value={rosterName} onChange={e => setRosterName(e.target.value)} disabled={matchEnded || busy} />
                    <select className="input" style={{ fontSize: '0.82rem', padding: '7px 10px', appearance: 'auto' }} value={rosterTeamSide} onChange={e => setRosterTeamSide(e.target.value)} disabled={matchEnded || busy}>
                      <option value="team1">{match.team1_name}</option>
                      <option value="team2">{match.team2_name}</option>
                    </select>
                    <button onClick={handleAddToRoster} disabled={addingToRoster || busy || !rosterSteamId.trim() || matchEnded} className="btn btn-primary btn-sm" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                      {addingToRoster && spinner('#ffffff')}
                      {addingToRoster ? t('matchDetail.pmAdding', 'Adding…') : t('matchDetail.pmAddBtn', 'Add Player')}
                    </button>
                    {pmProgress && pmProgress.action === 'add' && <PmStepTracker progress={pmProgress} />}
                  </PmFeatureCard>

                  {/* ── Card 2: Remove Player ── */}
                  <PmFeatureCard accent="#ef4444" icon={iconRemove} title={t('matchDetail.pmRemoveTitle', 'Remove Player')} desc={t('matchDetail.pmRemoveDesc', 'Remove a player from this match')}>
                    <input className="input" style={{ fontSize: '0.82rem', padding: '7px 10px' }} placeholder={t('matchDetail.pmRemovePlaceholder', 'Steam ID to remove')} value={removeSteamId} onChange={e => setRemoveSteamId(e.target.value)} disabled={matchEnded || busy} />
                    <button onClick={handleRemovePlayer} disabled={removingPlayer || busy || !removeSteamId.trim() || matchEnded} className="btn btn-sm" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', background: 'rgba(239,68,68,0.15)', color: 'var(--accent-red, #ef4444)', border: '1px solid rgba(239,68,68,0.35)' }}>
                      {removingPlayer && spinner('#ef4444')}
                      {removingPlayer ? t('matchDetail.pmRemoving', 'Removing…') : t('matchDetail.pmRemoveBtn', 'Remove Player')}
                    </button>
                    {pmProgress && pmProgress.action === 'remove' && <PmStepTracker progress={pmProgress} />}
                  </PmFeatureCard>

                  {/* ── Card 3: Temp Admin (add to spec + list) ── */}
                  <PmFeatureCard accent="#f59e0b" icon={iconShield} title={t('matchDetail.pmTempAdminCardTitle', 'Temp Admin')} desc={t('matchDetail.pmSpecHint', 'Add to spec + temp admin for this match')}>
                    <input className="input" style={{ fontSize: '0.82rem', padding: '7px 10px' }} placeholder={t('matchDetail.pmSpecPlaceholder', 'Steam ID for spec')} value={specSteamId} onChange={e => setSpecSteamId(e.target.value)} disabled={matchEnded || busy} />
                    <button onClick={handleAddSpec} disabled={addingSpec || busy || !specSteamId.trim() || matchEnded} className="btn btn-sm" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', background: 'rgba(245,158,11,0.15)', color: 'var(--accent-amber, #f59e0b)', border: '1px solid rgba(245,158,11,0.35)' }}>
                      {addingSpec && spinner('#f59e0b')}
                      {addingSpec ? t('matchDetail.pmAddingSpec', 'Adding…') : t('matchDetail.pmSpecBtn', 'Add to Spec')}
                    </button>
                    {pmProgress && pmProgress.action === 'spec' && <PmStepTracker progress={pmProgress} />}

                    {/* Current temp admins */}
                    {tempAdmins.length > 0 && !matchEnded && (
                      <div style={{ marginTop: '0.4rem', paddingTop: '0.55rem', borderTop: '1px solid var(--border-subtle, rgba(255,255,255,0.06))', display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                        <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-muted, #6b6b7b)', fontFamily: 'var(--font-sans)', marginBottom: '0.2rem' }}>
                          {t('matchDetail.pmTempAdminsTitle', 'Temp admins (this match)')}
                        </div>
                        {tempAdmins.map(ta => (
                          <div key={ta.id} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.15rem 0', fontSize: '0.78rem', fontFamily: 'var(--font-sans)' }}>
                            <span style={{ color: ta.revoked_at ? 'var(--text-muted, #4a4a5a)' : 'var(--text-primary, #e8e8ed)', textDecoration: ta.revoked_at ? 'line-through' : 'none', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {ta.name || ta.steam_id}
                            </span>
                            <span style={{ color: 'var(--text-muted, #4a4a5a)', fontFamily: 'var(--font-mono)', fontSize: '0.68rem' }}>{ta.steam_id}</span>
                            {!ta.revoked_at && (
                              <button onClick={() => handleRevokeTempAdmin(ta.steam_id)} className="btn btn-sm" style={{ padding: '2px 8px', fontSize: '0.7rem', background: 'transparent', color: 'var(--accent-red, #ef4444)', border: '1px solid rgba(239,68,68,0.35)' }}>{t('matchDetail.pmRevoke', 'Revoke')}</button>
                            )}
                            {ta.revoked_at && <span style={{ color: 'var(--text-muted, #4a4a5a)', fontSize: '0.7rem' }}>{t('matchDetail.pmRevoked', 'revoked')}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </PmFeatureCard>
                  </div>
                </div>
                );
              })()}

              {/* Admin Logs - Veto Audit Log */}
              {adminMatchTab === 'admin-logs' && (
                <AdminLogsTab matchId={match.id} />
              )}
            </div>
          )}
        </div>

        {/* ── RIGHT: Console Card + Damage Card ── */}
        {((isAdminView && isOwner && adminMatchTab === 'operations') || (!(isAdminView && isOwner) && !!match.show_killfeed)) && (
          <div className="match-side-column" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Console / Chat / Backups / Kill Feed card */}
            <div className="match-console-card" style={{ background: 'var(--bg-card, #111118)', border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))', borderRadius: 'var(--radius-md, 8px)', display: 'flex', flexDirection: 'column' }}>
              {/* Tab header */}
              <div style={{ display: 'flex', alignItems: 'center', padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border-subtle, rgba(255,255,255,0.06))', gap: '0.35rem', flexShrink: 0, flexWrap: 'wrap' }}>
                {isAdminView && isOwner && (
                  <>
                    <button onClick={() => setLogTab('server')} style={{ padding: '4px 12px', borderRadius: 'var(--radius-sm, 6px)', fontWeight: 500, fontSize: '0.72rem', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-sans)', background: logTab === 'server' ? 'var(--accent-cyan, #06b6d4)' : 'transparent', color: logTab === 'server' ? '#fff' : 'var(--text-muted, #4a4a5a)', transition: 'all 0.15s' }}>{t('matchDetail.console', 'Console')}</button>
                    <button onClick={() => setLogTab('chat')} style={{ padding: '4px 12px', borderRadius: 'var(--radius-sm, 6px)', fontWeight: 500, fontSize: '0.72rem', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-sans)', background: logTab === 'chat' ? 'var(--accent-cyan, #06b6d4)' : 'transparent', color: logTab === 'chat' ? '#fff' : 'var(--text-muted, #4a4a5a)', transition: 'all 0.15s' }}>{t('matchDetail.chat', 'Chat')}</button>
                    <button onClick={() => setLogTab('teamchat')} style={{ padding: '4px 12px', borderRadius: 'var(--radius-sm, 6px)', fontWeight: 500, fontSize: '0.72rem', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-sans)', background: logTab === 'teamchat' ? 'var(--accent-cyan, #06b6d4)' : 'transparent', color: logTab === 'teamchat' ? '#fff' : 'var(--text-muted, #4a4a5a)', transition: 'all 0.15s' }}>{t('teamChat.tab', 'Team Chat')}</button>
                    <button onClick={() => { setLogTab('backup'); fetchBackupRounds(); }} style={{ padding: '4px 12px', borderRadius: 'var(--radius-sm, 6px)', fontWeight: 500, fontSize: '0.72rem', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-sans)', background: logTab === 'backup' ? 'var(--accent-amber, #f59e0b)' : 'transparent', color: logTab === 'backup' ? '#fff' : 'var(--text-muted, #4a4a5a)', transition: 'all 0.15s' }}>{t('matchDetail.backups', 'Backups')}</button>
                  </>
                )}
                <button onClick={() => setLogTab('killfeed')} style={{ padding: '4px 12px', borderRadius: 'var(--radius-sm, 6px)', fontWeight: 500, fontSize: '0.72rem', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-sans)', background: logTab === 'killfeed' ? 'var(--accent-red, #ef4444)' : 'transparent', color: logTab === 'killfeed' ? '#fff' : 'var(--text-muted, #4a4a5a)', transition: 'all 0.15s' }}>{t('match.killFeed', 'Kill Feed')}</button>
                {isAdminView && isOwner && (
                  <button onClick={() => setLogTab('restore')} style={{ padding: '4px 12px', borderRadius: 'var(--radius-sm, 6px)', fontWeight: 500, fontSize: '0.72rem', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-sans)', background: logTab === 'restore' ? 'var(--accent-green, #22c55e)' : 'transparent', color: logTab === 'restore' ? '#fff' : 'var(--text-muted, #4a4a5a)', transition: 'all 0.15s' }}>{t('match.restoreChecker', 'Restore Checker')}</button>
                )}
                <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--text-muted, #4a4a5a)', fontFamily: 'var(--font-mono)' }}>{logCount > 0 ? `${logCount} logs` : ''}</span>
                <label style={{ display: logTab === 'killfeed' ? 'none' : 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', color: 'var(--text-muted, #4a4a5a)', cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>
                  <input type="checkbox" checked={autoScroll} onChange={e => setAutoScroll(e.target.checked)} style={{ accentColor: 'var(--accent-cyan, #06b6d4)' }} />
                  Auto-scroll
                </label>
              </div>

              {/* Log content area - terminal look for console */}
              <div ref={el => { logPanelRef.current = el; if (el && autoScroll && logTab !== 'backup' && logTab !== 'restore' && logTab !== 'killfeed') el.scrollTop = el.scrollHeight; }} style={{ flex: 1, overflowY: 'auto', padding: (logTab === 'backup' || logTab === 'restore') ? '0' : '0.5rem 0.75rem', fontFamily: logTab === 'restore' ? 'var(--font-sans)' : 'var(--font-mono)', fontSize: '0.72rem', lineHeight: 1.6, background: (logTab === 'server' || logTab === 'chat') ? '#08080d' : 'transparent' }}>

                {/* ── Backup Tab ── */}
                {logTab === 'backup' && (
                  <div style={{ padding: '0.75rem' }}>
                    {restoreBlockNotice && (
                      <div style={{
                        marginBottom: '0.6rem',
                        padding: '0.5rem 0.65rem',
                        borderRadius: 'var(--radius-sm, 6px)',
                        background: 'rgba(245,158,11,0.08)',
                        border: '1px solid rgba(245,158,11,0.2)',
                        color: 'var(--accent-amber, #f59e0b)',
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        fontFamily: 'var(--font-sans)',
                      }}>
                        {restoreBlockNotice}
                      </div>
                    )}
                    {backupLoading ? (
                      <div style={{ color: 'var(--accent-amber, #f59e0b)', textAlign: 'center', padding: '2rem', fontSize: '0.78rem', fontFamily: 'var(--font-sans)' }}>Loading backup restore points…</div>
                    ) : backupRounds.length === 0 ? (
                      <div style={{ color: 'var(--text-muted, #4a4a5a)', textAlign: 'center', padding: '2rem', fontSize: '0.78rem', fontFamily: 'var(--font-sans)' }}>No backup restore points found.</div>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem', fontFamily: 'var(--font-sans)' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--border-subtle, rgba(255,255,255,0.06))' }}>
                            {['MAP', 'ROUND', 'SCORE', 'TIMESTAMP', 'ACTION'].map(h => (
                              <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, letterSpacing: '0.06em', color: 'var(--text-muted, #4a4a5a)', fontSize: '0.7rem' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const isFinished = match.status === 'finished' || match.status === 'cancelled';
                            const restoreBlocked = !!backupRestoreBlock?.blocked;
                            const filteredBackups = (isFinished || selectedMapIndex === 'all' || selectedMapIndex === null)
                              ? backupRounds
                              : backupRounds.filter(r => parseInt(r.map_id) === parseInt(selectedMapIndex));
                            return filteredBackups.map((r, idx) => (
                              <tr key={idx} style={{ borderBottom: '1px solid var(--border-subtle, rgba(255,255,255,0.06))', transition: 'background 0.12s' }}
                                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover, #16161f)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                              >
                                <td style={{ padding: '7px 10px', color: 'var(--text-muted, #4a4a5a)', fontFamily: 'var(--font-mono)' }}>M{(r.map_id ?? 0) + 1}</td>
                                <td style={{ padding: '7px 10px' }}>
                                  <span style={{ fontWeight: 600, color: 'var(--text-primary, #e8e8ed)', fontFamily: 'var(--font-mono)' }}>R{String(r.round).padStart(2, '0')}</span>
                                </td>
                                <td style={{ padding: '7px 10px', fontFamily: 'var(--font-mono)' }}>
                                  <span style={{ color: 'var(--accent-green, #22c55e)', fontWeight: 600 }}>{r.score1 ?? '?'}</span>
                                  <span style={{ color: 'var(--text-muted, #4a4a5a)', margin: '0 3px' }}>:</span>
                                  <span style={{ color: 'var(--accent-red, #ef4444)', fontWeight: 600 }}>{r.score2 ?? '?'}</span>
                                </td>
                                <td style={{ padding: '7px 10px', color: 'var(--text-muted, #4a4a5a)', fontSize: '0.78rem', whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)' }}>
                                  {r.created_at ? formatLocalDateTime(r.created_at) : <span style={{ color: 'var(--text-muted, #4a4a5a)' }}>—</span>}
                                </td>
                                <td style={{ padding: '7px 10px' }}>
                                  <button
                                    disabled={(r.filename && restoringRound === r.filename) || match.status === 'finished' || match.status === 'cancelled' || restoreBlocked}
                                    onClick={() => {
                                      if (restoreBlocked) return;
                                      setConfirmModal({
                                        show: true,
                                        message: `Restore round ${r.round} (Score: ${r.score1}:${r.score2})? The match will be rolled back to this point.`,
                                        onConfirm: async () => {
                                          setConfirmModal({ show: false, message: '', onConfirm: null });
                                          let filename = r.filename;
                                          // If no filename yet (injected from score_update), fetch backup list to resolve it
                                          if (!filename) {
                                            try {
                                              const bRes = await axios.get(`/api/matches/${id}/backups`);
                                              const found = (bRes.data.rounds || []).find(br => br.map_id === r.map_id && br.round === r.round && br.filename);
                                              if (found) filename = found.filename;
                                            } catch (_) {}
                                          }
                                          if (!filename) {
                                            setAlertModal({ show: true, message: 'Backup file not ready yet. Wait a few seconds and try again.', type: 'error' });
                                            return;
                                          }
                                          setRestoringRound(filename);
                                          try {
                                            await axios.post(`/api/matches/${id}/restore`, { filename });
                                            setAlertModal({ show: true, message: `✅ Round ${r.round} restored successfully!`, type: 'success' });
                                          } catch (e) {
                                            setAlertModal({ show: true, message: 'Restore failed: ' + ((e.response && e.response.data && e.response.data.error) || e.message), type: 'error' });
                                          } finally {
                                            setRestoringRound(null);
                                          }
                                        }
                                      });
                                    }}
                                    style={{
                                      display: 'inline-flex', alignItems: 'center', gap: 4,
                                      padding: '3px 10px', borderRadius: 'var(--radius-sm, 6px)', fontSize: '0.7rem', fontWeight: 600, fontFamily: 'var(--font-sans)',
                                      background: (restoreBlocked || match.status === 'finished' || match.status === 'cancelled') ? 'rgba(148,163,184,0.14)' : ((r.filename && restoringRound === r.filename) ? 'rgba(245,158,11,0.1)' : 'var(--accent-amber, #f59e0b)'),
                                      color: (restoreBlocked || match.status === 'finished' || match.status === 'cancelled') ? 'var(--text-muted, #64748b)' : ((r.filename && restoringRound === r.filename) ? 'var(--accent-amber, #f59e0b)' : '#fff'),
                                      border: 'none', cursor: (restoreBlocked || match.status === 'finished' || match.status === 'cancelled') ? 'not-allowed' : 'pointer',
                                      opacity: (restoreBlocked || match.status === 'finished' || match.status === 'cancelled') ? 0.55 : 1,
                                    }}
                                    title={restoreBlocked ? `Restore disabled during round restart delay. Try again in ${backupRestoreBlock.remainingSeconds || 1}s.` : `Restore to round ${r.round}`}
                                  >
                                    {(r.filename && restoringRound === r.filename) ? '⏳ Restoring…' : '↩ Restore'}
                                  </button>
                                </td>
                              </tr>
                            ));
                          })()}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                {/* ── Kill Feed Tab: the match feed (feed/MatchFeed.js, GET /api/matches/:id/feed) ── */}
                {logTab === 'killfeed' && (
                  <div style={{ fontFamily: 'var(--font-sans)' }}>
                    <MatchFeed matchId={match.id} admin={isAdminView && isOwner} mapIndex={selectedMapIndex} live={match.status === 'live'} refreshKey={feedTick} />
                  </div>
                )}

                {/* ── Server / Chat Logs ── */}
                {logTab === 'restore' && renderRestoreCheckerContent()}

                {logTab === 'teamchat' && (
                  <TeamChatPanel
                    entries={chatLogs.filter(e => e.is_team && (selectedMapIndex === 'all' || selectedMapIndex === null || parseInt(e.map_index) === parseInt(selectedMapIndex)))}
                    team1Name={match.team1_name}
                    team2Name={match.team2_name}
                    team1Side={team1Side}
                    team2Side={team2Side}
                    autoScroll={autoScroll}
                  />
                )}

                {(logTab === 'server' || logTab === 'chat') && (
                  (() => {
                    const rawLogs = logTab === 'server' ? serverLogs : chatLogs;
                    let filteredLogs;
                    if (logTab === 'chat') {
                      const inMap = (mi) => selectedMapIndex === 'all' || selectedMapIndex === null || parseInt(mi) === parseInt(selectedMapIndex);
                      const chatEntries = rawLogs.filter(entry => !entry.is_team && inMap(entry.map_index));
                      // Surface connect/disconnect events INTO the chat view so admins
                      // managing from the Chat tab can see who DC'd / reconnected without
                      // switching to the Kill Feed tab. Derived from killFeed rows whose
                      // weapon is 'connected'/'disconnected'.
                      const connEntries = (killFeed || [])
                        .filter(k => (k.weapon === 'connected' || k.weapon === 'disconnected') && inMap(k.map_index))
                        .map(k => ({
                          is_chat: true,
                          is_conn: true,
                          conn_type: k.weapon, // 'connected' | 'disconnected'
                          player_name: k.killer_name,
                          team: k.killer_team,
                          ts: (parseServerDate(k.created_at) || new Date()).getTime(),
                          map_index: k.map_index,
                        }));
                      filteredLogs = [...chatEntries, ...connEntries].sort((a, b) => (a.ts || 0) - (b.ts || 0));
                    } else {
                      filteredLogs = rawLogs;
                    }
                    return filteredLogs.length === 0 ? (
                    <div style={{ color: 'var(--text-muted, #4a4a5a)', padding: '1rem', textAlign: 'center', fontFamily: 'var(--font-sans)', fontSize: '0.75rem' }}>No logs yet. Logs stream here when the server sends them.</div>
                  ) : (
                    filteredLogs.map((entry, i) => {
                      let nameColor = '#fb923c';
                      let isAdmin = false;
                      let isSystem = false;
                      if (entry.team) {
                        const t = entry.team.toUpperCase();
                        if (t === 'CT') nameColor = '#22d3ee';
                        else if (t === 'TERRORIST') nameColor = '#fb923c';
                        else if (t === 'SPECTATOR') nameColor = '#94a3b8';
                        else if (t === 'ADMIN') { nameColor = '#ef4444'; isAdmin = true; }
                        else if (t === 'SYSTEM') { isSystem = true; }
                      }
                      // Render timestamp in viewer's local timezone (HH:MM:SS)
                      let timeStr = '';
                      if (entry.ts) {
                        try {
                          const d = new Date(entry.ts);
                          if (!isNaN(d.getTime())) {
                            timeStr = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
                          }
                        } catch (_) { }
                      }
                      // Connect / disconnect line (surfaced into chat so admins see DCs
                      // without leaving the Chat tab). Green = joined, muted = quit.
                      if (entry.is_conn) {
                        const joined = entry.conn_type === 'connected';
                        return (
                          <div key={i} style={{ color: 'var(--text-primary, #e8e8ed)', wordBreak: 'break-all', paddingBottom: 1, fontStyle: 'italic' }}>
                            {timeStr && <span style={{ color: 'var(--text-muted, #6b6b7e)', fontSize: '0.7rem', marginRight: '0.4rem', fontFamily: 'monospace', fontStyle: 'normal' }}>[{timeStr}]</span>}
                            <span style={{ marginRight: '0.35rem' }}>{joined ? '→' : '←'}</span>
                            <span style={{ fontWeight: 600, fontStyle: 'normal' }}>{entry.player_name || 'Player'}</span> {joined ? t('matchDetail.joinedGame', 'joined the game') : t('matchDetail.quitGame', 'quit the game')}
                          </div>
                        );
                      }
                      // Chat text color: admin=red, system=green, CT/T=team color, unknown=muted gray.
                      const chatTextColor = entry.is_chat
                        ? (isAdmin ? '#ef4444'
                          : isSystem ? 'var(--accent-green, #22c55e)'
                          : (entry.team ? nameColor : 'var(--text-secondary, #8b8b9e)'))
                        : 'var(--text-secondary, #8b8b9e)';
                      return (
                        <div key={i} style={{ color: chatTextColor, wordBreak: 'break-all', paddingBottom: 1 }}>
                          {logTab === 'chat' ? (
                            isAdmin || isSystem ? (
                              <span>
                                {timeStr && <span style={{ color: 'var(--text-muted, #6b6b7e)', fontSize: '0.7rem', marginRight: '0.4rem', fontFamily: 'monospace' }}>[{timeStr}]</span>}
                                {entry.chat_message}
                              </span>
                            ) : (
                              <span>
                                {timeStr && <span style={{ color: 'var(--text-muted, #6b6b7e)', fontSize: '0.7rem', marginRight: '0.4rem', fontFamily: 'monospace' }}>[{timeStr}]</span>}
                                <span style={{ color: nameColor, fontWeight: 600 }}>{entry.player_name}</span>: {entry.chat_message}
                              </span>
                            )
                          ) : (
                            <span>
                              {timeStr && <span style={{ color: 'var(--text-muted, #6b6b7e)', fontSize: '0.7rem', marginRight: '0.4rem', fontFamily: 'monospace' }}>[{timeStr}]</span>}
                              {entry.line}
                            </span>
                          )}
                        </div>
                      );
                    })
                  );
                  })()
                )}

              </div>{/* end scrollable log/backup area */}

              {/* Send command input - only on Console and Chat tabs */}
              {isAdminView && isOwner && (logTab === 'server' || logTab === 'chat') && (
                <div className="md-send">

                  {logTab === 'chat' && (
                    <input
                      className="input md-send-prefix"
                      title={t('matchDetail.chatPrefixTitle', 'Live chat prefix')}
                      aria-label={t('matchDetail.chatPrefixTitle', 'Live chat prefix')}
                      value={chatPrefix}
                      onChange={e => setChatPrefix(e.target.value)}
                      onFocus={() => { chatPrefixEditingRef.current = true; }}
                      onBlur={() => { chatPrefixEditingRef.current = false; updateChatPrefix(); }}
                      onKeyDown={e => e.key === 'Enter' && updateChatPrefix()}
                      disabled={match.status === 'finished' || match.status === 'cancelled'}
                    />
                  )}

                  <input
                    className="input md-send-msg"
                    placeholder={logTab === 'chat' ? t('matchDetail.adminSayPlaceholder', 'Admin say message...') : t('matchDetail.rconPlaceholder', 'RCON command (e.g. mp_restartgame 1)')}
                    aria-label={logTab === 'chat' ? t('matchDetail.adminSayPlaceholder', 'Admin say message...') : t('matchDetail.rconPlaceholder', 'RCON command (e.g. mp_restartgame 1)')}
                    value={logTab === 'chat' ? adminChatMessage : rconCustom}
                    onChange={e => logTab === 'chat' ? setAdminChatMessage(e.target.value) : setRconCustom(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { logTab === 'chat' ? handleAdminChat() : handleCustomRcon(); } }}
                    disabled={match.status === 'finished' || match.status === 'cancelled'}
                  />
                  <button type="button" onClick={logTab === 'chat' ? handleAdminChat : handleCustomRcon} className="btn btn-primary btn-sm md-send-btn" disabled={match.status === 'finished' || match.status === 'cancelled'}>
                    {logTab === 'chat' ? t('matchDetail.adminSay', 'Admin say') : t('matchDetail.send', 'Send')}
                  </button>
                </div>
              )}
            </div>

            {/* ── Restore Checker (admin-only) ── */}
            {logTab === 'restore-panel-legacy' && isAdminView && (isOwner || user?.role === 'super_admin') && (
              damageEvents.length === 0 ? (
                <div className="match-restore-card" style={{ padding: '0.75rem', color: 'var(--text-muted, #4a4a5a)', textAlign: 'center', fontSize: '0.75rem', fontFamily: 'var(--font-sans)', background: 'var(--bg-card, #111118)', border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))', borderRadius: 'var(--radius-md, 8px)' }}>
                  No restore requests yet. Appears when players type .restore, nl, etc.
                </div>
              ) : (
                <div className="match-restore-card" style={{ background: 'var(--bg-card, #111118)', border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))', borderRadius: 'var(--radius-md, 8px)', overflow: 'hidden' }}>
                  {/* Header */}
                  <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--border-subtle, rgba(255,255,255,0.06))', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-primary, #e8e8ed)', fontFamily: 'var(--font-sans)' }}>{t('match.restoreChecker', 'Restore Checker')}</div>
                      <div style={{ fontSize: '0.65rem', color: 'var(--text-muted, #4a4a5a)', marginTop: '2px' }}>Shows if damage was dealt before a player requested a restore</div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.6rem', fontWeight: 600 }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} /> Safe</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} /> Damage</span>
                    </div>
                  </div>

                  <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                    {(() => {
                      // Group precomputed restore-check rows by map → round.
                      const byMap = {};
                      for (const e of damageEvents) {
                        const key = e.map_index ?? 0;
                        if (!byMap[key]) byMap[key] = {};
                        byMap[key][e.round_number ?? 1] = e;
                      }
                      const mapLabels = match.map_list ? JSON.parse(match.map_list) : [match.map].filter(Boolean);
                      const mapEntries = Object.entries(byMap).sort((a, b) => Number(a[0]) - Number(b[0]));
                      const multiMap = mapEntries.length > 1;
                      const liveMapIdx = (typeof match.current_map_index === 'number')
                        ? match.current_map_index
                        : parseInt(match.current_map_index, 10) || 0;

                      const toggleMap = (idx) => {
                        setExpandedRestoreMaps(prev => {
                          const next = new Set(prev);
                          if (next.has(idx)) next.delete(idx); else next.add(idx);
                          return next;
                        });
                      };

                      return mapEntries.map(([mapIdx, roundData]) => {
                        const mapLabel = (mapLabels[Number(mapIdx)] || `Map ${Number(mapIdx) + 1}`).replace(/^(de_|cs_)/, '');
                        const roundEntries = Object.entries(roundData).sort((a, b) => Number(b[0]) - Number(a[0]));
                        // Per-map summary — counts for the collapsed header.
                        let damageCount = 0, safeCount = 0, flagCount = 0;
                        for (const [, d] of roundEntries) {
                          if (d.has_damage) damageCount += 1; else safeCount += 1;
                          flagCount += (d.chat_flags || []).length;
                        }
                        const isLive = Number(mapIdx) === Number(liveMapIdx);
                        const expanded = !multiMap || expandedRestoreMaps.has(Number(mapIdx));
                        return (
                          <div key={mapIdx}>
                            {/* Map divider / collapsible header - only when more than one map */}
                            {multiMap && (
                              <div
                                onClick={() => toggleMap(Number(mapIdx))}
                                style={{
                                  padding: '0.45rem 1rem',
                                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem',
                                  fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                                  color: isLive ? 'var(--accent-blue, #3b82f6)' : 'var(--text-muted, #4a4a5a)',
                                  background: isLive ? 'rgba(59,130,246,0.08)' : 'rgba(59,130,246,0.04)',
                                  borderBottom: '1px solid rgba(59,130,246,0.1)',
                                  cursor: 'pointer', userSelect: 'none',
                                }}
                                role="button"
                                aria-expanded={expanded}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <span style={{ fontSize: '0.7rem', width: 12, display: 'inline-block', transition: 'transform 0.15s', transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>▸</span>
                                  <span>{mapLabel}</span>
                                  {isLive && <span style={{ fontSize: '0.55rem', padding: '1px 6px', borderRadius: 4, background: 'rgba(59,130,246,0.18)', color: 'var(--accent-blue, #3b82f6)', letterSpacing: '0.08em' }}>LIVE</span>}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: damageCount > 0 ? '#ef4444' : 'var(--text-muted, #4a4a5a)' }}>
                                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: damageCount > 0 ? '#ef4444' : '#22c55e', display: 'inline-block' }} />
                                    {damageCount > 0 ? `${damageCount} damage` : `${safeCount} safe`}
                                  </span>
                                  {flagCount > 0 && (
                                    <span style={{ color: 'var(--text-muted, #4a4a5a)' }}>· {flagCount} restore req</span>
                                  )}
                                </div>
                              </div>
                            )}
                            {expanded && roundEntries.map(([roundNum, data]) => {
                              const sortedDamage = [...(data.damage_events || [])].sort((a, b) => Number(a.dmg_second || 0) - Number(b.dmg_second || 0));
                              const hasDamage = data.has_damage;
                              const hasFlags = (data.chat_flags || []).length > 0;
                              const safeToRestore = !hasDamage && hasFlags;
                              const statusColor = hasDamage ? '#ef4444' : '#22c55e';
                              // Map new flag shape to the component's expected shape.
                              const flags = (data.chat_flags || []).map(f => ({ name: f.player_name, keyword: f.keyword }));

                              return (
                                <RestoreRoundRow
                                  key={`${mapIdx}_${roundNum}`}
                                  roundNum={roundNum}
                                  hasDamage={hasDamage}
                                  sortedDamage={sortedDamage}
                                  hasFlags={hasFlags}
                                  safeToRestore={safeToRestore}
                                  statusColor={statusColor}
                                  flags={flags}
                                  isLive={data.is_live}
                                />
                              );
                            })}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              ))}
        </div>
      )}
      </div>

      {/* Backup Modal */}
      {showBackupModal && (
        <ActionModal
          icon="restore" tone="amber"
          title={t('matchModals.restoreTitle', 'Restore backup')}
          subtitle={t('matchModals.restoreSub', 'Pick a round to take the match back to')}
          onClose={() => setShowBackupModal(false)}
          footer={<>
            {backupRestoreBlock?.blocked && <span className="am-hint">{restoreBlockNotice}</span>}
            <button type="button" className="mc-btn mc-neutral" onClick={() => setShowBackupModal(false)}>{t('common.cancel', 'Cancel')}</button>
            <button type="button" className="mc-btn mc-solid mc-amber" onClick={handleRestore} disabled={!selectedBackup || backupRestoreBlock?.blocked}>
              <McIcon name="restore" /><span>{t('matchModals.restore', 'Restore')}</span>
            </button>
          </>}
        >
          {backupFiles.length === 0 ? (
            <div className="am-empty">{t('matchDetail.restoreNoBackupsThisMap', 'No backups for this map yet. To go back to an earlier map, use Map controls → Replay.')}</div>
          ) : (
            <div className="am-list">
              {backupFiles.map((r, idx) => (
                <button type="button" key={idx} className={`am-opt${selectedBackup === r.filename ? ' on' : ''}`} onClick={() => setSelectedBackup(r.filename)}>
                  <span className="am-opt-n">R{String(r.round).padStart(2, '0')}</span>
                  <span className="am-opt-score">
                    <span className="am-team">{match.team1_name}</span>
                    <b>{r.score1 ?? '?'}</b><i>-</i><b>{r.score2 ?? '?'}</b>
                    <span className="am-team">{match.team2_name}</span>
                  </span>
                  {r.map_id != null && <span className="am-opt-tag">M{(r.map_id ?? 0) + 1}</span>}
                </button>
              ))}
            </div>
          )}
        </ActionModal>
      )}

      {/* ── Per-Map Forfeit Modal ── */}
      {showMapForfeitModal && (() => {
        const lastMap = (match.best_of || 1) - 1;
        return (
          <ActionModal
            icon="forfeit" tone="amber"
            title={t('matchModals.forfeitMapTitle', 'Forfeit map')}
            subtitle={t('matchModals.forfeitMapSub', 'Set the final score of this map')}
            busy={mapForfeitSubmitting}
            onClose={() => setShowMapForfeitModal(false)}
            footer={<>
              <button type="button" className="mc-btn mc-neutral" onClick={() => setShowMapForfeitModal(false)} disabled={mapForfeitSubmitting}>{t('common.cancel', 'Cancel')}</button>
              {mapForfeitIndex < lastMap && (
                <button type="button" className="mc-btn mc-amber" onClick={() => handleMapForfeit(true)} disabled={mapForfeitSubmitting}>
                  {mapForfeitSubmitting ? t('matchModals.saving', 'Saving…') : t('matchModals.saveNext', 'Save & next map')}
                </button>
              )}
              <button type="button" className="mc-btn mc-solid mc-amber" onClick={() => handleMapForfeit(false)} disabled={mapForfeitSubmitting}>
                <McIcon name="forfeit" /><span>{mapForfeitSubmitting ? t('matchModals.saving', 'Saving…') : t('matchModals.saveScore', 'Save score')}</span>
              </button>
            </>}
          >
            <div className="am-nav">
              <button type="button" onClick={() => openMapForfeitModal(mapForfeitIndex - 1)} disabled={mapForfeitIndex === 0}>‹ {t('matchModals.prev', 'Prev')}</button>
              <span>{t('matchModals.mapN', 'Map {{n}}', { n: mapForfeitIndex + 1 })} · {getMapList()[mapForfeitIndex] || 'TBD'}</span>
              <button type="button" onClick={() => openMapForfeitModal(mapForfeitIndex + 1)} disabled={mapForfeitIndex >= lastMap}>{t('matchModals.next', 'Next')} ›</button>
            </div>

            <div>
              <div className="am-label">{t('matchModals.winner', 'Winner')}</div>
              <div className="am-pick">
                <button type="button" className={mapForfeitScore1 > mapForfeitScore2 ? 'on' : ''} onClick={() => { setMapForfeitScore1(13); setMapForfeitScore2(0); }}>
                  {t('matchModals.teamWins', '{{team}} wins', { team: match.team1_name })} · 13-0
                </button>
                <button type="button" className={mapForfeitScore2 > mapForfeitScore1 ? 'on' : ''} onClick={() => { setMapForfeitScore1(0); setMapForfeitScore2(13); }}>
                  {t('matchModals.teamWins', '{{team}} wins', { team: match.team2_name })} · 0-13
                </button>
              </div>
            </div>

            <div>
              <div className="am-label">{t('matchModals.score', 'Score')}</div>
              <div className="am-score">
                <span className="am-team" style={{ color: team1Color }}>{match.team1_name}</span>
                <input type="number" min="0" className="am-input am-num" value={mapForfeitScore1} onChange={(e) => setMapForfeitScore1(parseInt(e.target.value) || 0)} />
                <i>:</i>
                <input type="number" min="0" className="am-input am-num" value={mapForfeitScore2} onChange={(e) => setMapForfeitScore2(parseInt(e.target.value) || 0)} />
                <span className="am-team" style={{ color: team2Color }}>{match.team2_name}</span>
              </div>
            </div>
          </ActionModal>
        );
      })()}

      {/* ── Remove Forfeit Modal ── */}
      {showRemoveForfeitModal && (() => {
        const entry = getMapForfeitEntry(removeForfeitIndex);
        const mapName = getMapList()[removeForfeitIndex] || 'TBD';
        return (
          <ActionModal
            icon="undo" tone="violet"
            title={t('matchModals.undoForfeitTitle', 'Undo forfeit')}
            subtitle={t('matchModals.mapN', 'Map {{n}}', { n: removeForfeitIndex + 1 }) + ` · ${mapName}`}
            busy={removeForfeitSubmitting}
            onClose={() => setShowRemoveForfeitModal(false)}
            footer={<>
              <button type="button" className="mc-btn mc-neutral" onClick={() => setShowRemoveForfeitModal(false)} disabled={removeForfeitSubmitting}>{t('common.cancel', 'Cancel')}</button>
              <button type="button" className="mc-btn mc-violet" onClick={() => handleRemoveForfeit(false)} disabled={removeForfeitSubmitting}>{t('matchModals.resetScoreOnly', 'Reset score only')}</button>
              <button type="button" className="mc-btn mc-solid mc-violet" onClick={() => handleRemoveForfeit(true)} disabled={removeForfeitSubmitting}>
                <McIcon name="undo" /><span>{t('matchModals.resetAndGo', 'Reset & go to map')}</span>
              </button>
            </>}
          >
            <p className="am-text">{t('matchModals.undoForfeitText', 'The forfeit score of this map goes back to 0-0 so it can be played.')}</p>
            <div className="am-box">
              <div className="am-preview">
                <span className="from">{entry ? entry.score_team1 : 0} : {entry ? entry.score_team2 : 0}</span>
                <span className="arrow">→</span>
                <span className="to">0 : 0</span>
              </div>
            </div>
            <p className="am-text"><strong>{t('matchModals.resetAndGo', 'Reset & go to map')}</strong> - {t('matchModals.resetAndGoHint', 'also makes this the current map and changes the server to it (warmup).')}</p>
          </ActionModal>
        );
      })()}

      {/* ── Server Migration Modal ── */}
      {showMigrationModal && (
        <ActionModal
          icon="migrate" tone={isManualMigration ? 'amber' : 'cyan'}
          width={isManualMigration ? 580 : 480}
          title={isManualMigration ? t('matchModals.migrateManualTitle', 'Manual server migration') : t('matchModals.migrateTitle', 'Migrate server')}
          subtitle={isManualMigration ? t('matchModals.migrateManualSub', 'Files must be copied by hand') : t('matchModals.migrateSub', 'Move this match to another server')}
          onClose={() => setShowMigrationModal(false)}
          footer={<>
            <button type="button" className="mc-btn mc-neutral" onClick={() => setShowMigrationModal(false)}>{t('common.cancel', 'Cancel')}</button>
            <button type="button" className={`mc-btn mc-solid ${isManualMigration ? 'mc-amber' : 'mc-cyan'}`} onClick={handleMigrateMatch} disabled={!migrationTargetServer || (isManualMigration && !manualFilesConfirmed)}>
              <McIcon name="migrate" /><span>{isManualMigration ? t('matchModals.migrateManual', 'Migrate (manual)') : t('matchModals.migrate', 'Migrate')}</span>
            </button>
          </>}
        >
          <p className="am-text">
            {isManualMigration
              ? t('matchModals.migrateManualText', 'One or both servers have no FTP/SFTP. Copy the files below between the servers yourself before the panel finishes the migration.')
              : t('matchModals.migrateText', 'The match is paused, its backup files are copied, and it is restored on the new server.')}
          </p>

          <div className="am-box am-kv">
            <span className="k">{t('matchModals.currentServer', 'Current')}</span>
            {match.server_country_code && (
              <img className="am-flag" src={`https://flagcdn.com/16x12/${match.server_country_code.toLowerCase()}.png`} alt={match.server_country_code} onError={e => { e.target.style.display = 'none'; }} />
            )}
            <strong>{match.server_name || `Server #${match.server_id}`}</strong>
            {match.server_city && match.server_country_name && <span>({match.server_city}, {match.server_country_name})</span>}
            {!sourceHasBackup && <span className="am-tag-warn">{t('matchModals.noFtp', 'No FTP/SFTP')}</span>}
          </div>

          <div>
            <div className="am-label">{t('matchModals.targetServer', 'Target server')}</div>
            {availableServers.length === 0 ? (
              <div className="am-empty">{t('matchModals.noServers', 'No free servers available.')}</div>
            ) : (
              <select className="am-select" value={migrationTargetServer?.value || ''} onChange={e => handleMigrationTargetChange(e.target.value)}>
                <option value="">{t('matchModals.selectServer', 'Select a server…')}</option>
                {availableServers.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.ip}:{s.port}){s.has_backup ? '' : ` - ${t('matchModals.noFtp', 'No FTP/SFTP')}`}
                  </option>
                ))}
              </select>
            )}
          </div>

          {isManualMigration && migrationTargetServer && (
            <div className="am-warn">
              <div className="am-warn-title">{t('matchModals.manualCopyTitle', 'Copy these files by hand')}</div>
              <p>{t('matchModals.manualCopyText', 'From the old server to the new one (csgo/ folder), with your host\'s file manager or SSH:')}</p>
              {migrationFiles ? (
                <div className="am-files">
                  <div className="k">{t('matchModals.fileBackup', 'Round backup')}</div>
                  <div className="v">{migrationFiles.backupPattern}</div>
                  <div className="k">{t('matchModals.fileForceTeams', 'Force-teams file')}</div>
                  <div className="v">{migrationFiles.forceTeamsFile}</div>
                  <div className="k">{t('matchModals.filePlayerNames', 'Player names file')}</div>
                  <div className="v">{migrationFiles.playerNamesFile}</div>
                  {migrationFiles.avatarFiles && migrationFiles.avatarFiles.length > 0 && (
                    <>
                      <div className="k">{t('matchModals.fileAvatars', 'Avatar files')}</div>
                      {migrationFiles.avatarFiles.map((f, i) => <div key={i} className="v">{f}</div>)}
                    </>
                  )}
                </div>
              ) : (
                <p>{t('matchModals.fileListFailed', 'Could not load the file list. Copy the round backup, force_teams and player_names files.')}</p>
              )}
              <p style={{ marginTop: '0.5rem' }}>
                {t('matchModals.manualAfter', 'After Migrate, the panel sets up the new server over RCON. Then type')} <code className="am-code">!start</code> {t('matchModals.manualAfter2', 'in-game to restore the match.')}
              </p>
              <label className="am-check">
                <input type="checkbox" checked={manualFilesConfirmed} onChange={e => setManualFilesConfirmed(e.target.checked)} />
                {t('matchModals.manualConfirm', 'I copied all files to the new server')}
              </label>
            </div>
          )}

          {match.migration_restore_round != null && (
            <div className="am-warn">
              {t('matchModals.pendingRestore', 'A migration restore is already pending (round {{n}}). Starting the match on the current server restores from that round.', { n: match.migration_restore_round })}
            </div>
          )}
        </ActionModal>
      )}

      {/* ── Rehost Modal ── */}
      {replayMapIdx != null && (() => {
        let list = [];
        try { list = typeof match.map_list === 'string' ? JSON.parse(match.map_list) : (match.map_list || []); } catch (_) { }
        const cur = match.current_map_index || 0;
        return (
          <ReplayMapModal
            matchId={id}
            mapIndex={replayMapIdx}
            mapName={(Array.isArray(list) && list[replayMapIdx]) || match.map || ''}
            team1Name={match.team1_name}
            team2Name={match.team2_name}
            laterMapsPlayed={Math.max(0, cur - replayMapIdx)}
            isFinished={match.status === 'finished'}
            onClose={() => setReplayMapIdx(null)}
            onStarted={() => {
              setReplayMapIdx(null);
              setRehosting(true);
              setRehostProgress({ step: 0, total_steps: 10, message: t('matchDetail.replayProgress', 'Starting replay...') });
              fetchMatch();
            }}
          />
        );
      })()}

      {showRehostModal && (
        <ActionModal
          icon="rehost" tone="violet"
          title={t('matchModals.rehostTitle', 'Rehost match')}
          subtitle={`${rehostMatchInfo?.map || match.map || ''} · ${rehostMatchInfo?.team1_name || match.team1_name} vs ${rehostMatchInfo?.team2_name || match.team2_name}`}
          onClose={() => setShowRehostModal(false)}
          footer={<>
            <button type="button" className="mc-btn mc-neutral" onClick={() => setShowRehostModal(false)}>{t('common.cancel', 'Cancel')}</button>
            <button type="button" className="mc-btn mc-solid mc-violet" onClick={handleRehost} disabled={rehostSelectedRound == null}>
              <McIcon name="rehost" /><span>{t('matchModals.rehost', 'Rehost')}</span>
            </button>
          </>}
        >
          <p className="am-text">{t('matchModals.rehostText', 'The server reloads the current map on the same server. When both teams are ready, the chosen score is restored automatically.')}</p>
          <div>
            <div className="am-label">{t('matchModals.restartFrom', 'Restart from')}</div>
            {rehostScores.length === 0 ? (
              <div className="am-empty">{t('matchModals.noRounds', 'No rounds recorded on this map yet.')}</div>
            ) : (
              <div className="am-list">
                {rehostScores.map((score) => (
                  <button type="button" key={score.round} className={`am-opt${rehostSelectedRound === score.round ? ' on' : ''}`} onClick={() => setRehostSelectedRound(score.round)}>
                    <span className="am-opt-n">R{String(score.round).padStart(2, '0')}</span>
                    <span className="am-opt-score">
                      <span className="am-team">{rehostMatchInfo?.team1_name || match.team1_name}</span>
                      <b>{score.score_team1 ?? '?'}</b><i>-</i><b>{score.score_team2 ?? '?'}</b>
                      <span className="am-team">{rehostMatchInfo?.team2_name || match.team2_name}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </ActionModal>
      )}

      {/* ── Forfeit Match Modal ── */}
      {showForfeitModal && (() => {
        let mapList = [];
        try { mapList = JSON.parse(match.map_list || '[]'); } catch (_) { }
        const setMapScore = (i, key, v) => {
          const updated = [...forfeitMapScores];
          updated[i] = { ...updated[i], [key]: parseInt(v) || 0 };
          setForfeitMapScores(updated);
        };
        return (
          <ActionModal
            icon="forfeit" tone="amber"
            title={t('matchModals.forfeitMatchTitle', 'Forfeit match')}
            subtitle={t('matchModals.forfeitMatchSub', 'End the match with a forfeit result')}
            busy={forfeitSubmitting}
            onClose={() => setShowForfeitModal(false)}
            footer={<>
              <button type="button" className="mc-btn mc-neutral" onClick={() => setShowForfeitModal(false)} disabled={forfeitSubmitting}>{t('common.cancel', 'Cancel')}</button>
              <button type="button" className="mc-btn mc-solid mc-amber" onClick={handleConfirmForfeit} disabled={forfeitSubmitting}>
                <McIcon name="forfeit" /><span>{forfeitSubmitting ? t('matchModals.forfeiting', 'Forfeiting…') : t('matchModals.confirmForfeit', 'Forfeit match')}</span>
              </button>
            </>}
          >
            <div>
              <div className="am-label">{t('matchModals.winner', 'Winner')}</div>
              <div className="am-pick">
                <button type="button" className={forfeitWinner === 'team1' ? 'on' : ''} onClick={() => setForfeitWinner('team1')}>{match.team1_name}</button>
                <button type="button" className={forfeitWinner === 'team2' ? 'on' : ''} onClick={() => setForfeitWinner('team2')}>{match.team2_name}</button>
              </div>
            </div>

            <div>
              <div className="am-label">{t('matchModals.mapScores', 'Map scores')} · {match.team1_name} : {match.team2_name}</div>
              {forfeitMapScores.map((ms, i) => (
                <div key={i} className="am-maprow">
                  <span className="am-mapname"><b>M{i + 1}</b>{mapList[i] || 'TBD'}</span>
                  <input type="number" min="0" className="am-input am-num" value={ms.score_team1} onChange={(e) => setMapScore(i, 'score_team1', e.target.value)} aria-label={match.team1_name} />
                  <i>:</i>
                  <input type="number" min="0" className="am-input am-num" value={ms.score_team2} onChange={(e) => setMapScore(i, 'score_team2', e.target.value)} aria-label={match.team2_name} />
                </div>
              ))}
            </div>

            <div>
              <div className="am-label">{t('matchModals.seriesScore', 'Series (maps won)')}</div>
              <div className="am-score">
                <span className="am-team" style={{ color: team1Color }}>{match.team1_name}</span>
                <input type="number" min="0" className="am-input am-num" value={forfeitMatchScore.team1} onChange={(e) => setForfeitMatchScore(prev => ({ ...prev, team1: parseInt(e.target.value) || 0 }))} />
                <i>:</i>
                <input type="number" min="0" className="am-input am-num" value={forfeitMatchScore.team2} onChange={(e) => setForfeitMatchScore(prev => ({ ...prev, team2: parseInt(e.target.value) || 0 }))} />
                <span className="am-team" style={{ color: team2Color }}>{match.team2_name}</span>
              </div>
            </div>
          </ActionModal>
        );
      })()}
      <AlertModal show={alertModal.show} message={alertModal.message} type={alertModal.type} onClose={() => setAlertModal({ ...alertModal, show: false })} />
      <ConfirmModal show={confirmModal.show} message={confirmModal.message} onConfirm={confirmModal.onConfirm} onClose={() => setConfirmModal({ ...confirmModal, show: false })} />
    </div>
  );

  const SETUP_STEPS = [
    { step: 1, label: t('matchSetup.deployAvatars', 'Deploying player avatars') + '...' },
    { step: 2, label: t('matchSetup.sendingMatch', 'Sending match to server') + '...' },
    { step: 3, label: t('matchSetup.configuringLogs', 'Configuring match logs') + '...' },
    { step: 4, label: t('matchSetup.enforcingNames', 'Enforcing player names') + '...' },
    { step: 5, label: t('matchSetup.backupPrefix', 'Setting backup prefix') + '...' },
    { step: 6, label: t('matchSetup.uploadingBackups', 'Uploading map backups') + '...' },
    { step: 7, label: t('matchSetup.enforcingSides', 'Enforcing team sides') + '...' },
    { step: 8, label: t('matchSetup.done', 'Done') },
  ];

  const setupOverlay = bracketStarting && setupStep !== 0 ? (() => {
    const totalSteps = 8;
    const isError = setupStep === -1;
    const isDone = setupStep >= totalSteps;
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
        <div style={{ padding: '2.5rem', maxWidth: '480px', width: '100%', textAlign: 'center', background: 'var(--bg-card, #111118)', border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))', borderRadius: 'var(--radius-lg, 12px)', boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }}>
          <h3 style={{ marginBottom: '1.5rem', color: isError ? '#ef4444' : isDone ? '#10b981' : '#f1f5f9', fontSize: '1.5rem', fontWeight: 'bold', fontFamily: 'var(--font-display)' }}>
            {isError ? t('matchSetup.setupFailed', 'Setup Failed') : isDone ? t('matchSetup.matchReady', 'Match Ready!') : t('matchSetup.settingUp', 'Setting Up Match') + '...'}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', textAlign: 'left', margin: '0 1rem' }}>
            {SETUP_STEPS.map(({ step: s, label }) => {
              const completed = setupStep >= s;
              const active = !completed && setupStep === s - 1 && !isError;
              const icon = completed ? 'fa-check-circle' : active ? 'fa-circle-notch fa-spin' : 'fa-circle';
              const color = completed ? '#10b981' : active ? '#60a5fa' : '#64748b';
              return (
                <div key={s} style={{ display: 'flex', alignItems: 'center', color, transition: 'color 0.3s ease' }}>
                  <i className={`fas ${icon}`} style={{ marginRight: '0.85rem', fontSize: '1.1rem', minWidth: '1.1rem', textAlign: 'center' }}></i>
                  <span style={{ fontSize: '0.95rem', fontWeight: 500 }}>{label}</span>
                </div>
              );
            })}
            {isError && (
              <div style={{ display: 'flex', alignItems: 'center', color: '#ef4444', marginTop: '0.5rem' }}>
                <i className="fas fa-exclamation-triangle" style={{ marginRight: '0.85rem', fontSize: '1.1rem', minWidth: '1.1rem', textAlign: 'center' }}></i>
                <span style={{ fontSize: '0.95rem', fontWeight: 500 }}>{setupStepLabel || t('matchSetup.errorDuringSetup', 'An error occurred during setup')}</span>
              </div>
            )}
          </div>
          <div style={{ marginTop: '1.5rem', height: '4px', width: '100%', backgroundColor: 'var(--bg-elevated, #1a1a24)', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              backgroundColor: isError ? '#ef4444' : '#10b981',
              width: isError ? '100%' : `${(Math.max(0, setupStep) / totalSteps) * 100}%`,
              transition: 'width 0.6s ease'
            }}></div>
          </div>
        </div>
      </div>
    );
  })() : null;

  if (isAdminView) return <>{content}{setupOverlay}</>;
  return (
    <div className="pub-root">
      <PublicHeader />
      <div className="pub-container pub-detail-container" style={{ paddingTop: '2rem' }}>
        {content}
      </div>
      <Footer />
      {setupOverlay}
    </div>
  );
}

export default MatchDetail;
